import "../../css/gwd-livescore-global.css";
import "../../css/gwd-livescore.css";
import "../../css/gwd-livescore-responsive.css";
import "../../css/gwd-fixtures.css";
import "../../css/gwd-fixtures-responsive.css";
import "../../css/gwd-results.css";
import "../../css/gwd-results-responsive.css";
import "../../css/gwd-league-standings.css";
import "../../css/gwd-league-standings-responsive.css";
import "../../css/gwd-odds-board.css";
import "../../css/gwd-odds-board-responsive.css";
import {
  currentFixtureWindow,
  currentMatchWindow,
  formatMatchDate,
  formatMatchDateTitle,
  formatTime,
  normalizeFixtureDate,
  toGmt7Iso,
} from "../shared/date-time";
import {
  setImage,
  setLeagueColor,
  setText,
  setTooltip,
  setVisibleText,
} from "../shared/dom";
import {
  displayCorners,
  displayHalfScore,
  displayScoreMeta,
  isNotStartedInfo,
  mobileExtraResultText,
  scoreMeta,
  winnerMarkers,
  winnerTooltips,
} from "../shared/scores";
import { layoutRegistry } from "../features/layouts";
import { BOOKMAKERS, DEFAULT_COMPANY_ID } from "../features/livescore/bookmakers";
import {
  ACTIVE_TAB_POLL_INTERVAL_MS,
  apiMatchTabs,
  COUNTS_SYNC_STALE_MS,
  LIVESTREAM_FALLBACK_DOMAINS,
  LIVESTREAM_ROUTE_PATH,
  matchTabs,
  MATCH_CLOCK_INTERVAL_MS,
  MATCH_INSIGHT_CACHE_MS,
  MATCH_INSIGHT_CLOSE_DELAY_MS,
  MATCH_INSIGHT_RETURN_LIST,
  MOBILE_VIEW_QUERY,
  ODD_CHANGE_HOLD_MS,
  WAKE_SYNC_STALE_MS,
} from "../features/livescore/config";
import { compactPlayerName } from "../features/livescore/format";
import {
  formatMatchClockHtml,
  setMatchClock,
  setMobileMatchStatusClock,
} from "../features/livescore/match-status";
import {
  loadFavorites,
  loadGroupMode,
  loadSettings,
  saveFavorites,
  saveGroupMode,
  saveSettings,
} from "../features/livescore/persistence";
import {
  dataGroupMode,
  isFixturesBoard,
  isResultsBoard,
  normalizeArray,
} from "../features/livescore/utils";
import type {
  BoardSettings,
  FilterMode,
  FilterOption,
  FilterStatus,
  GroupMode,
  MatchInsightEntry,
  MatchInsightIncident,
  MatchInsightStat,
  MatchTab,
  OddsFormat,
  OverlayKind,
  RenderEntry,
  State,
  View,
} from "../features/livescore/types";
import {
  livescoreRuntime,
  runtimeRequestKey,
} from "../shared/runtime/livescore-runtime";
import {
  DEFAULT_ODDS_FORMAT,
  findCompanyOdd,
  formatOdd,
  isOddRow,
  isOddTuple,
  normalizeOddsFormat,
  ODD_FIELD_KEYS,
  oddsSource,
  pickOdd as pickValue,
  roundOdd,
} from "../shared/odds";
import { mountLeagueStandings } from "../features/league-standings/layouts/default/main";
import { mountOddsBoard } from "../features/odds-board/layouts/default/main";

document.querySelectorAll<HTMLElement>("[data-gwd-standings-board]").forEach(mountLeagueStandings);
document.querySelectorAll<HTMLElement>("[data-gwd-odds-board]").forEach(mountOddsBoard);

const states = new WeakMap<HTMLElement, State>();
const TIME_TOTAL_STATUS_SEGMENTS = [
  { key: "live", statuses: ["LIVE"] },
  { key: "upcoming", statuses: ["UPCOMING"] },
  { key: "finished", statuses: ["FINISHED"] },
] as const;
const FIXTURES_TOTAL_STATUS_SEGMENTS = [
  { key: "live", statuses: ["LIVE"] },
  { key: "upcoming", statuses: ["UPCOMING"] },
] as const;
const FINISHED_POLL_WINDOW_MS = 4 * 60 * 60 * 1000;
const ODDS_DEBUG_MATCH_ID = "vjxm8ghen2kvr6o";
const TIME_MODE_STATUS_RANK: Record<FilterStatus, number> = {
  live: 0,
  upcoming: 1,
  finished: 2,
  odds: 3,
  all: 4,
};
const DEFAULT_FIXTURE_COMPETITION_IDS: string[] = [];
const ODDS_RETURN_LIST = [
  "asia",
  "asiaHt",
  "eu",
  "euHt",
  "bs",
  "bsHt",
  "cr",
  "cs",
  "csHt",
];
const MATCH_FILTER_ODDS_MARKETS = [
  "asia",
  "bs",
  "eu",
  "asiaHt",
  "bsHt",
  "euHt",
];
const MATCH_INSIGHT_ALLOWED_DETAIL_STATS = new Set(["83", "21", "25"]);
const MATCH_INSIGHT_DETAIL_STAT_LABEL_OVERRIDES: Record<string, string> = {
  "83": "C\u00fa s\u00fat",
  "21": "S\u00fat tr\u00fang \u0111\u00edch",
  "25": "TL ki\u1ec3m so\u00e1t b\u00f3ng",
};
const MATCH_INSIGHT_FALLBACK_STAT_LABEL_OVERRIDES: Record<string, string> = {
  shots: "C\u00fa s\u00fat",
  shotsOnTarget: "S\u00fat tr\u00fang \u0111\u00edch",
  possession: "TL ki\u1ec3m so\u00e1t b\u00f3ng",
};
const MATCH_INSIGHT_STAT_DEFS = [
  {
    key: "shots",
    label: "Cú sút",
    aliases: ["shots", "shoot", "shoots", "totalShots", "shot"],
  },
  {
    key: "shotsOnTarget",
    label: "Sút trúng",
    aliases: ["shotsOnTarget", "shotOnTarget", "shotsOnGoal", "targetShots"],
  },
  {
    key: "possession",
    label: "TL kiểm soát bóng",
    aliases: ["possession", "ballPossession", "possessionRate"],
    suffix: "%",
  },
  {
    key: "corners",
    label: "Phạt góc",
    aliases: ["corners", "corner", "cornerKicks"],
  },
  {
    key: "yellowCards",
    label: "Thẻ vàng",
    aliases: ["yellowCards", "yellow", "yellowCard"],
  },
  { key: "redCards", label: "Thẻ đỏ", aliases: ["redCards", "red", "redCard"] },
];
const MATCH_DETAIL_STAT_LABELS: Record<string, string> = {
  "1": "Bàn thắng",
  "2": "Phạt góc",
  "3": "Thẻ vàng",
  "4": "Thẻ đỏ",
  "5": "Việt vị",
  "6": "Đá phạt",
  "7": "Phát bóng lên",
  "8": "Phạt đền",
  "9": "Thay người",
  "10": "Bắt đầu",
  "11": "Giữa sân",
  "12": "Kết thúc",
  "13": "Tỷ số hiệp 1",
  "15": "Thẻ vàng thứ 2",
  "16": "Hỏng phạt đền",
  "17": "Phản lưới nhà",
  "19": "Thời gian bù giờ",
  "21": "Sút trúng đích",
  "22": "Sút không trúng đích",
  "23": "Tấn công",
  "24": "Tấn công nguy hiểm",
  "25": "Kiểm soát bóng",
  "26": "Hiệp phụ kết thúc",
  "27": "Luân lưu kết thúc",
  "28": "VAR",
  "29": "Sút luân lưu",
  "30": "Hỏng luân lưu",
  "37": "Sút bị chặn",
  "83": "Tổng cú sút",
};
const MATCH_DETAIL_STAT_ORDER = [
  "25",
  "23",
  "24",
  "83",
  "21",
  "22",
  "37",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "19",
  "28",
  "29",
];
const MATCH_INCIDENT_TYPE_LABELS: Record<
  string,
  { label: string; icon: string }
> = {
  goal: { label: "Bàn thắng", icon: "⚽" },
  penalty: { label: "Penalty", icon: "⚽" },
  own_goal: { label: "Phản lưới", icon: "⚽" },
  yellow_card: { label: "Thẻ vàng", icon: "🟨" },
  red_card: { label: "Thẻ đỏ", icon: "🟥" },
  substitution: { label: "Thay người", icon: "⇄" },
};
const MATCH_INSIGHT_INCIDENT_ICONS: Record<string, string> = {
  goal: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 12 12" fill="none" aria-hidden="true"><circle cx="6" cy="6" r="5.5" fill="#f2f5f8"/><path d="M6 2.1 7.5 3.2 6.9 5H5.1l-.6-1.8L6 2.1Zm-3.2 3 1.8.1.6 1.8-1.5 1.1A4 4 0 0 1 2.8 5.1Zm6.4 0a4 4 0 0 1-.9 3L6.8 7l.6-1.8 1.8-.1ZM4.3 9l1.7-.9 1.7.9A4 4 0 0 1 4.3 9Z" fill="#35495e"/><circle cx="6" cy="6" r="5.2" stroke="#6db740" stroke-width=".8"/></svg>`,
  "own-goal": `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 12 12" fill="none" aria-hidden="true"><circle cx="6" cy="6" r="5.4" fill="#f2f5f8" stroke="#e74c5b"/><path d="M3.7 3.7 8.3 8.3M8.3 3.7 3.7 8.3" stroke="#e74c5b" stroke-width="1.4" stroke-linecap="round"/></svg>`,
  "yellow-card": `<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="10" height="14" viewBox="0 0 9 12" fill="none"><rect width="9" height="12" rx="2" fill="#FFE400"/></svg>`,
  "red-card": `<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="10" height="14" viewBox="0 0 9 12" fill="none"><rect width="9" height="12" rx="2" fill="#FF3B30"/></svg>`,
  substitution: `<svg xmlns="http://www.w3.org/2000/svg"
     width="20"
     height="20"
     viewBox="0 0 20 20"
     fill="none"
     aria-hidden="true">
  <g transform="translate(0 0) scale(1.25)">
    <path d="M5.3 2.6 3.1 5c-.1.1-.1.2 0 .3 0 .1.1.1.2.1h1.4v7h1.6v-7h1.4c.1 0 .2 0 .2-.1.1-.1 0-.2 0-.3L5.6 2.6c-.1-.1-.2-.1-.3 0Z" fill="#00B242"/>
    <path d="M10.4 13.4 8.2 11c-.1-.1-.1-.2 0-.3 0-.1.1-.1.2-.1h1.4v-7h1.6v7h1.4c.1 0 .2 0 .2.1.1.1 0 .2 0 .3l-2.3 2.4c-.1.1-.2.1-.3 0Z" fill="#E11D23"/>
  </g>
</svg>`,
  var: `<svg xmlns="http://www.w3.org/2000/svg"
     width="20"
     height="20"
     viewBox="0 0 12 12"
     fill="none"
     aria-hidden="true">
  <rect x=".5" y=".5" width="11" height="8" rx="1" fill="#FFBA00"/>
  <rect x="1.5" y="1.5" width="9" height="6" rx=".4" fill="#4C8056"/>
  <path d="M4.5 9h3v1.4l.7.8H3.8l.7-.8V9Z" fill="#BD8E0F"/>
</svg>`,
};
const MATCH_INSIGHT_INCIDENT_TYPE_ICON_KEYS: Record<string, string> = {
  "1": "goal",
  "3": "yellow-card",
  "4": "red-card",
  "8": "goal",
  "9": "substitution",
  "15": "yellow-card",
  "16": "goal",
  "17": "own-goal",
  "28": "var",
  "29": "goal",
  "30": "goal",
};
const MATCH_INSIGHT_INCIDENT_TYPE_LABELS: Record<string, string> = {
  "1": "B\u00e0n th\u1eafng",
  "2": "Ph\u1ea1t g\u00f3c",
  "3": "Th\u1ebb v\u00e0ng",
  "4": "Th\u1ebb \u0111\u1ecf",
  "5": "Vi\u1ec7t v\u1ecb",
  "6": "\u0110\u00e1 ph\u1ea1t",
  "7": "Ph\u00e1t b\u00f3ng l\u00ean",
  "8": "Ph\u1ea1t \u0111\u1ec1n",
  "9": "Thay ng\u01b0\u1eddi",
  "10": "B\u1eaft \u0111\u1ea7u",
  "11": "HT",
  "12": "K\u1ebft th\u00fac",
  "13": "T\u1ef7 s\u1ed1 hi\u1ec7p 1",
  "15": "Th\u1ebb v\u00e0ng th\u1ee9 2",
  "16": "H\u1ecfng ph\u1ea1t \u0111\u1ec1n",
  "17": "Ph\u1ea3n l\u01b0\u1edbi nh\u00e0",
  "19": "Th\u1eddi gian b\u00f9 gi\u1edd",
  "21": "S\u00fat tr\u00fang \u0111\u00edch",
  "22": "S\u00fat kh\u00f4ng tr\u00fang \u0111\u00edch",
  "23": "T\u1ea5n c\u00f4ng",
  "24": "T\u1ea5n c\u00f4ng nguy hi\u1ec3m",
  "25": "Ki\u1ec3m so\u00e1t b\u00f3ng",
  "26": "Hi\u1ec7p ph\u1ee5 k\u1ebft th\u00fac",
  "27": "Lu\u00e2n l\u01b0u k\u1ebft th\u00fac",
  "28": "VAR",
  "29": "S\u00fat lu\u00e2n l\u01b0u",
  "30": "H\u1ecfng lu\u00e2n l\u01b0u",
  "37": "S\u00fat b\u1ecb ch\u1eb7n",
  "38": "Kho\u1ea3ng ngh\u1ec9 gi\u1eefa tr\u1eadn",
  "83": "T\u1ed5ng c\u00fa s\u00fat",
};
const api = async (
  endpoint: string,
  query: Record<string, any>,
  signal?: AbortSignal,
) => {
  return livescoreRuntime.fetchJson(endpoint, query, signal);
};

const fixtureRequestSignal = (state: State): AbortSignal | undefined => {
  if (!isFixturesBoard(state)) return undefined;
  if (!state.fixtureRequestController || state.fixtureRequestController.signal.aborted) {
    state.fixtureRequestController = new AbortController();
  }
  return state.fixtureRequestController.signal;
};

const cancelFixtureRequests = (state: State) => {
  state.fixtureRequestController?.abort();
  state.fixtureRequestController = new AbortController();
};

const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException && error.name === "AbortError";

const cloneTemplateElement = <T extends HTMLElement>(
  root: ParentNode,
  selector: string,
): T | null => {
  const template = root.querySelector<HTMLTemplateElement>(selector);
  return (template?.content.firstElementChild?.cloneNode(true) as T) || null;
};

const escapeHtml = (value: unknown): string =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const matchDetailEndpoint = (state: State, matchId: string): string => {
  const configured = String(state.contract.matchInsight?.endpoint || "");
  if (configured.includes("{matchId}")) {
    return configured.replace("{matchId}", encodeURIComponent(matchId));
  }

  const raw = String(configured || state.contract.endpoint || "/api/matches");
  const base = raw.replace(/\/+$/, "");
  return raw.endsWith("/")
    ? `${base}/${encodeURIComponent(matchId)}/`
    : `${base}/${encodeURIComponent(matchId)}`;
};

const buildMatchInsightQuery = (state: State): Record<string, any> => ({
  ...(state.contract.matchInsight?.query || {}),
  lang:
    state.contract.matchInsight?.query?.lang ||
    state.contract.query?.lang ||
    "vi",
  isIncludedRelation:
    state.contract.matchInsight?.query?.isIncludedRelation ?? true,
  returnList: normalizeArray(state.contract.matchInsight?.query?.returnList)
    .length
    ? normalizeArray(state.contract.matchInsight.query.returnList)
    : MATCH_INSIGHT_RETURN_LIST,
});

const statusForTab = (tab: MatchTab): string[] => {
  if (tab === "live") return ["LIVE"];
  if (tab === "finished") return ["FINISHED"];
  return ["LIVE", "FINISHED", "UPCOMING"];
};

const statusForFilter = (status: FilterStatus): string[] | null => {
  if (status === "live") return ["LIVE"];
  if (status === "finished") return ["FINISHED"];
  if (status === "upcoming") return ["UPCOMING"];
  return null;
};

const hotCompetitionIds = (state: State): string[] => {
  if (isFixturesBoard(state)) {
    const fixtureIds = normalizeArray(state.contract.fixtureCompetitionIds);
    return fixtureIds.length ? fixtureIds : DEFAULT_FIXTURE_COMPETITION_IDS;
  }

  const contractIds = normalizeArray(state.contract.priorityCompetitionIds);
  if (contractIds.length) return contractIds;

  const queryIds = normalizeArray(state.contract.query?.priorityCompetitionIds);
  return queryIds;
};

const applyApiGroupMode = (
  state: State,
  query: Record<string, any>,
  tab: MatchTab,
) => {
  delete query.priorityCompetitionIds;
  delete query.sortMatchTime;
  delete query.sortCompetition;

  const hotIds = hotCompetitionIds(state);
  if (state.groupMode === "league") {
    query.sortCompetition = "desc";
    if (hotIds.length) query.priorityCompetitionIds = hotIds;
    if (tab === "hot" && hotIds.length) query.competitionIds = hotIds;
    return;
  }

  query.sortMatchTime = tab === "finished" ? "desc" : "asc";
  if (tab === "hot" && hotIds.length) query.competitionIds = hotIds;
};

const buildQuery = (
  state: State,
  tab: MatchTab,
  useFilters = true,
): Record<string, any> => {
  const query: Record<string, any> = {
    ...(state.contract.query || {}),
    page: 1,
  };
  const matchWindow = currentMatchWindow();

  delete query.priorityCompetitionIds;
  delete query.competitionIds;
  delete query.countryIds;
  delete query.seasonId;
  delete query.companyIds;
  delete query.sortMatchTime;
  delete query.sortCompetition;
  const fixtureMode = isFixturesBoard(state);
  const dateInput =
    String(state.contract.fixtureDate || "") ||
    String(query.fixtureDate || "") ||
    String(query.date || "");
  const fixtureWindow = currentFixtureWindow(dateInput);
  query.fromDate = fixtureMode ? fixtureWindow.fromDate : matchWindow.fromDate;
  query.toDate = fixtureMode ? fixtureWindow.toDate : matchWindow.toDate;
  query.statuses = fixtureMode
    ? isResultsBoard(state) ? ["FINISHED"] : ["FINISHED", "LIVE", "UPCOMING"]
    : statusForTab(tab);

  query.returnList = isResultsBoard(state)
    ? ["info", "stats"]
    : ["info", "livestreams"];

  if (fixtureMode) {
    query.isIncludedRelation = "true";
    const selectedCompetitionIds =
      useFilters && state.applied.competitionIds.length
        ? state.applied.competitionIds
        : [];

    if (selectedCompetitionIds.length) {
      delete query.fromDate;
      delete query.toDate;
      delete query.priorityCompetitionIds;
      delete query.sortCompetition;
      query.competitionIds = selectedCompetitionIds;
      if (state.applied.seasonId) query.seasonId = state.applied.seasonId;
      query.sortMatchTime = "desc";
    } else {
      const priorityIds = hotCompetitionIds(state);
      if (state.groupMode === "league") {
        query.sortCompetition = "desc";
        if (priorityIds.length) query.priorityCompetitionIds = priorityIds;
      } else {
        query.sortMatchTime = isResultsBoard(state) ? "desc" : "asc";
      }
    }
  } else {
    applyApiGroupMode(state, query, tab);
  }

  if (useFilters && !fixtureMode) {
    const filterStatuses = statusForFilter(state.applied.status);
    if (filterStatuses) query.statuses = filterStatuses;
  }

  return query;
};

const hasSelectedFixtureDate = (state: State): boolean =>
  Boolean(String(state.contract.fixtureDate || "").trim());

const buildIndexQuery = (state: State): Record<string, any> => {
  if (!isFixturesBoard(state)) return buildQuery(state, "total", false);

  if (state.applied.competitionIds.length) {
    return buildQuery(state, "total", true);
  }

  const query = buildQuery(state, "total", false);
  if (isResultsBoard(state)) return query;
  if (hasSelectedFixtureDate(state)) {
    return query;
  }

  if (!state.applied.competitionIds.length) {
    delete query.competitionIds;
    delete query.priorityCompetitionIds;
    delete query.sortCompetition;
    delete query.sortMatchTime;
  }
  return query;
};

const queryKey = (query: Record<string, any>): string =>
  JSON.stringify(
    Object.keys(query)
      .sort()
      .map((key) => [key, query[key]]),
  );

const payloadData = (payload: any): Record<string, any> => {
  if (
    payload?.data?.matches ||
    payload?.data?.relation ||
    payload?.data?.total !== undefined
  )
    return payload.data;
  return payload || {};
};

const logResultsApiData = (
  state: State,
  source: "php-initial" | "client-fetch",
  payload: any,
  query?: Record<string, any>,
) => {
  if (!isResultsBoard(state)) return;
  const data = payloadData(payload);
  console.groupCollapsed(
    `[GWD Results API] ${source} | ${Array.isArray(data.matches) ? data.matches.length : 0} matches`,
  );
  if (query) console.log("query:", query);
  console.log("response:", payload);
  console.log("data:", data);
  console.log("matches:", Array.isArray(data.matches) ? data.matches : []);
  console.log("relation:", data.relation || {});
  console.groupEnd();
};

const oddsReturnList = (state: State): string[] => {
  const configured = normalizeArray(state.contract.oddsReturnList);
  return configured.length ? configured : ODDS_RETURN_LIST;
};

const syncGroupModeStores = (state: State) => {
  const storeMode = dataGroupMode(state);
  state.views = state.viewsByGroupMode[storeMode];
  state.indexView = state.indexViewsByGroupMode[storeMode];
  state.totals = state.totalsByGroupMode[storeMode];
};

const ensureView = (state: State, tab: MatchTab, useFilters = true): View => {
  syncGroupModeStores(state);
  if (tab === "follow") return ensureFollowView(state);
  const nextQuery = buildQuery(state, tab, useFilters);
  const filtered =
    useFilters &&
    (state.applied.competitionIds.length > 0 ||
      state.applied.countryIds.length > 0 ||
      Boolean(statusForFilter(state.applied.status)));
  const current = state.views[tab];

  if (
    current &&
    current.filtered === filtered &&
    queryKey(current.query) === queryKey(nextQuery)
  ) {
    return current;
  }

  const view: View = {
    tab,
    groupMode: dataGroupMode(state),
    query: nextQuery,
    ids: [],
    page: 0,
    total: Infinity,
    loading: false,
    done: false,
    filtered,
    paint: false,
  };

  state.views[tab] = view;
  return view;
};

const ensureFollowView = (state: State): View => {
  syncGroupModeStores(state);
  const ids = [...state.favorites].filter((id) =>
    Boolean(state.entities.matches[id]),
  );
  const view: View = {
    tab: "follow",
    groupMode: dataGroupMode(state),
    query: { follow: true },
    ids,
    page: 1,
    total: ids.length,
    loading: false,
    done: true,
    filtered: false,
    paint: true,
  };
  state.views.follow = view;
  return view;
};

const ensureIndexView = (state: State): View => {
  syncGroupModeStores(state);
  const nextQuery = buildIndexQuery(state);
  const current = state.indexView;

  if (current && queryKey(current.query) === queryKey(nextQuery)) {
    return current;
  }

  state.indexView = {
    tab: "total",
    groupMode: dataGroupMode(state),
    query: nextQuery,
    ids: [],
    page: 0,
    total: Infinity,
    loading: false,
    done: false,
    filtered: false,
    paint: false,
  };
  state.indexViewsByGroupMode[state.indexView.groupMode] = state.indexView;

  return state.indexView;
};

const relationItems = (value: any): any[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "object") return Object.values(value);
  return [];
};

const mergeRelations = (state: State, relation: Record<string, any>) => {
  relationItems(relation.teams).forEach((team) => {
    if (team?.id) state.entities.teams[String(team.id)] = team;
  });

  relationItems(relation.competitions).forEach((competition) => {
    if (competition?.id)
      state.entities.competitions[String(competition.id)] = {
        ...(state.entities.competitions[String(competition.id)] || {}),
        ...Object.fromEntries(
          Object.entries(competition).filter(
            ([, value]) => value !== undefined && value !== null && value !== "",
          ),
        ),
      };
  });

  relationItems(relation.venues).forEach((venue) => {
    if (venue?.id) state.entities.venues[String(venue.id)] = venue;
  });

  relationItems(relation.stages).forEach((stage) => {
    if (stage?.id) state.entities.stages[String(stage.id)] = stage;
  });

  relationItems(relation.seasons).forEach((season) => {
    if (season?.id) state.entities.seasons[String(season.id)] = season;
  });

  relationItems(relation.countries).forEach((country) => {
    if (country?.id) {
      state.entities.countries[String(country.id)] = {
        id: String(country.id),
        name: country.name || country.shortName || country.country || "Kh?c",
      };
    }
  });

  relationItems(relation.venues).forEach((venue) => {
    if (venue?.countryId && venue?.country) {
      state.entities.countries[String(venue.countryId)] = {
        id: String(venue.countryId),
        name: String(venue.country),
      };
    }
  });
};

const mergeMatchEntity = (state: State, id: string, next: any): boolean => {
  return livescoreRuntime.mergeMatch(id, next).changed;
};

const livestreamMatchId = (item: any, fallbackId = ""): string =>
  String(
    item?.matchId ??
      item?.match_id ??
      item?.id ??
      item?.info?.id ??
      fallbackId ??
      "",
  );

const collectLivestreamSources = (data: any): Record<string, any> => {
  const result: Record<string, any> = {};
  const append = (item: any, fallbackId = "") => {
    const id = livestreamMatchId(item, fallbackId);
    if (!id) return;
    result[id] = item;
  };
  const appendSource = (source: any) => {
    if (!source) return;
    if (Array.isArray(source)) {
      source.forEach((item) => append(item));
      return;
    }
    if (typeof source === "object") {
      Object.entries(source).forEach(([id, item]) => append(item, id));
    }
  };

  appendSource(data?.livestreams);
  appendSource(data?.relation?.livestreams);
  return result;
};

const mergePayload = (state: State, view: View, payload: any) => {
  const previousIdCount = view.ids.length;
  const data = payloadData(payload);
  const relation = {
    ...(data.relation || {}),
    teams: data.relation?.teams || data.teams || [],
    competitions: data.relation?.competitions || data.competitions || [],
    venues: data.relation?.venues || data.venues || [],
    countries: data.relation?.countries || data.countries || [],
    stages: data.relation?.stages || data.stages || [],
    seasons: data.relation?.seasons || data.seasons || [],
  };
  mergeRelations(state, relation);

  const livestreamsByMatchId = collectLivestreamSources(data);
  const matches = (data.matches || []).map((match: any) => {
    const id = String(match?.id ?? match?.info?.id ?? "");
    if (!id || match?.livestreams !== undefined || livestreamsByMatchId[id] === undefined)
      return match;
    return { ...match, livestreams: livestreamsByMatchId[id] };
  });

  const { ids, addedIds, changedIds } = livescoreRuntime.mergeMatches(
    matches,
  );
  ids.forEach((id) => {
    if (!view.ids.includes(id)) view.ids.push(id);
  });

  if (changedIds.length || addedIds.length)
    livescoreRuntime.emit("matches:update", {
      changedIds,
      addedIds,
      source: "mergePayload",
    });

  view.total = Number(data.total ?? view.ids.length);
  view.done =
    view.ids.length >= view.total ||
    matches.length === 0 ||
    view.ids.length === previousIdCount;
  return { ids, total: view.total, empty: matches.length === 0 };
};

const pruneFavorites = (state: State) => {
  let changed = false;
  state.favorites.forEach((id) => {
    if (!state.entities.matches[id]) {
      state.favorites.delete(id);
      changed = true;
    }
  });
  if (changed) saveFavorites(state.favorites);
};

const collectMarketOdds = (data: Record<string, any>): any[] => {
  const matches: any[] = [];
  const appendMatch = (
    match: any,
    fallbackId?: string,
    clearMissingMarkets = false,
  ) => {
    const id = match?.id ?? match?.matchId ?? match?.match_id ?? fallbackId;
    if (!id) return;

    const normalized = { ...(match || {}), id };
    if (clearMissingMarkets) {
      ODDS_RETURN_LIST.forEach((market) => {
        if (normalized[market] === undefined) normalized[market] = null;
      });
    }
    matches.push(normalized);
  };

  const sourceMatches = data.matches || data.items || data.odds || [];
  if (Array.isArray(sourceMatches)) {
    sourceMatches.forEach((match: any) => appendMatch(match, undefined, true));
  } else if (sourceMatches && typeof sourceMatches === "object") {
    Object.entries(sourceMatches).forEach(([id, match]) =>
      appendMatch(match, id, true),
    );
  }

  ODDS_RETURN_LIST.forEach((market) => {
    const source = data[market];
    if (!source) return;
    const rows = Array.isArray(source)
      ? source.map((row) => ["", row])
      : Object.entries(source);
    rows.forEach(([fallbackId, row]: any) => {
      if (fallbackId && isOddTuple(row)) {
        matches.push({ id: fallbackId, [market]: row });
        return;
      }

      (Array.isArray(row) && !isOddTuple(row) ? row : [row]).forEach(
        (item: any) => {
          const id = item?.matchId ?? item?.match_id ?? item?.id ?? fallbackId;
          if (!id) return;
          matches.push({ id, [market]: [item] });
        },
      );
    });
  });

  return matches;
};

const mergeOddsPayload = (
  state: State,
  payload: any,
): { changedIds: string[]; total?: number } => {
  const data = payloadData(payload);
  const oddsMatches = collectMarketOdds(data);
  const { changedIds } = livescoreRuntime.mergeMatches(oddsMatches);
  if (oddsMatches.length && !changedIds.length) {
    console.warn("GWD Livescore odds payload has no match id", data);
  }
  const total = Number(data.total);
  if (changedIds.length)
    livescoreRuntime.emit("odds:update", {
      changedIds,
      source: "mergeOddsPayload",
    });
  return {
    changedIds,
    total: Number.isFinite(total) ? total : undefined,
  };
};

const configuredOddsCompanyIds = (state: State): string[] => {
  const ids = normalizeArray(state.contract.companyIds).filter(
    (companyId) => Boolean(BOOKMAKERS[companyId]),
  );
  return ids.length ? ids : [state.companyId || DEFAULT_COMPANY_ID];
};

const buildOddsQuery = (
  state: State,
  view: View,
  page: number,
): Record<string, any> => {
  const query: Record<string, any> = {
    ...view.query,
    page,
    companyIds: configuredOddsCompanyIds(state),
    returnList: oddsReturnList(state),
  };

  if (state.polling) {
    // Keep all configured bookmaker markets fresh, matching gwd_odds. The
    // selected company only controls presentation and never narrows polling.
    query.statuses = ["LIVE", "UPCOMING"];
  }

  if (state.groupMode === "time") {
    delete query.sortCompetition;
    delete query.priorityCompetitionIds;
  } else {
    delete query.sortMatchTime;
  }

  return query;
};

const logLivescoreOddsResponse = (
  state: State,
  query: Record<string, any>,
  payload: any,
) => {
  const data = payloadData(payload);
  const matches = Array.isArray(data.matches) ? data.matches : [];
  console.groupCollapsed(
    `[GWD Odds Compare] livescore | company=${state.companyId} | matches=${matches.length}`,
  );
  console.log("query", query);
  console.log("raw payload", payload);
  console.table(
    matches.map((match: any) => ({
      id: String(match?.id || match?.info?.id || ""),
      status: match?.info?.status || match?.info?.statusId || "",
      asia: Boolean(match?.asia || match?.odds?.asia || match?.markets?.asia),
      eu: Boolean(match?.eu || match?.odds?.eu || match?.markets?.eu),
      bs: Boolean(match?.bs || match?.odds?.bs || match?.markets?.bs),
    })),
  );
  const debugMatch = matches.find(
    (match: any) => String(match?.id || match?.info?.id || "") === ODDS_DEBUG_MATCH_ID,
  );
  console.log(`debug match ${ODDS_DEBUG_MATCH_ID}`, debugMatch || "not returned in this page");
  console.groupEnd();
};

const loadOddsPage = async (
  board: HTMLElement,
  state: State,
  view: View,
  page: number,
) => {
  const requestKey = `${queryKey(view.query)}::odds::${page}::${configuredOddsCompanyIds(state).join(",")}`;
  if (state.oddsRequests.has(requestKey)) return;

  state.oddsRequests.add(requestKey);
  updateFilterTabCounts(board, state);
  try {
    const oddsQuery = buildOddsQuery(state, view, page);
    const payload = await api(
      state.contract.endpoint,
      oddsQuery,
      fixtureRequestSignal(state),
    );
    logLivescoreOddsResponse(state, oddsQuery, payload);
    const { changedIds } = mergeOddsPayload(state, payload);
    syncOddsBadge(board, state);
    updateFilterTabCounts(board, state);
    patchVisibleOddsRows(board, state, changedIds);
    if (state.active === "odds" || state.applied.status === "odds") {
      const oddsView = ensureView(state, state.active, true);
      syncViewBadge(board, state, oddsView);
      if (canPaint(state, oddsView))
        reconcileVisibleViewRows(board, state, oddsView);
    }
  } catch (error) {
    console.warn("GWD Livescore odds request failed", error);
  } finally {
    state.oddsRequests.delete(requestKey);
    updateFilterTabCounts(board, state);
    if (isIndexCountReady(state) && state.oddsRequests.size === 0) {
      syncBackgroundStateToActiveView(board, state);
    }
  }
};

const loadOddsForLoadedPages = (
  board: HTMLElement,
  state: State,
  view: View,
) => {
  if (isFixturesBoard(state)) return;
  const loadedPages = Math.max(view.page, view.ids.length ? 1 : 0);
  for (let page = 1; page <= loadedPages; page += 1) {
    void loadOddsPage(board, state, view, page);
  }
};

const positionLabel = (value: unknown): string => {
  if (value === undefined || value === null || value === "") return "";
  return `(${value})`;
};

const numericOdd = (value: unknown): number | null => {
  if (value === undefined || value === null || value === "") return null;
  const normalized = Number(String(value).trim());
  return Number.isFinite(normalized) ? normalized : null;
};

const renderOddContent = (element: HTMLElement, text: string) => {
  const nextIcon = element.classList.contains("is-odd-up")
    ? "up"
    : element.classList.contains("is-odd-down")
      ? "down"
      : "";
  if (
    element.dataset.gwdRenderedOddText === text &&
    element.dataset.gwdRenderedOddIcon === nextIcon
  ) {
    return;
  }

  element.replaceChildren(document.createTextNode(text));
  const iconText = nextIcon === "up" ? "\u25b2" : nextIcon === "down" ? "\u25bc" : "";
  if (iconText) {
    const board = element.closest<HTMLElement>("[data-gwd-livescore-board]");
    const icon = board
      ? cloneTemplateElement<HTMLElement>(
          board,
          "[data-gwd-odd-change-icon-template]",
        )
      : null;
    if (icon) {
      icon.textContent = iconText;
      element.append(icon);
    }
  }
  element.dataset.gwdRenderedOddText = text;
  element.dataset.gwdRenderedOddIcon = nextIcon;
};

const setOddText = (
  root: ParentNode,
  selector: string,
  value: unknown,
  state: State,
) => {
  const element = root.querySelector<HTMLElement>(selector);
  if (!element) return;

  const nextText = formatOdd(value, state.oddsFormat);
  const numericNextValue = numericOdd(value);
  const nextValue = numericNextValue === null ? null : roundOdd(numericNextValue);
  const previousValue = numericOdd(element.dataset.gwdOddValue);
  element.dataset.gwdOddText = nextText;

  if (nextValue === null) {
    window.clearTimeout(Number(element.dataset.gwdOddTimer || 0));
    element.classList.remove("is-odd-up", "is-odd-down");
    delete element.dataset.gwdOddTimer;
  } else if (
    state.settings.showOddsChange &&
    previousValue !== null &&
    nextValue !== previousValue
  ) {
    window.clearTimeout(Number(element.dataset.gwdOddTimer || 0));
    element.classList.remove("is-odd-up", "is-odd-down");
    element.classList.add(
      nextValue > previousValue ? "is-odd-up" : "is-odd-down",
    );
    element.dataset.gwdOddTimer = String(
      window.setTimeout(() => {
        element.classList.remove("is-odd-up", "is-odd-down");
        delete element.dataset.gwdOddTimer;
        renderOddContent(
          element,
          element.dataset.gwdOddText || element.textContent || "-",
        );
      }, ODD_CHANGE_HOLD_MS),
    );
  } else if (!state.settings.showOddsChange) {
    window.clearTimeout(Number(element.dataset.gwdOddTimer || 0));
    element.classList.remove("is-odd-up", "is-odd-down");
    delete element.dataset.gwdOddTimer;
  }

  renderOddContent(element, nextText);
  element.dataset.gwdOddFormat = state.oddsFormat;
  if (nextValue === null) {
    delete element.dataset.gwdOddValue;
  } else {
    element.dataset.gwdOddValue = String(nextValue);
  }
};

const slugifyText = (value: string): string =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/đ/g, "d")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const livestreamSource = (match: any): any =>
  match?.livestreams ??
  match?.info?.livestreams ??
  match?.relation?.livestreams ??
  null;

const livestreamLinks = (match: any): any[] => {
  const source = livestreamSource(match);
  if (!source) return [];
  const links = Array.isArray(source)
    ? source
    : Array.isArray(source.links)
      ? source.links
      : Array.isArray(source.items)
        ? source.items
        : [];
  return links.filter(Boolean);
};

const livestreamCommentatorLinks = (match: any): any[] =>
  livestreamLinks(match).filter((link) =>
    Boolean(
      link?.commentator ||
        link?.commentatorName ||
        link?.commentatorSource ||
        link?.source ||
        link?.name ||
        link?.avatar,
    ),
  );

const hasLivestream = (match: any): boolean => {
  const source = livestreamSource(match);
  if (!source) return false;
  if (Array.isArray(source)) return source.length > 0;
  if (Array.isArray(source.links) || Array.isArray(source.items))
    return livestreamLinks(match).length > 0 || Boolean(source.available);
  return Boolean(source.available ?? source.enabled ?? source);
};

const fixtureRoundLabel = (match: any): string => {
  const info = match?.info || {};
  const infoRound = info.round && typeof info.round === "object" ? info.round : {};
  const label = String(infoRound.roundNum ?? "").trim();
  if (!label || label === "0") return "";
  return /^\d+$/.test(label) ? `Round ${label}` : label;
};

const fixtureStageLabelMap: Record<string, string> = {
  final: "Chung kết",
  "semi-finals": "Bán kết",
  semifinals: "Bán kết",
  "quarter-finals": "Tứ kết",
  quarterfinals: "Tứ kết",
  "round of 16": "Vòng 1/8",
  "third place": "Tranh hạng ba",
  "group stage": "Vòng bảng",
  league: "",
};

const groupLetter = (value: unknown): string => {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return "";
  return String.fromCharCode(64 + Math.min(26, Math.max(1, num)));
};

const fixtureRoundMeta = (state: State, match: any) => {
  const round =
    match?.info?.round && typeof match.info.round === "object"
      ? match.info.round
      : {};
  const stageId = String(round.stageId || "");
  const stage = stageId ? state.entities.stages?.[stageId] || {} : {};
  const stageName = String(stage.name || "").trim();
  const stageKey = stageName.toLowerCase();
  const stageLabel = fixtureStageLabelMap[stageKey] ?? stageName;
  const stageOrder = Number(stage.order || 0);
  const roundNum = Number(round.roundNum || 0);
  const groupNum = Number(round.groupNum || 0);
  const groupCount = Number(stage.groupCount || 0);
  const isLeagueStage = stageKey === "league" || (!stageName && roundNum > 0);

  let label = "";
  if (isLeagueStage && roundNum > 0) {
    label = `Vòng ${roundNum}`;
  } else if (groupCount > 0 || groupNum > 0) {
    const parts = [stageLabel || "Vòng bảng"];
    const letter = groupLetter(groupNum);
    if (letter) parts.push(`Bảng ${letter}`);
    if (roundNum > 0) parts.push(`Lượt ${roundNum}`);
    label = parts.join(" - ");
  } else if (stageLabel) {
    label = stageLabel;
  } else if (roundNum > 0) {
    label = `Vòng ${roundNum}`;
  }

  return {
    groupNum,
    key: stageId || `round-${roundNum || "unknown"}`,
    label: label || "Vòng đấu",
    roundNum,
    stageOrder,
  };
};

const hasGwdLivestreamPlugin = (state: State): boolean =>
  Boolean(state.contract?.hasGwdLivestream);

const livestreamFallbackBases = (state: State): string[] => {
  const configured = state.contract?.livestreamFallbackBases;
  if (Array.isArray(configured)) return configured.map(String).filter(Boolean);
  return LIVESTREAM_FALLBACK_DOMAINS;
};

const livestreamRouteBase = (state: State, useFallback: boolean): string => {
  const configured = useFallback ? livestreamFallbackBases(state).find(Boolean) : "";
  if (configured) return configured.replace(/\/+$/, "");
  return `${location.origin}${LIVESTREAM_ROUTE_PATH}`.replace(/\/+$/, "");
};

const buildLivestreamUrl = (
  state: State,
  matchId: string,
  homeName: string,
  awayName: string,
  link?: any,
  useFallback = false,
): string => {
  const slug = `${slugifyText(homeName)}-vs-${slugifyText(awayName)}-${matchId}`;
  let url: URL;
  try {
    url = new URL(`${livestreamRouteBase(state, useFallback)}/${slug}`);
  } catch {
    return "";
  }
  const liveId = link?.liveId || link?.live_id || link?.commentatorId || link?.id || "";
  if (liveId && !useFallback) url.searchParams.set("liveid", String(liveId));
  return url.toString();
};

const commentatorName = (link: any, index: number): string =>
  String(
    link?.commentator ||
      link?.commentatorName ||
      link?.commentatorSource ||
      link?.source ||
      link?.name ||
      `BLV ${index + 1}`,
  );

const patchFixtureWatch = (
  row: HTMLElement,
  state: State,
  match: any,
  matchId: string,
  homeName: string,
  awayName: string,
) => {
  const wrap = row.querySelector<HTMLElement>("[data-gwd-fixture-watch]");
  if (!wrap) return;

  const hasPlugin = hasGwdLivestreamPlugin(state);
  const commentatorLinks = livestreamCommentatorLinks(match);
  const available =
    statusOf(match) !== "finished" &&
    (hasPlugin ? commentatorLinks.length > 0 : hasLivestream(match));
  // Keep the empty watch cell in the grid so the round stays in the last column.
  if (wrap.hidden) wrap.hidden = false;
  wrap.classList.toggle("is-empty", !available);

  const button = wrap.querySelector<HTMLButtonElement>("button");
  if (button) {
    const nextTabIndex = available ? 0 : -1;
    if (button.tabIndex !== nextTabIndex) button.tabIndex = nextTabIndex;
    const nextAriaHidden = available ? "false" : "true";
    if (button.getAttribute("aria-hidden") !== nextAriaHidden)
      button.setAttribute("aria-hidden", nextAriaHidden);
  }

  const menu = wrap.querySelector<HTMLElement>("[data-gwd-fixture-watch-menu]");
  if (!menu) return;
  const disableWatch = () => {
    wrap.classList.add("is-empty");
    if (button) {
      button.tabIndex = -1;
      button.setAttribute("aria-hidden", "true");
    }
    if (menu.childNodes.length) menu.replaceChildren();
    menu.hidden = true;
    delete menu.dataset.gwdFixtureWatchSignature;
    delete wrap.dataset.gwdFixtureWatchMode;
    delete wrap.dataset.gwdFixtureWatchHref;
  };
  if (!available) {
    disableWatch();
    return;
  }

  const fallbackUrl = buildLivestreamUrl(state, matchId, homeName, awayName, undefined, true);
  if (!hasPlugin && !fallbackUrl) {
    disableWatch();
    return;
  }

  wrap.dataset.gwdFixtureWatchMode = hasPlugin ? "dropdown" : "fallback";
  if (hasPlugin) {
    delete wrap.dataset.gwdFixtureWatchHref;
  } else {
    wrap.dataset.gwdFixtureWatchHref = fallbackUrl;
  }
  const links = hasPlugin ? commentatorLinks : [{}];
  const signatureItems = hasPlugin
    ? commentatorLinks
        .map((link, index) => [
          buildLivestreamUrl(state, matchId, homeName, awayName, link, false),
          commentatorName(link, index),
        ])
        .filter(([href]) => Boolean(href))
    : [[fallbackUrl, "Xem trực tiếp"]];
  if (signatureItems.length === 0) {
    disableWatch();
    return;
  }
  const signature = JSON.stringify(signatureItems);
  if (
    menu.dataset.gwdFixtureWatchSignature === signature &&
    menu.querySelectorAll("a").length === signatureItems.length
  )
    return;

  const templateRoot =
    wrap.closest<HTMLElement>("[data-gwd-livescore-board]") || wrap;
  const fragment = document.createDocumentFragment();
  links.forEach((link, index) => {
    const href = hasPlugin
      ? buildLivestreamUrl(state, matchId, homeName, awayName, link, false)
      : fallbackUrl;
    if (!href) return;
    const anchor =
      cloneTemplateElement<HTMLAnchorElement>(
        templateRoot,
        "[data-gwd-fixture-watch-link-template]",
      ) || document.createElement("a");
    anchor.href = href;
    anchor.target = "_blank";
    anchor.rel = "nofollow noopener noreferrer";
    anchor.textContent = hasPlugin ? commentatorName(link, index) : "Xem trực tiếp";
    fragment.append(anchor);
  });
  menu.replaceChildren(fragment);
  menu.dataset.gwdFixtureWatchSignature = signature;
};
const closeFixtureWatchMenus = (board: HTMLElement) => {
  board
    .querySelectorAll<HTMLElement>("[data-gwd-fixture-watch-menu]")
    .forEach((menu) => {
      menu.hidden = true;
    });
};

const patchFixtureMatchRow = (
  row: HTMLElement,
  state: State,
  id: string,
  match: any,
  homeName: string,
  awayName: string,
) => {
  const info = match.info || {};
  const status = statusOf(match);
  row.dataset.gwdFixtureStatus = status;

  const dateElement = row.querySelector<HTMLElement>("[data-gwd-date]");
  const timeElement = row.querySelector<HTMLElement>("[data-gwd-time]");
  if (dateElement) {
    const dateHidden = status === "live";
    if (dateElement.hidden !== dateHidden) dateElement.hidden = dateHidden;
    const nextDate = formatMatchDate(info.matchTime);
    if (dateElement.textContent !== nextDate) dateElement.textContent = nextDate;
  }
  if (timeElement) {
    if (status === "live") {
      const nextHtml = formatMatchClockHtml(info);
      if (timeElement.innerHTML !== nextHtml) timeElement.innerHTML = nextHtml;
    } else {
      const nextText =
        status === "finished" && !isResultsBoard(state) ? `FT ${formatTime(info.matchTime)}` : formatTime(info.matchTime);
      if (timeElement.textContent !== nextText) timeElement.textContent = nextText;
    }
  }

  const homeMeta = displayScoreMeta(info, "home");
  const awayMeta = displayScoreMeta(info, "away");
  const markers = winnerMarkers(info.homeScores, info.awayScores);
  const markerTips = winnerTooltips(info.homeScores, info.awayScores);
  const upcoming = status === "upcoming" || isNotStartedInfo(info);
  setText(row, "[data-gwd-home-score]", upcoming ? "VS" : homeMeta.goal);
  setText(row, "[data-gwd-away-score]", upcoming ? "" : awayMeta.goal);
  row
    .querySelector<HTMLElement>(".gwd-fixtures-board__vs")
    ?.classList.toggle("is-vs", upcoming);

  const round = isFixtureLeagueFiltered(state) ? "" : fixtureRoundLabel(match);
  const roundElement = row.querySelector<HTMLElement>("[data-gwd-round]");
  if (roundElement) {
    const mobileDateFallback = isResultsBoard(state)
      ? ""
      : [formatMatchDate(info.matchTime), formatTime(info.matchTime)]
          .filter(Boolean)
          .join(" ");
    const roundText = round || mobileDateFallback;
    if (roundElement.textContent !== roundText) roundElement.textContent = roundText;
    const hasMobileDateFallback = round === "" && mobileDateFallback !== "";
    const roundHidden = round === "" && !hasMobileDateFallback;
    if (roundElement.hidden !== roundHidden) roundElement.hidden = roundHidden;
    roundElement.classList.toggle(
      "has-mobile-date-fallback",
      hasMobileDateFallback,
    );
  }

  setVisibleText(row, "[data-gwd-home-extra]", "*", markers.home);
  setVisibleText(row, "[data-gwd-away-extra]", "*", markers.away);
  setTooltip(row, "[data-gwd-home-extra]", markerTips.home);
  setTooltip(row, "[data-gwd-away-extra]", markerTips.away);
  const extraResult = mobileExtraResultText(info, homeName, awayName);
  setVisibleText(
    row,
    "[data-gwd-fixture-extra-result]",
    extraResult,
    Boolean(extraResult),
  );

  if (isResultsBoard(state)) {
    const populatedStats = (...sources: any[]) =>
      sources.find(
        (source) =>
          source &&
          typeof source === "object" &&
          !Array.isArray(source) &&
          Object.keys(source).length > 0,
      ) || {};
    const homeStats = populatedStats(
      match.resultStats?.home,
      info.homeTeamStats,
      match.stats?.homeTeamStats,
      match.stats?.home,
      match.stats?.homeTeam,
    );
    const awayStats = populatedStats(
      match.resultStats?.away,
      info.awayTeamStats,
      match.stats?.awayTeamStats,
      match.stats?.away,
      match.stats?.awayTeam,
    );
    const stat = (stats: any, key: string, fallback: string | number = "-") => stats[key] !== undefined && stats[key] !== null && stats[key] !== "" ? stats[key] : fallback;
    const homeScore = stat(homeStats, "score", homeMeta.goal);
    const awayScore = stat(awayStats, "score", awayMeta.goal);
    const homeRed = Number(stat(homeStats, "redCards", homeMeta.red));
    const awayRed = Number(stat(awayStats, "redCards", awayMeta.red));
    const homeYellow = Number(stat(homeStats, "yellowCards", homeMeta.yellow));
    const awayYellow = Number(stat(awayStats, "yellowCards", awayMeta.yellow));
    const homeCorner = stat(homeStats, "corners", homeMeta.corner >= 0 ? homeMeta.corner : "-");
    const awayCorner = stat(awayStats, "corners", awayMeta.corner >= 0 ? awayMeta.corner : "-");
    setText(row, "[data-gwd-home-score]", homeScore);
    setText(row, "[data-gwd-away-score]", awayScore);
    setVisibleText(row, "[data-gwd-home-red]", homeRed, homeRed > 0);
    setVisibleText(row, "[data-gwd-away-red]", awayRed, awayRed > 0);
    setVisibleText(row, "[data-gwd-home-yellow]", homeYellow, homeYellow > 0);
    setVisibleText(row, "[data-gwd-away-yellow]", awayYellow, awayYellow > 0);
    setText(row, "[data-gwd-result-half]", `${stat(homeStats, "firstHalfScore", homeMeta.half)} : ${stat(awayStats, "firstHalfScore", awayMeta.half)}`);
    setText(row, "[data-gwd-result-corners-h1]", `${stat(homeStats, "firstHalfCorners")} - ${stat(awayStats, "firstHalfCorners")}`);
    setText(row, "[data-gwd-result-corners-ft]", `${homeCorner} - ${awayCorner}`);
    setText(row, "[data-gwd-result-cards-h1]", `${stat(homeStats, "firstHalfYellowCard")} - ${stat(awayStats, "firstHalfYellowCard")}`);
    setText(row, "[data-gwd-result-cards-ft]", `${homeYellow} - ${awayYellow}`);
  }

  patchFixtureWatch(row, state, match, id, homeName, awayName);
};

const matchDateKey = (state: State, id: string): string => {
  const value = state.entities.matches[id]?.info?.matchTime;
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "__unknown_date__";
  const parts = new Intl.DateTimeFormat("en-CA", {
    day: "2-digit",
    month: "2-digit",
    timeZone: "Asia/Ho_Chi_Minh",
    year: "numeric",
  }).formatToParts(date);
  const byType = Object.fromEntries(
    parts.map((part) => [part.type, part.value]),
  );
  return `${byType.year}-${byType.month}-${byType.day}`;
};

const patchOdds = (row: HTMLElement, state: State, match: any) => {
  const companyId = state.companyId || DEFAULT_COMPANY_ID;
  const asia =
    findCompanyOdd(oddsSource(match, "asia"), companyId, "asia") ||
    findCompanyOdd(oddsSource(match, "asiaHt"), companyId, "asiaHt");
  const eu =
    findCompanyOdd(oddsSource(match, "eu"), companyId, "eu") ||
    findCompanyOdd(oddsSource(match, "euHt"), companyId, "euHt");
  const bs =
    findCompanyOdd(oddsSource(match, "bs"), companyId, "bs") ||
    findCompanyOdd(oddsSource(match, "bsHt"), companyId, "bsHt");

  setOddText(
    row,
    "[data-gwd-odds-asia-home]",
    pickValue(asia, [...ODD_FIELD_KEYS.home]),
    state,
  );
  setText(
    row,
    "[data-gwd-odds-asia-handicap]",
    pickValue(asia, [...ODD_FIELD_KEYS.line]),
  );
  setOddText(
    row,
    "[data-gwd-odds-asia-away]",
    pickValue(asia, [...ODD_FIELD_KEYS.away]),
    state,
  );
  setOddText(
    row,
    "[data-gwd-odds-eu-home]",
    pickValue(eu, [...ODD_FIELD_KEYS.home]),
    state,
  );
  setOddText(
    row,
    "[data-gwd-odds-eu-draw]",
    pickValue(eu, [...ODD_FIELD_KEYS.draw]),
    state,
  );
  setOddText(
    row,
    "[data-gwd-odds-eu-away]",
    pickValue(eu, [...ODD_FIELD_KEYS.away]),
    state,
  );
  setOddText(
    row,
    "[data-gwd-odds-bs-over]",
    pickValue(bs, [...ODD_FIELD_KEYS.over]),
    state,
  );
  setText(
    row,
    "[data-gwd-odds-bs-line]",
    pickValue(bs, [...ODD_FIELD_KEYS.line, "total"]),
  );
  setOddText(
    row,
    "[data-gwd-odds-bs-under]",
    pickValue(bs, [...ODD_FIELD_KEYS.under]),
    state,
  );
};

const updateBookmakerUi = (board: HTMLElement, state: State) => {
  const bookmaker =
    BOOKMAKERS[state.companyId] || BOOKMAKERS[DEFAULT_COMPANY_ID];
  board
    .querySelectorAll<HTMLElement>("[data-gwd-bookmaker-toggle]")
    .forEach((element) => {
      element.textContent = `${bookmaker.name} ▾`;
    });
  board
    .querySelectorAll<HTMLElement>("[data-gwd-bookmaker-menu]")
    .forEach((menu) => {
      const fragment = document.createDocumentFragment();
      configuredOddsCompanyIds(state).forEach((companyId) => {
        const item = BOOKMAKERS[companyId];
        if (!item) return;
        const button = cloneTemplateElement<HTMLButtonElement>(
          board,
          "[data-gwd-bookmaker-option-template]",
        );
        if (!button) return;
        button.dataset.gwdBookmakerId = item.id;
        button.className = item.id === state.companyId ? "is-active" : "";
        button.textContent = item.name;
        fragment.append(button);
      });
      menu.replaceChildren(fragment);
    });
};

const applySettings = (board: HTMLElement, state: State) => {
  const settings = state.settings;
  board.dataset.gwdShowHdp = String(settings.showHdp && settings.showOdds);
  board.dataset.gwdShowTx = String(settings.showTx && settings.showOdds);
  board.dataset.gwdShowEu = String(settings.showEu && settings.showOdds);
  board.dataset.gwdShowOdds = String(settings.showOdds);
  board.dataset.gwdHasOdds = String(
    settings.showOdds &&
      (settings.showHdp || settings.showEu || settings.showTx),
  );
  board.dataset.gwdShowBasic = String(settings.showBasic);
  board.dataset.gwdShowRanking = String(settings.showRanking);
  board.dataset.gwdShowCards = String(settings.showCards);
  board.dataset.gwdShowOddsChange = String(settings.showOddsChange);
  board.dataset.gwdShowHt = String(settings.showHt);
  board.dataset.gwdShowCorners = String(settings.showCorners);
  if (!settings.showPopup) {
    const panel = board.querySelector<HTMLElement>("[data-gwd-match-insight]");
    if (panel) panel.hidden = true;
  }

  board
    .querySelectorAll<HTMLInputElement>("[data-gwd-setting]")
    .forEach((input) => {
      const key = input.dataset.gwdSetting as keyof BoardSettings;
      input.checked = Boolean(settings[key]);
    });
  board
    .querySelectorAll<HTMLSelectElement>("[data-gwd-setting-select]")
    .forEach((select) => {
      const key = select.dataset.gwdSettingSelect as keyof BoardSettings;
      select.value = String(settings[key] || "");
    });
};

const syncFavoriteBadges = (board: HTMLElement, state: State) => {
  setBadge(board, "follow", state.favorites.size);
};

const competitionRowLabel = (competition: any): string =>
  competition?.code ||
  competition?.shortCode ||
  competition?.shortName ||
  competition?.name ||
  "-";

const competitionGroupLabel = (competition: any): string =>
  competition?.name ||
  competition?.shortName ||
  competition?.code ||
  competition?.shortCode ||
  "-";

const competitionLabelForKey = (
  state: State,
  competitionId: string,
): string => {
  const competition = state.entities.competitions[competitionId] || {};
  const relationLabel = competitionGroupLabel(competition);
  if (relationLabel !== "-") return relationLabel;
  return competitionId || "-";
};

const competitionLogo = (competition: any): string =>
  competition?.logo || competition?.icon || "";

const patchGroupRow = (
  row: HTMLElement,
  state: State,
  entry: RenderEntry,
) => {
  const competitionId =
    entry.groupId || entry.key.replace(/^group:/, "").split(":")[0] || "";
  row.dataset.gwdGroupKey = entry.key.replace(/^group:/, "");
  if (row.hidden) row.hidden = false;
  const competition = state.entities.competitions[competitionId] || {};
  let title =
    entry.title ||
    competitionLabelForKey(state, competitionId);
  if (!String(title || "").trim() || title === "-") {
    title = competitionId && competitionId !== "__unknown__" ? competitionId : "Khác";
  }
  const currentTitle =
    row.querySelector<HTMLElement>("[data-gwd-group-title]")?.textContent?.trim() ||
    "";
  if (
    currentTitle &&
    currentTitle !== "-" &&
    currentTitle !== competitionId &&
    (title === "-" || title === competitionId)
  ) {
    title = currentTitle;
  }
  const titleElement = row.querySelector<HTMLElement>("[data-gwd-group-title]");
  if (titleElement?.hidden) titleElement.hidden = false;
  setText(row, "[data-gwd-group-title]", title || "-");
  setVisibleText(row, "[data-gwd-group-round]", entry.round || "", Boolean(entry.round));
  setImage(row, "[data-gwd-group-logo]", competitionLogo(competition), title);
};

const matchCompetitionKey = (state: State, id: string): string => {
  const match = state.entities.matches[id] || {};
  return String(match.info?.competitionId || "__unknown__");
};

const teamDisplayName = (team: any): string =>
  String(team?.shortName || team?.name || "").trim();

const matchTeamNames = (
  state: State,
  id: string,
): { home: string; away: string } => {
  const match = state.entities.matches[id] || {};
  const info = match.info || {};
  const home =
    state.entities.teams[String(info.homeTeamId || "")] ||
    info.homeTeam ||
    match.homeTeam ||
    {};
  const away =
    state.entities.teams[String(info.awayTeamId || "")] ||
    info.awayTeam ||
    match.awayTeam ||
    {};

  return {
    home: teamDisplayName(home),
    away: teamDisplayName(away),
  };
};

const isRenderableMatch = (state: State, id: string): boolean => {
  const names = matchTeamNames(state, id);
  return Boolean(names.home && names.away);
};

const viewMatchIds = (state: State, view: View): string[] =>
  view.ids.filter((id) => {
    if (!isRenderableMatch(state, id)) return false;
    if (view.tab === "live" && state.recentFinishedIds.has(id)) return false;
    if (!matchPassesFixtureSeason(state, id)) return false;
    if (!matchPassesFixtureDate(state, id)) return false;
    if (view.tab === "odds" || state.applied.status === "odds")
      return hasOdds(state.entities.matches[id]);
    return true;
  });

const matchTimeValue = (state: State, id: string): number => {
  const value =
    state.entities.matches[id]?.info?.matchTime ||
    state.entities.matches[id]?.matchTime;
  const date = value ? new Date(value) : null;
  const time = date ? date.getTime() : NaN;
  return Number.isFinite(time) ? time : 0;
};

const compareTimeModeIds = (
  state: State,
  view: View,
  left: string,
  right: string,
): number => {
  const leftStatus = statusOf(state.entities.matches[left]);
  const rightStatus = statusOf(state.entities.matches[right]);
  const leftRank =
    TIME_MODE_STATUS_RANK[leftStatus] ?? TIME_MODE_STATUS_RANK.all;
  const rightRank =
    TIME_MODE_STATUS_RANK[rightStatus] ?? TIME_MODE_STATUS_RANK.all;
  if (leftRank !== rightRank) return leftRank - rightRank;

  if (state.settings.followOnTop && view.tab !== "follow") {
    const favoriteDiff =
      Number(state.favorites.has(right)) - Number(state.favorites.has(left));
    if (favoriteDiff !== 0) return favoriteDiff;
  }

  const timeDifference = view.tab === "finished" || isResultsBoard(state)
    ? matchTimeValue(state, right) - matchTimeValue(state, left)
    : matchTimeValue(state, left) - matchTimeValue(state, right);
  if (timeDifference !== 0) return timeDifference;
  return left.localeCompare(right);
};

const hotCompetitionRankMap = (state: State): Map<string, number> =>
  new Map(hotCompetitionIds(state).map((id, index) => [id, index]));

const compareLeagueModeIds = (
  state: State,
  view: View,
  left: string,
  right: string,
): number => {
  const ranks = hotCompetitionRankMap(state);
  const leftCompetition = matchCompetitionKey(state, left);
  const rightCompetition = matchCompetitionKey(state, right);
  const leftRank = ranks.has(leftCompetition)
    ? ranks.get(leftCompetition)!
    : Number.MAX_SAFE_INTEGER;
  const rightRank = ranks.has(rightCompetition)
    ? ranks.get(rightCompetition)!
    : Number.MAX_SAFE_INTEGER;
  if (leftRank !== rightRank) return leftRank - rightRank;

  if (
    state.settings.followOnTop &&
    view.tab !== "follow" &&
    leftCompetition === rightCompetition
  ) {
    const favoriteDiff =
      Number(state.favorites.has(right)) - Number(state.favorites.has(left));
    if (favoriteDiff !== 0) return favoriteDiff;
  }

  if (isResultsBoard(state)) {
    // The API already applies sortCompetition/priorityCompetitionIds. Preserve
    // that server order so hydration and later CSR reconciliation use the same
    // league sequence instead of re-sorting competition IDs lexically.
    if (leftCompetition !== rightCompetition)
      return view.ids.indexOf(left) - view.ids.indexOf(right);
    const timeDifference = matchTimeValue(state, right) - matchTimeValue(state, left);
    if (timeDifference !== 0) return timeDifference;
    return left.localeCompare(right);
  }

  return 0;
};

const isFixtureLeagueFiltered = (state: State): boolean =>
  isFixturesBoard(state) && state.applied.competitionIds.length > 0;

const fixtureSelectedCompetitionId = (state: State): string =>
  isFixtureLeagueFiltered(state) ? String(state.applied.competitionIds[0] || "") : "";

const fixtureCompetitionSeasons = (state: State, competitionId?: string): any[] => {
  const id = String(competitionId || fixtureSelectedCompetitionId(state) || "");
  if (!id) return [];
  const entitySeasons = Object.values(state.entities.seasons || {})
    .filter((season: any) => String(season?.competitionId || "") === id)
    .sort((left: any, right: any) => {
      const currentDiff = Number(right?.isCurrent || 0) - Number(left?.isCurrent || 0);
      if (currentDiff !== 0) return currentDiff;
      const rightStart = Number(right?.startTime || 0);
      const leftStart = Number(left?.startTime || 0);
      if (rightStart !== leftStart) return rightStart - leftStart;
      return String(right?.year || "").localeCompare(String(left?.year || ""));
    });
  if (entitySeasons.length) {
    state.fixtureSeasonsByCompetitionId[id] = [...entitySeasons];
    return entitySeasons;
  }
  return [...(state.fixtureSeasonsByCompetitionId[id] || [])];
};

const fixtureCurrentSeasonId = (state: State, competitionId?: string): string => {
  const id = String(competitionId || fixtureSelectedCompetitionId(state) || "");
  if (!id) return "";
  const competition = state.entities.competitions[id] || {};
  const curSeasonId = String(
    competition?.currentSeasonId ||
      competition?.curSeasonId ||
      competition?.currentSeason?.id ||
      competition?.currentSeason?.seasonId ||
      "",
  );
  if (curSeasonId) return curSeasonId;
  const currentSeason = fixtureCompetitionSeasons(state, id).find(
    (season: any) =>
      Number(season?.isCurrent || 0) === 1 ||
      season?.current === true ||
      season?.isCurrent === true,
  );
  return String(currentSeason?.id || "");
};

const fixtureActiveSeasonId = (state: State): string =>
  state.applied.seasonId || fixtureCurrentSeasonId(state);

const fixtureSeasonStateKey = (
  state: State,
  seasonId = fixtureActiveSeasonId(state),
  competitionId = fixtureSelectedCompetitionId(state),
): string => (competitionId && seasonId ? `${competitionId}:${seasonId}` : "");

const fixtureViewScopeKey = (state: State): string => {
  const competitionId = fixtureSelectedCompetitionId(state);
  if (competitionId) {
    if (isResultsBoard(state)) return `results:competition:${competitionId}`;
    const seasonId = fixtureActiveSeasonId(state) || "unresolved";
    return `competition:${competitionId}:${seasonId}`;
  }
  return `default:${state.groupMode}:${String(state.contract.fixtureDate || "window")}`;
};

const isFixtureSeasonFullyLoaded = (state: State): boolean => {
  const key = fixtureSeasonStateKey(state);
  return Boolean(key && state.loadedFixtureSeasonIds.has(key));
};

const isFixtureCurrentSeasonActive = (state: State): boolean => {
  const activeSeasonId = fixtureActiveSeasonId(state);
  const currentSeasonId = fixtureCurrentSeasonId(state);
  return Boolean(activeSeasonId && activeSeasonId === currentSeasonId);
};

const fixtureSeasonLabel = (season: any): string =>
  String(season?.year || season?.name || season?.shortName || season?.id || "");

const fixtureActiveSeasonLabel = (state: State): string => {
  const seasonId = fixtureActiveSeasonId(state);
  return seasonId ? fixtureSeasonLabel(state.entities.seasons?.[seasonId] || {}) : "";
};

const matchPassesFixtureSeason = (state: State, id: string): boolean => {
  if (!isFixtureLeagueFiltered(state)) return true;
  const seasonId = fixtureActiveSeasonId(state);
  if (!seasonId) return true;
  const match = state.entities.matches[id];
  const matchSeasonId = String(match?.info?.seasonId || match?.seasonId || "");
  return !matchSeasonId || matchSeasonId === seasonId;
};

const matchPassesFixtureDate = (state: State, id: string): boolean => {
  if (!isFixtureLeagueFiltered(state) || !state.applied.fixtureDate) return true;
  return matchDateKey(state, id) === state.applied.fixtureDate;
};

const fixtureDateLabel = (dateKey: string): string => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) return dateKey;
  const [year, month, day] = dateKey.split("-");
  return `${day}/${month}/${year}`;
};

const fixtureAvailableDates = (state: State): string[] => {
  if (!isFixtureLeagueFiltered(state)) return [];
  const competitionId = fixtureSelectedCompetitionId(state);
  const dates = new Set<string>();
  Object.keys(state.entities.matches || {}).forEach((id) => {
    const match = state.entities.matches[id];
    if (String(match?.info?.competitionId || "") !== competitionId) return;
    if (!matchPassesFixtureSeason(state, id)) return;
    const dateKey = matchDateKey(state, id);
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) dates.add(dateKey);
  });
  return [...dates].sort((left, right) => left.localeCompare(right));
};

const compareFixtureRoundIds = (
  state: State,
  left: string,
  right: string,
): number => {
  const leftMatch = state.entities.matches[left] || {};
  const rightMatch = state.entities.matches[right] || {};
  const leftMeta = fixtureRoundMeta(state, leftMatch);
  const rightMeta = fixtureRoundMeta(state, rightMatch);

  if (leftMeta.stageOrder !== rightMeta.stageOrder)
    return rightMeta.stageOrder - leftMeta.stageOrder;
  if (leftMeta.roundNum !== rightMeta.roundNum)
    return rightMeta.roundNum - leftMeta.roundNum;
  if (leftMeta.groupNum !== rightMeta.groupNum)
    return leftMeta.groupNum - rightMeta.groupNum;

  return matchTimeValue(state, left) - matchTimeValue(state, right);
};

const oddsCount = (state: State): number => {
  const ids = state.indexView?.ids?.length
    ? state.indexView.ids
    : Object.keys(state.entities.matches);
  return ids.filter(
    (id) => isRenderableMatch(state, id) && hasOdds(state.entities.matches[id]),
  ).length;
};

const isIndexCountReady = (state: State): boolean =>
  Boolean(state.indexView?.done);

const isOddsCountReady = (state: State): boolean =>
  isIndexCountReady(state) && state.oddsRequests.size === 0;

const syncOddsBadge = (board: HTMLElement, state: State) => {
  if (!isOddsCountReady(state)) {
    if (!state.countBadgesReady.odds) setBadgeLoading(board, "odds", true);
    return;
  }

  const total = oddsCount(state);
  state.totals.odds = total;
  state.countBadgesReady.odds = true;
  setBadge(board, "odds", total);
};

const displayIdsForView = (state: State, view: View): string[] => {
  const sourceIds = viewMatchIds(state, view);
  if (isFixtureLeagueFiltered(state)) {
    return [...sourceIds].sort((a, b) => compareFixtureRoundIds(state, a, b));
  }

  const ids =
    state.groupMode === "time"
      ? [...sourceIds].sort((a, b) => compareTimeModeIds(state, view, a, b))
      : [...sourceIds].sort((a, b) => compareLeagueModeIds(state, view, a, b));

  if (state.groupMode !== "league") return ids;

  const groups = new Map<string, string[]>();
  ids.forEach((id) => {
    const key = matchCompetitionKey(state, id);
    const group = groups.get(key) || [];
    group.push(id);
    groups.set(key, group);
  });

  return [...groups.values()].flat();
};

const updateGroupUi = (board: HTMLElement, state: State) => {
  board.dataset.gwdGroupMode = state.groupMode;
  board
    .querySelectorAll<HTMLElement>("[data-gwd-group-label]")
    .forEach((button) => {
      const nextText = isFixturesBoard(state)
        ? state.groupMode === "league"
          ? "Theo gi\u1ea3i"
          : "Theo th\u1eddi gian"
        : state.groupMode === "league"
          ? "THEO TH\u1edcI GIAN"
          : "THEO GI\u1ea2I \u0110\u1ea4U";
      if (button.textContent !== nextText) button.textContent = nextText;
    });
};

function patchVisibleOddsRows(board: HTMLElement, state: State, ids: string[]) {
  const idSet = new Set(ids);
  ids.forEach((id) => {
    if (!state.entities.matches[id]) idSet.delete(id);
  });
  if (!idSet.size) return;

  board.querySelectorAll<HTMLElement>("[data-gwd-match-id]").forEach((row) => {
    const id = String(row.dataset.gwdMatchId || "");
    if (idSet.has(id)) patchOdds(row, state, state.entities.matches[id]);
  });
}

const patchVisibleMatchClocks = (board: HTMLElement, state: State) => {
  if (isResultsBoard(state)) return;
  board.querySelectorAll<HTMLElement>("[data-gwd-match-id]").forEach((row) => {
    const id = String(row.dataset.gwdMatchId || "");
    const info = state.entities.matches[id]?.info;
    if (info) {
      setMatchClock(row, "[data-gwd-time]", info);
      setMobileMatchStatusClock(row, info);
    }
  });
};

const syncMinuteBlinkPhase = (board: HTMLElement) => {
  const now = Date.now();
  board.style.setProperty(
    "--gwd-minute-blink-delay",
    `${-((now % 1000) / 1000)}s`,
  );
};

const startMatchClock = (board: HTMLElement, state: State) => {
  // Results only contain FINISHED matches. Running the live clock formatter here
  // would alternate the result time between its scheduled time and the "FT" label.
  if (isResultsBoard(state)) return;

  let clockTimer = 0;
  let blinkTimer = 0;

  const syncBlink = () => {
    syncMinuteBlinkPhase(board);
    const nextDelay = 1000 - (Date.now() % 1000);
    blinkTimer = window.setTimeout(syncBlink, Math.max(50, nextDelay));
  };

  const tick = () => {
    patchVisibleMatchClocks(board, state);
    const nextDelay = MATCH_CLOCK_INTERVAL_MS - (Date.now() % MATCH_CLOCK_INTERVAL_MS);
    clockTimer = window.setTimeout(tick, Math.max(250, nextDelay));
  };

  syncBlink();
  clockTimer = window.setTimeout(tick, Math.max(250, MATCH_CLOCK_INTERVAL_MS - (Date.now() % MATCH_CLOCK_INTERVAL_MS)));
  window.addEventListener(
    "pagehide",
    () => {
      window.clearTimeout(clockTimer);
      window.clearTimeout(blinkTimer);
    },
    { once: true },
  );
};

const startResponsiveViewSync = (board: HTMLElement, state: State) => {
  if (isFixturesBoard(state)) return;
  const media = window.matchMedia(MOBILE_VIEW_QUERY);
  const sync = () => {
    const current = ensureView(state, state.active, true);
    if (media.matches) renderMobileView(board, state, current);
  };

  media.addEventListener("change", sync);
};

const patchMatchRow = (row: HTMLElement, state: State, id: string) => {
  const match = state.entities.matches[id] || {};
  const info = match.info || {};
  const home = state.entities.teams[String(info.homeTeamId || "")] || {};
  const away = state.entities.teams[String(info.awayTeamId || "")] || {};
  const competition =
    state.entities.competitions[String(info.competitionId || "")] || {};
  const { home: homeName, away: awayName } = matchTeamNames(state, id);
  const homePosition = positionLabel(info.homePosition);
  const awayPosition = positionLabel(info.awayPosition);
  const homeMeta = displayScoreMeta(info, "home");
  const awayMeta = displayScoreMeta(info, "away");
  const markers = winnerMarkers(info.homeScores, info.awayScores);
  const markerTips = winnerTooltips(info.homeScores, info.awayScores);

  row.dataset.gwdMatchId = id;
  const favorite = row.querySelector<HTMLButtonElement>("[data-gwd-favorite]");
  if (favorite) {
    const active = state.favorites.has(id);
    const nextText = active ? "★" : "☆";
    if (favorite.textContent !== nextText) favorite.textContent = nextText;
    favorite.classList.toggle("is-active", active);
    favorite.setAttribute("aria-pressed", String(active));
  }
  setText(row, "[data-gwd-league]", competitionRowLabel(competition));
  setLeagueColor(row, "[data-gwd-league]", competition.primaryColor);
  setText(row, "[data-gwd-date]", formatMatchDate(info.matchTime));
  setMatchClock(row, "[data-gwd-time]", info);
  setText(row, "[data-gwd-home]", homeName);
  setText(row, "[data-gwd-away]", awayName);
  setVisibleText(
    row,
    "[data-gwd-home-position]",
    homePosition,
    Boolean(homePosition),
  );
  setVisibleText(
    row,
    "[data-gwd-away-position]",
    awayPosition,
    Boolean(awayPosition),
  );
  setText(row, "[data-gwd-home-score]", homeMeta.goal);
  setText(row, "[data-gwd-away-score]", awayMeta.goal);
  row
    .querySelector<HTMLElement>(".gwd-livescore-board__score")
    ?.classList.toggle("is-vs", isNotStartedInfo(info));
  setText(row, "[data-gwd-half]", displayHalfScore(info));
  setText(row, "[data-gwd-corners]", displayCorners(info));
  setVisibleText(row, "[data-gwd-home-red]", homeMeta.red, homeMeta.red > 0);
  setVisibleText(
    row,
    "[data-gwd-home-yellow]",
    homeMeta.yellow,
    homeMeta.yellow > 0,
  );
  setVisibleText(row, "[data-gwd-away-red]", awayMeta.red, awayMeta.red > 0);
  setVisibleText(
    row,
    "[data-gwd-away-yellow]",
    awayMeta.yellow,
    awayMeta.yellow > 0,
  );
  setVisibleText(row, "[data-gwd-home-extra]", "*", markers.home);
  setVisibleText(row, "[data-gwd-away-extra]", "*", markers.away);
  setTooltip(row, "[data-gwd-home-extra]", markerTips.home);
  setTooltip(row, "[data-gwd-away-extra]", markerTips.away);
  setImage(row, "[data-gwd-home-logo]", home.logo, homeName);
  setImage(row, "[data-gwd-away-logo]", away.logo, awayName);
  if (isFixturesBoard(state)) {
    patchFixtureMatchRow(row, state, id, match, homeName, awayName);
    return;
  }
  patchFixtureWatch(row, state, match, id, homeName, awayName);
  patchOdds(row, state, match);
};

const patchMobileMatchCard = (card: HTMLElement, state: State, id: string) => {
  const match = state.entities.matches[id] || {};
  const info = match.info || {};
  const home = state.entities.teams[String(info.homeTeamId || "")] || {};
  const away = state.entities.teams[String(info.awayTeamId || "")] || {};
  const { home: homeName, away: awayName } = matchTeamNames(state, id);
  const homeMeta = displayScoreMeta(info, "home");
  const awayMeta = displayScoreMeta(info, "away");
  const homePosition = positionLabel(info.homePosition);
  const awayPosition = positionLabel(info.awayPosition);

  card.dataset.gwdMatchId = id;
  const favorite = card.querySelector<HTMLButtonElement>("[data-gwd-favorite]");
  if (favorite) {
    const active = state.favorites.has(id);
    const nextText = active ? "★" : "☆";
    if (favorite.textContent !== nextText) favorite.textContent = nextText;
    favorite.classList.toggle("is-active", active);
    favorite.setAttribute("aria-pressed", String(active));
  }

  setText(card, "[data-gwd-mobile-time]", formatTime(info.matchTime));
  setText(card, "[data-gwd-mobile-date]", formatMatchDate(info.matchTime));
  setMobileMatchStatusClock(card, info);
  setText(card, "[data-gwd-home]", homeName);
  setText(card, "[data-gwd-away]", awayName);
  setVisibleText(
    card,
    "[data-gwd-home-position]",
    homePosition,
    Boolean(homePosition),
  );
  setVisibleText(
    card,
    "[data-gwd-away-position]",
    awayPosition,
    Boolean(awayPosition),
  );
  setText(card, "[data-gwd-home-score]", homeMeta.goal);
  setText(card, "[data-gwd-away-score]", awayMeta.goal);
  card
    .querySelectorAll<HTMLElement>(".gwd-livescore-board__mobile-score")
    .forEach((score) => {
      score.classList.toggle("is-vs", isNotStartedInfo(info));
    });
  setText(card, "[data-gwd-mobile-home-half]", homeMeta.half);
  setText(card, "[data-gwd-mobile-away-half]", awayMeta.half);
  setText(
    card,
    "[data-gwd-mobile-home-corner]",
    homeMeta.corner >= 0 ? homeMeta.corner : "-",
  );
  setText(
    card,
    "[data-gwd-mobile-away-corner]",
    awayMeta.corner >= 0 ? awayMeta.corner : "-",
  );
  setVisibleText(card, "[data-gwd-home-red]", homeMeta.red, homeMeta.red > 0);
  setVisibleText(
    card,
    "[data-gwd-home-yellow]",
    homeMeta.yellow,
    homeMeta.yellow > 0,
  );
  setVisibleText(card, "[data-gwd-away-red]", awayMeta.red, awayMeta.red > 0);
  setVisibleText(
    card,
    "[data-gwd-away-yellow]",
    awayMeta.yellow,
    awayMeta.yellow > 0,
  );
  const extraResult = mobileExtraResultText(info, homeName, awayName);
  setVisibleText(
    card,
    "[data-gwd-mobile-extra-result]",
    extraResult,
    Boolean(extraResult),
  );
  setImage(card, "[data-gwd-home-logo]", home.logo, homeName);
  setImage(card, "[data-gwd-away-logo]", away.logo, awayName);
  patchFixtureWatch(card, state, match, id, homeName, awayName);
  patchOdds(card, state, match);
};

const desktopEntriesForView = (state: State, view: View): RenderEntry[] => {
  const entries: RenderEntry[] = [];
  let lastCompetitionKey = "";
  let lastDateKey = "";
  const dateOccurrences = new Map<string, number>();
  const fixtureMode = isFixturesBoard(state);

  displayIdsForView(state, view).forEach((id) => {
    const competitionKey = matchCompetitionKey(state, id);
    const roundGroup = isFixtureLeagueFiltered(state)
      ? fixtureRoundMeta(state, state.entities.matches[id] || {})
      : null;
    const displayGroupKey = roundGroup
      ? `round:${roundGroup.key}:${roundGroup.roundNum}:${roundGroup.groupNum}`
      : competitionKey;
    if (
      (fixtureMode || state.groupMode === "league") &&
      displayGroupKey !== lastCompetitionKey
    ) {
      const competitionTitle = competitionLabelForKey(
        state,
        String(competitionKey),
      );
      const groupKey = roundGroup
        ? `group:${displayGroupKey}`
        : fixtureMode && state.groupMode === "time"
          ? `group:${competitionKey}:${id}`
          : `group:${competitionKey}`;
      entries.push({
        type: "group",
        key: groupKey,
        groupId: String(competitionKey),
        title: roundGroup?.label ||
          (competitionTitle && competitionTitle !== "-"
            ? competitionTitle
            : String(competitionKey || "Khác")),
        round: fixtureRoundLabel(state.entities.matches[id] || {}),
      });
      lastCompetitionKey = displayGroupKey;
    }

    if (!fixtureMode && state.groupMode === "time") {
      const dateKey = matchDateKey(state, id);
      if (dateKey !== lastDateKey) {
        const occurrence = dateOccurrences.get(dateKey) || 0;
        dateOccurrences.set(dateKey, occurrence + 1);
        entries.push({
          type: "date",
          key: `date:${dateKey}:${occurrence}`,
          title: formatMatchDateTitle(
            state.entities.matches[id]?.info?.matchTime,
          ),
        });
        lastDateKey = dateKey;
      }
    }

    entries.push({ type: "match", key: `match:${id}`, id });
  });

  return entries;
};

const currentDesktopKeys = (list: HTMLElement): string[] =>
  [...list.children]
    .filter((child) => !child.matches("[data-gwd-livescore-ad]"))
    .map((child) => {
    const element = child as HTMLElement;
    if (element.matches("[data-gwd-date-row]"))
      return `date:${element.dataset.gwdDateKey || ""}`;
    if (element.matches("[data-gwd-group-row]"))
      return `group:${element.dataset.gwdGroupKey || ""}`;
    if (element.matches("[data-gwd-match-id]"))
      return `match:${element.dataset.gwdMatchId || ""}`;
    return "unknown";
  });

const sameKeys = (left: string[], right: string[]): boolean =>
  left.length === right.length &&
  left.every((value, index) => value === right[index]);

const patchDesktopInPlace = (
  list: HTMLElement,
  state: State,
  entries: RenderEntry[],
): boolean => {
  const nextKeys = entries.map((entry) => entry.key);
  if (!sameKeys(currentDesktopKeys(list), nextKeys)) return false;

  const renderedChildren = [...list.children].filter(
    (child) => !child.matches("[data-gwd-livescore-ad]"),
  );
  entries.forEach((entry, index) => {
    const element = renderedChildren[index] as HTMLElement | undefined;
    if (!element) return;

    if (entry.type === "group") {
      patchGroupRow(element, state, entry);
      return;
    }

    if (entry.type === "date") {
      setText(element, "[data-gwd-date-title]", entry.title || "-");
      return;
    }

    if (entry.id) patchMatchRow(element, state, entry.id);
  });

  return true;
};

const keyedDesktopNodes = (list: HTMLElement): Map<string, HTMLElement> => {
  const nodes = new Map<string, HTMLElement>();
  [...list.children].forEach((child) => {
    const element = child as HTMLElement;
    if (element.matches("[data-gwd-date-row]")) {
      nodes.set(`date:${element.dataset.gwdDateKey || ""}`, element);
      return;
    }
    if (element.matches("[data-gwd-group-row]")) {
      nodes.set(`group:${element.dataset.gwdGroupKey || ""}`, element);
      return;
    }
    if (element.matches("[data-gwd-match-id]")) {
      nodes.set(`match:${element.dataset.gwdMatchId || ""}`, element);
    }
  });
  return nodes;
};

const placeNodeAt = (parent: HTMLElement, node: HTMLElement, index: number) => {
  const renderedChildren = [...parent.children].filter(
    (child) => !child.matches("[data-gwd-livescore-ad]"),
  );
  if (renderedChildren[index] === node) return;
  parent.insertBefore(node, renderedChildren[index] || null);
};

const pruneEmptyGroupRows = (list: HTMLElement) => {
  const children = [...list.children] as HTMLElement[];
  children.forEach((child, index) => {
    if (
      !child.matches("[data-gwd-group-row]") &&
      !child.matches("[data-gwd-mobile-group-row]")
    )
      return;

    let hasMatch = false;
    for (let cursor = index + 1; cursor < children.length; cursor += 1) {
      const next = children[cursor];
      if (
        next.matches("[data-gwd-group-row]") ||
        next.matches("[data-gwd-mobile-group-row]") ||
        next.matches("[data-gwd-date-row]") ||
        next.matches("[data-gwd-mobile-date-row]")
      )
        break;
      if (next.matches("[data-gwd-match-id]")) {
        hasMatch = true;
        break;
      }
    }

    if (!hasMatch) child.remove();
  });
};

const renderDesktopView = (
  board: HTMLElement,
  state: State,
  view: View,
  reset = false,
  prune = false,
) => {
  const list = board.querySelector<HTMLElement>("[data-gwd-list]");
  const template = board.querySelector<HTMLTemplateElement>(
    "[data-gwd-match-template]",
  );
  const groupTemplate = board.querySelector<HTMLTemplateElement>(
    "[data-gwd-group-template]",
  );
  const dateTemplate = board.querySelector<HTMLTemplateElement>(
    "[data-gwd-date-template]",
  );
  if (!list || !template) return;

  const entries = desktopEntriesForView(state, view);

  if (!reset && !prune && patchDesktopInPlace(list, state, entries)) {
    syncInfeedAds(board);
    return;
  }

  if (reset) list.replaceChildren();

  if (prune) {
    const activeIds = new Set(viewMatchIds(state, view));
    list.querySelectorAll<HTMLElement>("[data-gwd-match-id]").forEach((row) => {
      if (!activeIds.has(String(row.dataset.gwdMatchId || ""))) row.remove();
    });
  }

  const existingNodes = keyedDesktopNodes(list);
  const nextKeys = new Set(entries.map((entry) => entry.key));

  entries.forEach((entry, index) => {
    let element = existingNodes.get(entry.key);

    if (entry.type === "group") {
      if (!element) {
        element = groupTemplate?.content.firstElementChild?.cloneNode(
          true,
        ) as HTMLElement | undefined;
        if (!element) return;
        element.dataset.gwdGroupKey = entry.key.replace(/^group:/, "");
      }
      patchGroupRow(element, state, entry);
      placeNodeAt(list, element, index);
      return;
    }

    if (entry.type === "date") {
      if (!element) {
        element = dateTemplate?.content.firstElementChild?.cloneNode(
          true,
        ) as HTMLElement | undefined;
        if (!element) return;
        element.dataset.gwdDateKey = entry.key.replace(/^date:/, "");
      }
      setText(element, "[data-gwd-date-title]", entry.title || "-");
      placeNodeAt(list, element, index);
      return;
    }

    if (!entry.id) return;
    if (!element) {
      element = template.content.firstElementChild?.cloneNode(true) as
        | HTMLElement
        | undefined;
      if (!element) return;
    }
    patchMatchRow(element, state, entry.id);
    placeNodeAt(list, element, index);
  });

  keyedDesktopNodes(list).forEach((node, key) => {
    if (!nextKeys.has(key)) node.remove();
  });
  if (isFixturesBoard(state)) pruneEmptyGroupRows(list);
  syncInfeedAds(board);
};

const mobileGroupTitle = (state: State, ids: string[], key: string): string => {
  if (isFixtureLeagueFiltered(state)) {
    return fixtureRoundMeta(state, state.entities.matches[ids[0]] || {}).label;
  }

  if (isFixturesBoard(state) || state.groupMode === "league") {
    return `${competitionLabelForKey(state, key)} (${ids.length})`;
  }

  const first = state.entities.matches[ids[0]]?.info?.matchTime;
  return formatMatchDateTitle(first);
};

const mobileEntriesForView = (state: State, view: View): RenderEntry[] => {
  const ids = displayIdsForView(state, view);
  const entries: RenderEntry[] = [];
  let lastKey = "";
  const dateOccurrences = new Map<string, number>();

  ids.forEach((id) => {
    const fixtureMode = isFixturesBoard(state);
    const roundGroup = isFixtureLeagueFiltered(state)
      ? fixtureRoundMeta(state, state.entities.matches[id] || {})
      : null;
    const key = roundGroup
      ? `round:${roundGroup.key}:${roundGroup.roundNum}:${roundGroup.groupNum}`
      : fixtureMode || state.groupMode === "league"
        ? matchCompetitionKey(state, id)
        : matchDateKey(state, id);
    if (key !== lastKey) {
      const type = fixtureMode || state.groupMode === "league" ? "group" : "date";
      const occurrence =
        type === "date" ? dateOccurrences.get(key) || 0 : undefined;
      if (occurrence !== undefined) dateOccurrences.set(key, occurrence + 1);
      entries.push({
        type,
        key: `${type}:${key}${occurrence === undefined ? "" : `:${occurrence}`}`,
        title: mobileGroupTitle(state, [id], key),
      });
      lastKey = key;
    }

    entries.push({ type: "match", key: `match:${id}`, id });
  });

  return entries;
};

const currentMobileKeys = (list: HTMLElement): string[] =>
  [...list.children].map((child) => {
    const element = child as HTMLElement;
    if (element.matches("[data-gwd-mobile-group-row]"))
      return `group:${element.dataset.gwdMobileGroupKey || ""}`;
    if (element.matches("[data-gwd-mobile-date-row]"))
      return `date:${element.dataset.gwdMobileDateKey || ""}`;
    if (element.matches("[data-gwd-match-id]"))
      return `match:${element.dataset.gwdMatchId || ""}`;
    return "unknown";
  });

const keyedMobileNodes = (list: HTMLElement): Map<string, HTMLElement> => {
  const nodes = new Map<string, HTMLElement>();
  [...list.children].forEach((child) => {
    const element = child as HTMLElement;
    if (element.matches("[data-gwd-mobile-group-row]")) {
      nodes.set(`group:${element.dataset.gwdMobileGroupKey || ""}`, element);
      return;
    }
    if (element.matches("[data-gwd-mobile-date-row]")) {
      nodes.set(`date:${element.dataset.gwdMobileDateKey || ""}`, element);
      return;
    }
    if (element.matches("[data-gwd-match-id]")) {
      nodes.set(`match:${element.dataset.gwdMatchId || ""}`, element);
    }
  });
  return nodes;
};

const patchMobileInPlace = (
  list: HTMLElement,
  state: State,
  entries: RenderEntry[],
): boolean => {
  const nextKeys = entries.map((entry) => entry.key);
  if (!sameKeys(currentMobileKeys(list), nextKeys)) return false;

  entries.forEach((entry, index) => {
    const element = list.children[index] as HTMLElement | undefined;
    if (!element) return;

    if (entry.type === "group") {
      setText(element, "[data-gwd-mobile-group-title]", entry.title || "-");
      return;
    }

    if (entry.type === "date") {
      setText(element, "[data-gwd-mobile-date-title]", entry.title || "-");
      return;
    }

    if (entry.id) patchMobileMatchCard(element, state, entry.id);
  });

  return true;
};

const renderMobileView = (board: HTMLElement, state: State, view: View) => {
  if (isFixturesBoard(state)) {
    const list = board.querySelector<HTMLElement>("[data-gwd-mobile-list]");
    if (list?.childElementCount) list.replaceChildren();
    return;
  }
  const list = board.querySelector<HTMLElement>("[data-gwd-mobile-list]");
  const cardTemplate = board.querySelector<HTMLTemplateElement>(
    "[data-gwd-mobile-card-template]",
  );
  const leagueTemplate = board.querySelector<HTMLTemplateElement>(
    "[data-gwd-mobile-group-template]",
  );
  const dateTemplate = board.querySelector<HTMLTemplateElement>(
    "[data-gwd-mobile-date-template]",
  );
  if (!list || !cardTemplate) return;
  if (!window.matchMedia(MOBILE_VIEW_QUERY).matches) return;

  const entries = mobileEntriesForView(state, view);
  if (patchMobileInPlace(list, state, entries)) return;

  const existingNodes = keyedMobileNodes(list);
  const nextKeys = new Set(entries.map((entry) => entry.key));

  entries.forEach((entry, index) => {
    let element = existingNodes.get(entry.key);

    if (entry.type === "group" || entry.type === "date") {
      const groupTemplate =
        entry.type === "group" ? leagueTemplate : dateTemplate;
      if (!element) {
        element = groupTemplate?.content.firstElementChild?.cloneNode(
          true,
        ) as HTMLElement | undefined;
        if (!element) return;
      }
      if (entry.type === "group") {
        element.dataset.gwdMobileGroupKey = entry.key.replace(/^group:/, "");
        setText(element, "[data-gwd-mobile-group-title]", entry.title || "-");
      } else {
        element.dataset.gwdMobileDateKey = entry.key.replace(/^date:/, "");
        setText(element, "[data-gwd-mobile-date-title]", entry.title || "-");
      }
      placeNodeAt(list, element, index);
      return;
    }

    if (!entry.id) return;
    if (!element) {
      element = cardTemplate.content.firstElementChild?.cloneNode(true) as
        | HTMLElement
        | undefined;
      if (!element) return;
    }
    patchMobileMatchCard(element, state, entry.id);
    placeNodeAt(list, element, index);
  });

  keyedMobileNodes(list).forEach((node, key) => {
    if (!nextKeys.has(key)) node.remove();
  });
  if (isFixturesBoard(state)) pruneEmptyGroupRows(list);
};

const mountView = (
  board: HTMLElement,
  state: State,
  view: View,
  reset = false,
  prune = false,
) => {
  setLoading(board, view.loading);
  removeSkeletons(board);
  renderDesktopView(board, state, view, reset, prune);
  renderMobileView(board, state, view);
  syncEmptyState(board, state, view);
};

const patchVisibleViewRows = (board: HTMLElement, state: State, view: View) => {
  const activeIds = new Set(view.ids);
  board
    .querySelectorAll<HTMLElement>("[data-gwd-list] [data-gwd-match-id]")
    .forEach((row) => {
      const id = String(row.dataset.gwdMatchId || "");
      if (activeIds.has(id) && state.entities.matches[id])
        patchMatchRow(row, state, id);
    });
  renderMobileView(board, state, view);
  syncEmptyState(board, state, view);
};

const patchVisibleMobileCards = (
  board: HTMLElement,
  state: State,
  ids: string[],
) => {
  if (!window.matchMedia(MOBILE_VIEW_QUERY).matches) return;
  const idSet = new Set(ids);
  board
    .querySelectorAll<HTMLElement>("[data-gwd-mobile-list] [data-gwd-match-id]")
    .forEach((card) => {
      const id = String(card.dataset.gwdMatchId || "");
      if (idSet.has(id) && state.entities.matches[id])
        patchMobileMatchCard(card, state, id);
    });
};

const patchSharedStoreChanges = (
  board: HTMLElement,
  state: State,
  ids: string[],
  oddsOnly = false,
) => {
  if (!ids.length) return;
  const visibleIds = new Set<string>();
  board.querySelectorAll<HTMLElement>("[data-gwd-match-id]").forEach((row) => {
    const id = String(row.dataset.gwdMatchId || "");
    if (id) visibleIds.add(id);
  });
  const changedVisibleIds = ids.filter((id) => visibleIds.has(id));
  if (!changedVisibleIds.length) return;

  if (oddsOnly) {
    patchVisibleOddsRows(board, state, changedVisibleIds);
  } else {
    board
      .querySelectorAll<HTMLElement>("[data-gwd-list] [data-gwd-match-id]")
      .forEach((row) => {
        const id = String(row.dataset.gwdMatchId || "");
        if (changedVisibleIds.includes(id) && state.entities.matches[id])
          patchMatchRow(row, state, id);
      });
  }

  const view = ensureView(state, state.active, true);
  patchVisibleMobileCards(board, state, changedVisibleIds);
  syncEmptyState(board, state, view);
  syncOddsBadge(board, state);
  updateFilterTabCounts(board, state);
};

const enqueueSharedStorePatch = (
  board: HTMLElement,
  state: State,
  ids: string[],
  oddsOnly = false,
) => {
  ids.forEach((id) => {
    if (!id) return;
    if (oddsOnly) state.patchQueue.oddsIds.add(id);
    else state.patchQueue.matchIds.add(id);
  });
  if (state.patchQueue.frame) return;

  state.patchQueue.frame = window.requestAnimationFrame(() => {
    state.patchQueue.frame = 0;
    const matchIds = [...state.patchQueue.matchIds];
    const oddsIds = [...state.patchQueue.oddsIds];
    state.patchQueue.matchIds.clear();
    state.patchQueue.oddsIds.clear();

    patchSharedStoreChanges(board, state, matchIds, false);
    patchSharedStoreChanges(board, state, oddsIds, true);
  });
};

const subscribeSharedStore = (board: HTMLElement, state: State) => {
  state.unsubscribeStore?.();
  const unsubscribeMatches = livescoreRuntime.subscribe(
    "matches:update",
    ({ changedIds = [], addedIds = [] }) => {
      enqueueSharedStorePatch(board, state, [...changedIds, ...addedIds], false);
    },
  );
  const unsubscribeOdds = livescoreRuntime.subscribe(
    "odds:update",
    ({ changedIds = [] }) => {
      enqueueSharedStorePatch(board, state, changedIds, true);
    },
  );
  const unsubscribeRelations = livescoreRuntime.subscribe(
    "relations:update",
    () => {
      const view = ensureView(state, state.active, true);
      patchVisibleViewRows(board, state, view);
    },
  );
  state.unsubscribeStore = () => {
    unsubscribeMatches();
    unsubscribeOdds();
    unsubscribeRelations();
  };
};

const REVIVE_ID = "1718ffff6aff14155bf9e84ffb3a29ee";

type ReviveWindow = Window & {
  reviveAsync?: Record<
    string,
    { dispatchEvent?: (name: string, element: HTMLElement) => void }
  >;
};

const infeedZones = (board: HTMLElement): string[] =>
  (board.dataset.bannerZones ?? "")
    .split(",")
    .map((zone) => zone.trim())
    .filter(Boolean);

const infeedGroupZones = (zones: string[], group: number): string[] => [
  zones[(group * 2) % zones.length],
  zones[(group * 2 + 1) % zones.length],
];

const patchInfeedZones = (
  ad: HTMLElement,
  zones: string[],
  group: number,
): boolean => {
  const nextZones = infeedGroupZones(zones, group);
  const signature = nextZones.join(",");
  if (ad.dataset.bannerZones === signature) return false;

  ad.dataset.bannerZones = signature;
  ad.replaceChildren(
    ...nextZones.map((zone) => {
      const element = document.createElement("ins");
      element.dataset.z = zone;
      element.dataset.reviveId = REVIVE_ID;
      return element;
    }),
  );
  return true;
};

const refreshInfeedAds = (ads: HTMLElement[]): void => {
  window.setTimeout(() => {
    const revive = (window as ReviveWindow).reviveAsync?.[REVIVE_ID];
    if (!revive?.dispatchEvent) return;

    ads
      .flatMap((ad) =>
        Array.from(ad.querySelectorAll<HTMLElement>("ins[data-revive-id]")),
      )
      .forEach((element, index) => {
        window.setTimeout(
          () => revive.dispatchEvent?.("refresh", element),
          index * 100,
        );
      });
  }, 100);
};

const syncInfeedAds = (board: HTMLElement): void => {
  const list = board.querySelector<HTMLElement>("[data-gwd-list]");
  const zones = infeedZones(board);
  if (!list) return;

  const existingAds = new Map<number, HTMLElement>();
  list
    .querySelectorAll<HTMLElement>(":scope > [data-gwd-livescore-ad]")
    .forEach((ad) => {
      const group = Number(ad.dataset.gwdLivescoreAd);
      if (Number.isInteger(group) && !existingAds.has(group)) {
        existingAds.set(group, ad);
      } else {
        ad.remove();
      }
    });

  if (!zones.length) {
    existingAds.forEach((ad) => ad.remove());
    return;
  }

  const matches = Array.from(
    list.querySelectorAll<HTMLElement>(
      ":scope > [data-gwd-match-id]:not([data-gwd-skeleton])",
    ),
  );
  const refreshed: HTMLElement[] = [];
  const activeGroups = new Set<number>();

  matches.forEach((match, index) => {
    if ((index + 1) % 6 !== 0) return;

    const group = Math.floor(index / 6);
    const existing = existingAds.get(group);
    const ad = existing ?? document.createElement("div");
    if (!existing) {
      ad.className = "gwd-livescore-infeed-ad";
      ad.dataset.gwdLivescoreAd = String(group);
      ad.setAttribute("aria-label", "Quảng cáo");
    }
    const zonesChanged = patchInfeedZones(ad, zones, group);

    activeGroups.add(group);
    if (match.nextElementSibling !== ad) match.insertAdjacentElement("afterend", ad);
    if (!existing || zonesChanged) refreshed.push(ad);
    });

  existingAds.forEach((ad, group) => {
    if (!activeGroups.has(group)) ad.remove();
  });
  if (refreshed.length) refreshInfeedAds(refreshed);
};

const reconcileVisibleViewRows = (
  board: HTMLElement,
  state: State,
  view: View,
) => {
  renderDesktopView(board, state, view);
  renderMobileView(board, state, view);
  syncEmptyState(board, state, view);
};

const setEmptyState = (board: HTMLElement, visible: boolean) => {
  const empty = board.querySelector<HTMLElement>("[data-gwd-empty]");
  if (!empty) return;
  const nextHidden = !visible;
  if (empty.hidden !== nextHidden) empty.hidden = nextHidden;
};

const syncEmptyState = (board: HTMLElement, state: State, view: View) => {
  if (!canPaint(state, view)) return;
  const hasMatches = viewMatchIds(state, view).length > 0;
  const hasSettled = view.done || view.page > 0;
  const showEmpty = hasSettled && !view.loading && !hasMatches;
  setEmptyState(board, showEmpty);
  if (isFixturesBoard(state) && showEmpty) {
    board.querySelectorAll<HTMLElement>(
      "[data-gwd-fixtures-date-wrap], [data-gwd-fixtures-date-select-wrap], [data-gwd-fixtures-season-wrap]",
    ).forEach((control) => {
      control.hidden = true;
    });
  }
};

const finalizeEmptyFixturesView = (
  board: HTMLElement,
  state: State,
  view: View,
) => {
  if (
    !isFixturesBoard(state) ||
    !canPaint(state, view) ||
    !view.done ||
    view.loading ||
    viewMatchIds(state, view).length > 0
  )
    return;

  board.querySelector<HTMLElement>("[data-gwd-list]")?.replaceChildren();
  board.querySelector<HTMLElement>("[data-gwd-mobile-list]")?.replaceChildren();
  board.querySelectorAll<HTMLElement>(
    "[data-gwd-fixtures-date-wrap], [data-gwd-fixtures-date-select-wrap], [data-gwd-fixtures-season-wrap]",
  ).forEach((control) => {
    control.hidden = true;
  });
  setLoading(board, false);
  setEmptyState(board, true);
};

const shouldFallbackLiveToTotal = (state: State, view: View): boolean =>
  !isFixturesBoard(state) &&
  !state.liveFallbackChecked &&
  view.tab === "live" &&
  state.active === "live" &&
  view.done &&
  displayIdsForView(state, view).length === 0 &&
  state.applied.competitionIds.length === 0 &&
  state.applied.countryIds.length === 0 &&
  state.applied.status === "all";

const setLoading = (board: HTMLElement, visible: boolean) => {
  const loading = board.querySelector<HTMLElement>("[data-gwd-loading]");
  if (!loading) return;
  const nextHidden = !visible;
  if (loading.hidden !== nextHidden) loading.hidden = nextHidden;
};

const removeSkeletons = (root: ParentNode) => {
  root
    .querySelectorAll<HTMLElement>("[data-gwd-skeleton]")
    .forEach((element) => element.remove());
};

const hasRenderedRows = (board: HTMLElement): boolean =>
  Boolean(
    board.querySelector(
      "[data-gwd-list] [data-gwd-match-id]:not([data-gwd-skeleton])",
    ),
  );

const renderMatchSkeletons = (board: HTMLElement, count = 8, force = false) => {
  const list = board.querySelector<HTMLElement>("[data-gwd-list]");
  const template = board.querySelector<HTMLTemplateElement>(
    "[data-gwd-match-skeleton-template]",
  );
  if (!list || !template || list.querySelector("[data-gwd-skeleton]")) return;
  if (!force && hasRenderedRows(board)) return;
  setEmptyState(board, false);

  const fragment = document.createDocumentFragment();
  for (let index = 0; index < count; index += 1) {
    const row = template.content.firstElementChild?.cloneNode(true);
    if (row) fragment.append(row);
  }
  list.replaceChildren(fragment);
};

const prepareMatchListTransition = (board: HTMLElement) => {
  setLoading(board, true);
  setEmptyState(board, false);
  renderMatchSkeletons(board, 8, true);
  const mobileList = board.querySelector<HTMLElement>("[data-gwd-mobile-list]");
  if (mobileList?.childElementCount) mobileList.replaceChildren();
};

const renderFilterSkeletons = (board: HTMLElement, count = 10) => {
  const items = board.querySelector<HTMLElement>("[data-gwd-filter-items]");
  const template = board.querySelector<HTMLTemplateElement>(
    "[data-gwd-filter-skeleton-template]",
  );
  if (!items || !template) return;

  const fragment = document.createDocumentFragment();
  for (let index = 0; index < count; index += 1) {
    const item = template.content.firstElementChild?.cloneNode(true);
    if (item) fragment.append(item);
  }
  items.replaceChildren(fragment);
};

const updateTabUi = (board: HTMLElement, state: State) => {
  board.querySelectorAll<HTMLElement>("[data-gwd-tab]").forEach((button) => {
    const tab = button.dataset.gwdTab;
    const filterActive =
      tab === state.applied.mode &&
      (state.applied.competitionIds.length > 0 ||
        state.applied.countryIds.length > 0 ||
        Boolean(statusForFilter(state.applied.status)) ||
        state.applied.status === "odds");
    button.classList.toggle("is-active", tab === state.active || filterActive);
    button.classList.toggle("is-filter-open", tab === state.draft.mode);
  });
};

const activateTabButtonImmediately = (
  board: HTMLElement,
  activeTab: string,
) => {
  board.querySelectorAll<HTMLElement>("[data-gwd-tab]").forEach((button) => {
    const tab = button.dataset.gwdTab;
    button.classList.toggle("is-active", tab === activeTab);
    button.classList.remove("is-filter-open");
  });
};

const setBadge = (board: HTMLElement, tab: MatchTab, value: number) => {
  const badge = board.querySelector<HTMLElement>(
    `[data-gwd-tab-count="${tab}"]`,
  );
  if (badge) {
    badge.classList.remove("is-loading");
    const next = String(value);
    if (badge.textContent !== next) badge.textContent = next;
  }
};

const syncViewBadge = (board: HTMLElement, state: State, view: View) => {
  if (view.tab === "odds") {
    syncOddsBadge(board, state);
    return;
  }
  if (state.applied.status === "odds" && view.tab === state.active) {
    if (!isOddsCountReady(state)) {
      if (!state.countBadgesReady.odds) setBadgeLoading(board, view.tab, true);
      return;
    }
    setBadge(board, view.tab, oddsCount(state));
    return;
  }
  if (!Number.isFinite(view.total) && view.page === 0) return;
  const total = isFixtureLeagueFiltered(state)
    ? displayIdsForView(state, view).length
    : Number.isFinite(view.total)
      ? view.total
      : view.ids.length;
  state.totals[view.tab] = total;
  setBadge(board, view.tab, total);
};

const setAnyBadge = (board: HTMLElement, key: string, value: number) => {
  const badge = board.querySelector<HTMLElement>(
    `[data-gwd-tab-count="${key}"]`,
  );
  if (badge) {
    badge.classList.remove("is-loading");
    const next = String(value);
    if (badge.textContent !== next) badge.textContent = next;
  }
};

const setBadgeLoading = (board: HTMLElement, key: string, loading: boolean) => {
  const badge = board.querySelector<HTMLElement>(
    `[data-gwd-tab-count="${key}"]`,
  );
  if (!badge) return;
  badge.classList.toggle("is-loading", loading);
  if (loading && !badge.textContent?.trim()) badge.textContent = " ";
};

const deferUntilAfterTabPaint = (callback: () => void) => {
  window.requestAnimationFrame(() => {
    window.setTimeout(callback, 0);
  });
};

const waitForDomPaint = (): Promise<void> =>
  new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });

const canPaint = (state: State, view: View): boolean =>
  view.paint &&
  state.active === view.tab &&
  state.groupMode === view.groupMode &&
  state.views[view.tab] === view;

const isTimeTotalSegmentedView = (state: State, view: View): boolean =>
  view.tab === "total" &&
  (state.applied.status === "all" || state.applied.status === "odds") &&
  !isResultsBoard(state);

const totalStatusSegments = (state: State) =>
  isFixturesBoard(state)
    ? FIXTURES_TOTAL_STATUS_SEGMENTS
    : TIME_TOTAL_STATUS_SEGMENTS;

const cloneSegmentView = (view: View, statuses: readonly string[]): View => ({
  ...view,
  query: {
    ...view.query,
    statuses: [...statuses],
  },
  ids: [],
  page: 0,
  total: Infinity,
  loading: false,
  done: false,
  paint: false,
});

const syncSegmentedTotalView = (view: View, segments: View[]) => {
  const ids: string[] = [];
  const seen = new Set<string>();
  segments.forEach((segment) => {
    segment.ids.forEach((id) => {
      if (seen.has(id)) return;
      seen.add(id);
      ids.push(id);
    });
  });

  const knownTotals = segments.filter((segment) =>
    Number.isFinite(segment.total),
  );
  view.ids = ids;
  view.page = Math.max(0, ...segments.map((segment) => segment.page));
  if (knownTotals.length === segments.length) {
    view.total = knownTotals.reduce((sum, segment) => sum + segment.total, 0);
  }
  view.done = segments.every((segment) => segment.done);
};

const paintSegmentedTimeTotalView = (
  board: HTMLElement,
  state: State,
  view: View,
  backgroundOnly: boolean,
  page: number,
) => {
  syncFavoriteBadges(board, state);
  if (Number.isFinite(view.total)) syncViewBadge(board, state, view);
  updateFilterTabCounts(board, state);

  if (!backgroundOnly && canPaint(state, view)) {
    const list = board.querySelector<HTMLElement>("[data-gwd-list]");
    if (list) removeSkeletons(list);
    if (page === 1 && hasRenderedRows(board))
      reconcileVisibleViewRows(board, state, view);
    else mountView(board, state, view);
  }
};

const loadSegmentedTimeTotalFull = async (
  board: HTMLElement,
  state: State,
  view: View,
  paint: boolean,
  backgroundOnly = false,
  showSkeleton = false,
) => {
  if (paint) view.paint = true;

  if (view.loading || view.done) {
    if (!backgroundOnly && canPaint(state, view)) {
      syncViewBadge(board, state, view);
      mountView(board, state, view);
      loadOddsForLoadedPages(board, state, view);
    }
    return;
  }

  view.loading = true;
  setLoading(board, !backgroundOnly && canPaint(state, view));
  if (!backgroundOnly && canPaint(state, view)) setEmptyState(board, false);
  if (
    backgroundOnly &&
    view === state.indexView &&
    view.ids.length === 0 &&
    !state.countBadgesReady.filters
  ) {
    setBadgeLoading(board, "leagues", true);
    setBadgeLoading(board, "countries", true);
  }
  if (
    showSkeleton &&
    !backgroundOnly &&
    canPaint(state, view) &&
    view.ids.length === 0
  ) {
    renderMatchSkeletons(board);
  }

  const preserveRenderedLayout =
    !backgroundOnly && view.ids.length > 0 && hasRenderedRows(board);

  const segments = totalStatusSegments(state).map((segment) =>
    cloneSegmentView(view, segment.statuses),
  );

  try {
    for (const segment of segments) {
      try {
        while (!segment.done) {
          const page = segment.page + 1;
          const payload = await api(state.contract.endpoint, {
            ...segment.query,
            page,
          }, fixtureRequestSignal(state));
          mergePayload(state, segment, payload);
          if (isFixturesBoard(state)) syncFixtureDateUi(board, state);
          if (!isFixturesBoard(state)) void loadOddsPage(board, state, segment, page);
          segment.page = page;
          if (!preserveRenderedLayout) {
            syncSegmentedTotalView(view, segments);
            paintSegmentedTimeTotalView(board, state, view, backgroundOnly, page);
          }
          if (isFixturesBoard(state)) await waitForDomPaint();
          else
            await new Promise<void>((resolve) =>
              requestAnimationFrame(() => resolve()),
            );
        }
      } catch (error) {
        if (isAbortError(error)) return;
        segment.total = segment.ids.length;
        segment.done = true;
        console.warn(
          "GWD Livescore segmented status request failed",
          segment.query,
          error,
        );
        if (!preserveRenderedLayout) syncSegmentedTotalView(view, segments);
      }
    }
  } catch (error) {
    console.warn("GWD Livescore segmented total request failed", error);
  } finally {
    syncSegmentedTotalView(view, segments);
    view.loading = false;
    if (
      isFixturesBoard(state) &&
      view !== state.indexView &&
      state.views[view.tab] !== view
    ) return;
    if (backgroundOnly && view === state.indexView && view.done) {
      pruneFavorites(state);
      syncFavoriteBadges(board, state);
      syncFixtureLeagueChips(board, state);
      syncFixtureDateUi(board, state);
      syncFixtureSeasonUi(board, state);
      syncFixtureLeagueHeading(board, state);
      syncBackgroundStateToActiveView(board, state);
    }
    if (isFixturesBoard(state) && view.done) {
      syncFixtureLeagueChips(board, state);
      syncFixtureDateUi(board, state);
      syncFixtureSeasonUi(board, state);
      syncFixtureLeagueHeading(board, state);
    }
    if (!backgroundOnly) setLoading(board, false);
    updateFilterTabCounts(board, state);
    renderFilterPopover(board, state);
    if (!backgroundOnly && canPaint(state, view)) {
      if (preserveRenderedLayout) reconcileVisibleViewRows(board, state, view);
      syncEmptyState(board, state, view);
      finalizeEmptyFixturesView(board, state, view);
    }
  }
};

const loadFull = async (
  board: HTMLElement,
  state: State,
  view: View,
  paint: boolean,
  backgroundOnly = false,
  showSkeleton = false,
  forceSingleAll = false,
) => {
  if (!forceSingleAll && isTimeTotalSegmentedView(state, view)) {
    await loadSegmentedTimeTotalFull(
      board,
      state,
      view,
      paint,
      backgroundOnly,
      showSkeleton,
    );
    return;
  }

  if (paint) view.paint = true;
  if (view.tab === "follow") {
    const followView = ensureFollowView(state);
    if (!backgroundOnly && canPaint(state, followView))
      mountView(board, state, followView, false, true);
    syncFavoriteBadges(board, state);
    return;
  }

  if (view.loading || view.done) {
    if (!backgroundOnly && canPaint(state, view)) {
      syncViewBadge(board, state, view);
      mountView(board, state, view);
      loadOddsForLoadedPages(board, state, view);
    }
    return;
  }

  view.loading = true;
  setLoading(board, !backgroundOnly && canPaint(state, view));
  if (!backgroundOnly && canPaint(state, view)) setEmptyState(board, false);
  if (
    backgroundOnly &&
    view === state.indexView &&
    view.ids.length === 0 &&
    !state.countBadgesReady.filters
  ) {
    setBadgeLoading(board, "leagues", true);
    setBadgeLoading(board, "countries", true);
  }
  if (
    showSkeleton &&
    !backgroundOnly &&
    canPaint(state, view) &&
    view.ids.length === 0
  ) {
    renderMatchSkeletons(board);
  }

  let requestFailed = false;
  try {
    while (!view.done) {
      const page = view.page + 1;
      const requestQuery = {
        ...view.query,
        page,
      };
      const payload = await api(
        state.contract.endpoint,
        requestQuery,
        fixtureRequestSignal(state),
      );
      logResultsApiData(state, "client-fetch", payload, requestQuery);
      mergePayload(state, view, payload);
      if (
        isFixtureLeagueFiltered(state) &&
        !view.query.seasonId &&
        !state.applied.seasonId
      ) {
        const currentSeasonId = fixtureCurrentSeasonId(state);
        if (currentSeasonId) {
          state.applied.seasonId = currentSeasonId;
          view.query = buildQuery(state, view.tab, true);
          view.ids = [];
          view.page = 0;
          view.total = Infinity;
          view.done = false;
          syncFixtureSeasonUi(board, state);
          syncFixtureLeagueHeading(board, state);
          syncFixtureDateUi(board, state);
          continue;
        }
      }
      syncFavoriteBadges(board, state);
      if (isFixturesBoard(state)) syncFixtureDateUi(board, state);
      if (!isFixturesBoard(state)) void loadOddsPage(board, state, view, page);
      view.page = page;
      if (state.groupMode === view.groupMode && state.views[view.tab] === view) {
        syncViewBadge(board, state, view);
      }
      updateFilterTabCounts(board, state);

      if (!backgroundOnly && canPaint(state, view)) {
        const list = board.querySelector<HTMLElement>("[data-gwd-list]");
        if (list) removeSkeletons(list);
        if (isFixturesBoard(state)) {
          // Publish every completed page/group before requesting the next page.
          reconcileVisibleViewRows(board, state, view);
        } else if (
          page === 1 &&
          hasRenderedRows(board) &&
          state.groupMode !== "league" &&
          !isFixturesBoard(state)
        )
          patchVisibleViewRows(board, state, view);
        else if (page === 1 && hasRenderedRows(board))
          reconcileVisibleViewRows(board, state, view);
        else mountView(board, state, view);
      }

      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => resolve()),
      );
    }
  } catch (error) {
    if (isAbortError(error)) return;
    requestFailed = true;
    console.warn("GWD Livescore match request failed", error);
  } finally {
    if (requestFailed) {
      view.total = view.ids.length;
      view.done = true;
    }
    view.loading = false;
    if (
      isFixturesBoard(state) &&
      view !== state.indexView &&
      state.views[view.tab] !== view
    ) return;
    if (backgroundOnly && view === state.indexView && view.done) {
      pruneFavorites(state);
      syncFavoriteBadges(board, state);
      syncFixtureLeagueChips(board, state);
      syncFixtureDateUi(board, state);
      syncFixtureSeasonUi(board, state);
      syncFixtureLeagueHeading(board, state);
      syncBackgroundStateToActiveView(board, state);
    }
    if (isFixturesBoard(state) && view.done) {
      syncFixtureLeagueChips(board, state);
      syncFixtureDateUi(board, state);
      syncFixtureSeasonUi(board, state);
      syncFixtureLeagueHeading(board, state);
    }
    if (!backgroundOnly) setLoading(board, false);
    updateFilterTabCounts(board, state);
    renderFilterPopover(board, state);
    if (!backgroundOnly && canPaint(state, view)) {
      syncEmptyState(board, state, view);
      finalizeEmptyFixturesView(board, state, view);
    }
  }
};

const refreshFixtureLeagueLive = async (board: HTMLElement, state: State) => {
  if (isResultsBoard(state) || !isFixtureLeagueFiltered(state) || state.polling) return;

  const current = ensureView(state, "total", true);
  const liveView: View = {
    ...current,
    query: {
      ...buildQuery(state, "total", true),
      statuses: ["LIVE"],
    },
    ids: [],
    page: 0,
    total: Infinity,
    loading: false,
    done: false,
    paint: false,
  };

  state.polling = true;
  try {
    while (!liveView.done) {
      const page = liveView.page + 1;
      const payload = await api(
        state.contract.endpoint,
        { ...liveView.query, page },
        fixtureRequestSignal(state),
      );
      mergePayload(state, liveView, payload);
      liveView.page = page;
      liveView.ids.forEach((id) => {
        if (!current.ids.includes(id)) current.ids.push(id);
      });
      if (canPaint(state, current)) reconcileVisibleViewRows(board, state, current);
      if (isFixturesBoard(state)) await waitForDomPaint();
      else
        await new Promise<void>((resolve) =>
          requestAnimationFrame(() => resolve()),
        );
    }
    state.lastActiveSyncAt = Date.now();
  } catch (error) {
    if (!isAbortError(error))
      console.warn("GWD Fixtures live refresh failed", error);
  } finally {
    state.polling = false;
  }
};

const mergeInfoSnapshotIds = (
  state: State,
  target: View,
  snapshotIds: string[],
): void => {
  snapshotIds.forEach((id) => {
    if (!target.ids.includes(id)) target.ids.push(id);
  });
};

const refreshInfoSnapshots = async (board: HTMLElement, state: State) => {
  if (state.polling || document.hidden || !navigator.onLine) return;

  if (
    !isResultsBoard(state) &&
    isFixtureLeagueFiltered(state) &&
    isFixtureSeasonFullyLoaded(state) &&
    isFixtureCurrentSeasonActive(state)
  ) {
    await refreshFixtureLeagueLive(board, state);
    return;
  }
  if (isFixtureLeagueFiltered(state) && !isResultsBoard(state)) return;

  const current = ensureView(state, state.active, true);
  const indexView = ensureIndexView(state);
  const baseQuery = buildIndexQuery(state);
  const statuses = isResultsBoard(state)
    ? (["FINISHED"] as const)
    : (["LIVE", "FINISHED"] as const);

  state.polling = true;
  try {
    const snapshots: View[] = [];
    for (const status of statuses) {
        const finishedWindow =
          status === "FINISHED"
            ? {
                fromDate: toGmt7Iso(
                  new Date(Date.now() - FINISHED_POLL_WINDOW_MS),
                ),
                toDate: toGmt7Iso(new Date()),
              }
            : {};
        const snapshot: View = {
          ...indexView,
          query: {
            ...baseQuery,
            ...finishedWindow,
            statuses: [status],
            page: 1,
          },
          ids: [],
          page: 0,
          total: Infinity,
          loading: false,
          done: false,
          paint: false,
        };
        const payload = await api(
          state.contract.endpoint,
          snapshot.query,
          fixtureRequestSignal(state),
        );
        mergePayload(state, snapshot, payload);
        snapshot.page = 1;
        snapshots.push(snapshot);
    }

    const snapshotIds = Array.from(
      new Set(snapshots.flatMap((snapshot) => snapshot.ids)),
    );
    const finishedIds = new Set(
      snapshots
        .filter(
          (snapshot) => normalizeArray(snapshot.query.statuses)[0] === "FINISHED",
        )
        .flatMap((snapshot) => snapshot.ids),
    );
    state.recentFinishedIds = finishedIds;

    mergeInfoSnapshotIds(state, indexView, snapshotIds);
    mergeInfoSnapshotIds(state, current, snapshotIds);
    current.ids = current.ids.filter((id) =>
      matchPassesTab(state, id, current.tab),
    );

    if (shouldUseLocalFilteredView(state)) {
      syncLocalFilteredActiveView(board, state);
    } else if (canPaint(state, current)) {
      reconcileVisibleViewRows(board, state, current);
    }

    syncFavoriteBadges(board, state);
    updateFilterTabCounts(board, state);
    loadOddsForLoadedPages(board, state, current);
    state.lastActiveSyncAt = Date.now();
  } catch (error) {
    if (!isAbortError(error))
      console.warn("GWD Livescore info snapshot refresh failed", error);
  } finally {
    state.polling = false;
    renderFilterPopover(board, state);
  }
};

const refreshActiveTab = async (board: HTMLElement, state: State) => {
  if (state.polling || document.hidden || !navigator.onLine) return;
  if (
    !isResultsBoard(state) &&
    isFixtureLeagueFiltered(state) &&
    isFixtureSeasonFullyLoaded(state) &&
    isFixtureCurrentSeasonActive(state)
  ) {
    await refreshFixtureLeagueLive(board, state);
    return;
  }
  if (isFixtureLeagueFiltered(state)) return;

  const requestedTab = state.active;
  const view = ensureView(state, requestedTab, true);
  if (view.tab === "follow") {
    if (view.loading) return;
    state.polling = true;
    try {
      const indexView = ensureIndexView(state);
      const freshIndex: View = {
        ...indexView,
        ids: [],
        page: 0,
        total: Infinity,
        loading: false,
        done: false,
      };

      while (!freshIndex.done) {
        const page = freshIndex.page + 1;
        const payload = await api(state.contract.endpoint, {
          ...freshIndex.query,
          page,
        }, fixtureRequestSignal(state));
        mergePayload(state, freshIndex, payload);
        freshIndex.page = page;
        await new Promise<void>((resolve) =>
          requestAnimationFrame(() => resolve()),
        );
      }

      state.indexViewsByGroupMode[freshIndex.groupMode] = freshIndex;
      if (state.groupMode === freshIndex.groupMode) state.indexView = freshIndex;
      state.views.follow = undefined;
      const followView = ensureFollowView(state);
      reconcileVisibleViewRows(board, state, followView);
      updateFilterTabCounts(board, state);
      state.lastActiveSyncAt = Date.now();
    } catch (error) {
      console.warn("GWD Livescore follow tab refresh failed", error);
    } finally {
      state.polling = false;
    }
    syncFavoriteBadges(board, state);
    return;
  }
  if (view.loading) return;

  if (shouldUseLocalFilteredView(state)) {
    state.polling = true;
    try {
      const indexView = ensureIndexView(state);
      const freshIndex: View = {
        ...indexView,
        ids: [],
        page: 0,
        total: Infinity,
        loading: false,
        done: false,
      };

      if (isTimeTotalSegmentedView(state, freshIndex)) {
        await loadSegmentedTimeTotalFull(board, state, freshIndex, false, true);
      } else {
        while (!freshIndex.done) {
          const page = freshIndex.page + 1;
          const payload = await api(state.contract.endpoint, {
            ...freshIndex.query,
            page,
          }, fixtureRequestSignal(state));
          mergePayload(state, freshIndex, payload);
          if (!isFixturesBoard(state)) void loadOddsPage(board, state, freshIndex, page);
          freshIndex.page = page;
          await new Promise<void>((resolve) =>
            requestAnimationFrame(() => resolve()),
          );
        }
      }

      if (freshIndex.done) {
        state.indexViewsByGroupMode[freshIndex.groupMode] = freshIndex;
        if (state.groupMode === freshIndex.groupMode) state.indexView = freshIndex;
        if (state.active === requestedTab)
          syncLocalFilteredActiveView(board, state);
        state.lastActiveSyncAt = Date.now();
      }
    } catch (error) {
      console.warn("GWD Livescore local filtered refresh failed", error);
    } finally {
      state.polling = false;
      renderFilterPopover(board, state);
    }
    return;
  }

  if (isTimeTotalSegmentedView(state, view)) {
    state.polling = true;
    view.paint = true;

    try {
      const freshView: View = {
        ...view,
        ids: [],
        page: 0,
        total: Infinity,
        loading: false,
        done: false,
      };

      await loadSegmentedTimeTotalFull(board, state, freshView, false, true);
      if (!freshView.done) return;

      view.ids = freshView.ids;
      view.page = freshView.page;
      view.total = Number.isFinite(freshView.total)
        ? freshView.total
        : freshView.ids.length;
      view.done = freshView.done;

      const indexView = ensureIndexView(state);
      if (
        !indexView.loading &&
        indexView !== view &&
        queryKey(indexView.query) === queryKey(view.query)
      ) {
        indexView.ids = [...view.ids];
        indexView.page = view.page;
        indexView.total = view.total;
        indexView.done = view.done;
        state.indexViewsByGroupMode[indexView.groupMode] = indexView;
      }

      state.totals[view.tab] = view.total;
      setBadge(board, view.tab, view.total);
      updateFilterTabCounts(board, state);
      if (canPaint(state, view)) reconcileVisibleViewRows(board, state, view);
      state.lastActiveSyncAt = Date.now();
    } catch (error) {
      console.warn("GWD Livescore segmented active tab refresh failed", error);
    } finally {
      state.polling = false;
      renderFilterPopover(board, state);
    }
    return;
  }

  state.polling = true;
  view.paint = true;
  if (canPaint(state, view)) setEmptyState(board, false);

  try {
    const freshView: View = {
      ...view,
      ids: [],
      page: 0,
      total: Infinity,
      loading: false,
      done: false,
    };

    while (!freshView.done) {
      const page = freshView.page + 1;
      const payload = await api(state.contract.endpoint, {
        ...view.query,
        page,
      }, fixtureRequestSignal(state));
      mergePayload(state, freshView, payload);
      syncFavoriteBadges(board, state);
      if (!isFixturesBoard(state)) void loadOddsPage(board, state, view, page);
      freshView.page = page;

      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => resolve()),
      );
    }

    view.ids = freshView.ids;
    view.page = freshView.page;
    view.total = Number.isFinite(freshView.total)
      ? freshView.total
      : freshView.ids.length;
    view.done = freshView.done;

    const hasFilters =
      state.applied.competitionIds.length > 0 ||
      state.applied.countryIds.length > 0 ||
      Boolean(statusForFilter(state.applied.status));
    if (view.tab === "total" && !hasFilters) {
      const indexView = ensureIndexView(state);
      if (
        !indexView.loading &&
        indexView !== view &&
        queryKey(indexView.query) === queryKey(view.query)
      ) {
        indexView.ids = [...view.ids];
        indexView.page = view.page;
        indexView.total = view.total;
        indexView.done = view.done;
        state.indexViewsByGroupMode[indexView.groupMode] = indexView;
      }
    }

    state.totals[view.tab] = view.total;
    setBadge(board, view.tab, view.total);
    updateFilterTabCounts(board, state);

    if (shouldFallbackLiveToTotal(state, view)) {
      state.liveFallbackChecked = true;
      state.polling = false;
      activateTabButtonImmediately(board, "total");
      switchTab(board, state, "total");
      return;
    }

    if (canPaint(state, view)) {
      reconcileVisibleViewRows(board, state, view);
    }

    state.lastActiveSyncAt = Date.now();
  } catch (error) {
    console.warn("GWD Livescore active tab refresh failed", error);
  } finally {
    state.polling = false;
    renderFilterPopover(board, state);
  }
};

const startActiveTabPolling = (board: HTMLElement, state: State) => {
  window.setInterval(() => {
    if (document.hidden || !navigator.onLine) return;
    if (
      isFixtureLeagueFiltered(state) &&
      (!isFixtureSeasonFullyLoaded(state) ||
        !isFixtureCurrentSeasonActive(state))
    ) return;
    const fixtureLeagueMode =
      !isResultsBoard(state) &&
      isFixtureLeagueFiltered(state) &&
      isFixtureSeasonFullyLoaded(state) &&
      isFixtureCurrentSeasonActive(state);
    const query = fixtureLeagueMode
      ? { ...buildQuery(state, "total", true), statuses: ["LIVE"] }
      : buildIndexQuery(state);
    const pollKey = runtimeRequestKey(state.contract.endpoint, query);
    if (!livescoreRuntime.beginPoll(pollKey, ACTIVE_TAB_POLL_INTERVAL_MS)) {
      syncBackgroundStateToActiveView(board, state);
      return;
    }

    const primaryPromise = fixtureLeagueMode
      ? refreshFixtureLeagueLive(board, state)
      : refreshInfoSnapshots(board, state);
    const promises = [primaryPromise].filter(
      (promise): promise is Promise<void> => Boolean(promise),
    );
    if (!promises.length) {
      livescoreRuntime.endPoll(pollKey);
      return;
    }
    void Promise.allSettled(promises).finally(() =>
      livescoreRuntime.endPoll(pollKey),
    );
  }, ACTIVE_TAB_POLL_INTERVAL_MS);
};

const refreshCounts = async (board: HTMLElement, state: State) => {
  if (!navigator.onLine) return;
  syncFavoriteBadges(board, state);
  await Promise.all(
    apiMatchTabs.map(async (tab) => {
      try {
        if (tab === "odds") {
          updateFilterTabCounts(board, state);
          return;
        }

        const payload = await api(
          state.contract.endpoint,
          buildQuery(state, tab, false),
          fixtureRequestSignal(state),
        );
        const total = Number(payloadData(payload).total || 0);
        state.totals[tab] = total;
        setBadge(board, tab, total);
      } catch (error) {
        if (isAbortError(error)) return;
        console.warn(`GWD Livescore ${tab} count request failed`, error);
        if (state.totals[tab] !== undefined)
          setBadge(board, tab, state.totals[tab] || 0);
      }
    }),
  );
  state.lastCountsSyncAt = Date.now();
};

const wakeSync = async (board: HTMLElement, state: State, force = false) => {
  if (state.wakeSyncing || document.hidden || !navigator.onLine) return;

  const now = Date.now();
  const shouldSyncActive =
    force || now - state.lastActiveSyncAt >= WAKE_SYNC_STALE_MS;
  const shouldSyncCounts =
    force || now - state.lastCountsSyncAt >= COUNTS_SYNC_STALE_MS;
  if (!shouldSyncActive && !shouldSyncCounts) return;

  state.wakeSyncing = true;
  try {
    if (shouldSyncActive) await refreshInfoSnapshots(board, state);
    if (isFixtureLeagueFiltered(state)) return;
    if (shouldSyncCounts) void refreshCounts(board, state);
  } finally {
    state.wakeSyncing = false;
  }
};

const startWakeSync = (board: HTMLElement, state: State) => {
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) void wakeSync(board, state, true);
  });
  window.addEventListener("focus", () => {
    void wakeSync(board, state, true);
  });
  window.addEventListener("online", () => {
    void wakeSync(board, state, true);
  });
};

const toggleFavorite = (board: HTMLElement, state: State, id: string) => {
  if (!id) return;
  if (state.favorites.has(id)) state.favorites.delete(id);
  else state.favorites.add(id);
  saveFavorites(state.favorites);
  syncFavoriteBadges(board, state);

  const current = ensureView(state, state.active, true);
  mountView(board, state, current, false, state.active === "follow");
};

function closeBoardOverlays(
  board: HTMLElement,
  state: State,
  except?: OverlayKind,
) {
  if (except !== "settings") {
    const settings = board.querySelector<HTMLElement>(
      "[data-gwd-settings-panel]",
    );
    if (settings) settings.hidden = true;
  }

  if (except !== "bookmaker") {
    board
      .querySelectorAll<HTMLElement>("[data-gwd-bookmaker-menu]")
      .forEach((menu) => {
        menu.hidden = true;
      });
  }

  if (except !== "insight") {
    const insight = board.querySelector<HTMLElement>(
      "[data-gwd-match-insight]",
    );
    if (insight) insight.hidden = true;
  }

  if (except !== "fixtureWatch") {
    closeFixtureWatchMenus(board);
  }

  if (except !== "filter") {
    state.draft.mode = null;
    const filter = board.querySelector<HTMLElement>(
      "[data-gwd-filter-popover]",
    );
    if (filter) filter.hidden = true;
    updateTabUi(board, state);
  }
}

const toggleSettingsPanel = (
  board: HTMLElement,
  state: State,
  open?: boolean,
) => {
  const panel = board.querySelector<HTMLElement>("[data-gwd-settings-panel]");
  if (!panel) return;
  const shouldOpen = open === undefined ? panel.hidden : open;
  if (shouldOpen) closeBoardOverlays(board, state, "settings");
  panel.hidden = !shouldOpen;
};

const updateSetting = (
  board: HTMLElement,
  state: State,
  key: keyof BoardSettings,
  value: boolean | string,
) => {
  (state.settings as any)[key] = value;
  saveSettings(state.settings);
  applySettings(board, state);
  patchVisibleViewRows(board, state, ensureView(state, state.active, true));
};

const changeBookmaker = (
  board: HTMLElement,
  state: State,
  companyId: string,
) => {
  if (!BOOKMAKERS[companyId] || state.companyId === companyId) return;
  if (!configuredOddsCompanyIds(state).includes(companyId)) return;
  state.companyId = companyId;
  updateBookmakerUi(board, state);
  board
    .querySelectorAll<HTMLElement>("[data-gwd-bookmaker-menu]")
    .forEach((menu) => {
      menu.hidden = true;
  });
  patchVisibleViewRows(board, state, ensureView(state, state.active, true));
};

const statusOf = (match: any): FilterStatus => {
  const statusId = Number(match?.info?.statusId ?? match?.statusId);
  if (statusId === 1) return "upcoming";
  if (statusId === 8) return "finished";
  if (statusId >= 2 && statusId <= 7) return "live";

  const status = String(
    match?.info?.status || match?.status || "",
  ).toUpperCase();
  if (status === "LIVE") return "live";
  if (status === "FINISHED") return "finished";
  if (status === "UPCOMING") return "upcoming";
  return "all";
};

const hasMarketOdds = (match: any, market: string): boolean => {
  const source = oddsSource(match, market);
  if (!source) return false;
  if (isOddTuple(source) || isOddRow(source)) return true;
  if (Array.isArray(source))
    return source.some((item) => isOddTuple(item) || isOddRow(item));
  if (typeof source === "object") {
    return Object.values(source).some(
      (value) =>
        isOddTuple(value) ||
        isOddRow(value) ||
        (Array.isArray(value) &&
          value.some((item) => isOddTuple(item) || isOddRow(item))),
    );
  }
  return false;
};

const hasOdds = (match: any): boolean =>
  MATCH_FILTER_ODDS_MARKETS.some((market) => hasMarketOdds(match, market));

const emptyCounts = (): Record<FilterStatus, number> => ({
  all: 0,
  odds: 0,
  upcoming: 0,
  finished: 0,
  live: 0,
});

const priorityCompetitionIds = (state: State): Set<string> =>
  new Set(hotCompetitionIds(state));

const fixtureCompetitionFilterIds = (state: State): Set<string> =>
  isFixturesBoard(state) ? new Set(hotCompetitionIds(state)) : new Set();

const filterGroupLetter = (label: string): string => {
  const first = label.trim().charAt(0).toUpperCase();
  return /^[A-Z]$/.test(first) ? first : "#";
};

const getMatchCountry = (
  state: State,
  match: any,
  competition: any,
): { id: string; label: string } => {
  const venueId = String(match?.info?.venueId || "");
  const venue = venueId ? state.entities.venues[venueId] : null;
  const value =
    venue?.countryId ||
    competition?.countryId ||
    competition?.nationId ||
    competition?.country?.id ||
    "";
  const id = value ? String(value) : "";
  const label =
    id && state.entities.countries[id]?.name
      ? state.entities.countries[id].name
      : venue?.country || competition?.host?.country || "Khác";

  if (!id || !label || label === "Khác")
    return { id: "__other__", label: "Khác" };

  return { id, label: String(label) };
};

const hasLocalEntityFilter = (state: State): boolean =>
  state.applied.competitionIds.length > 0 ||
  state.applied.countryIds.length > 0;

const localFilterSourceIds = (state: State): string[] => {
  const indexView = ensureIndexView(state);
  return indexView.ids.length
    ? indexView.ids
    : Object.keys(state.entities.matches);
};

const matchPassesLocalFilters = (state: State, id: string): boolean => {
  const match = state.entities.matches[id];
  if (!match) return false;

  if (state.applied.competitionIds.length) {
    const competitionId = String(match?.info?.competitionId || "");
    if (!state.applied.competitionIds.includes(competitionId)) return false;
    if (!matchPassesFixtureSeason(state, id)) return false;
    if (!matchPassesFixtureDate(state, id)) return false;
  }

  if (state.applied.countryIds.length) {
    const competition =
      state.entities.competitions[String(match?.info?.competitionId || "")] ||
      {};
    const country = getMatchCountry(state, match, competition);
    if (!state.applied.countryIds.includes(country.id)) return false;
  }

  const filterStatuses = statusForFilter(state.applied.status);
  if (filterStatuses && statusOf(match) !== state.applied.status) return false;
  if (state.applied.status === "odds" && !hasOdds(match)) return false;

  return true;
};

const matchPassesTab = (state: State, id: string, tab: MatchTab): boolean => {
  const match = state.entities.matches[id];
  if (!match) return false;
  if (!matchPassesLocalFilters(state, id)) return false;

  if (tab === "live") return statusOf(match) === "live";
  if (tab === "finished") return statusOf(match) === "finished";
  if (tab === "hot") {
    const competitionId = String(match?.info?.competitionId || "");
    return hotCompetitionIds(state).includes(competitionId);
  }
  if (tab === "odds") return hasOdds(match);
  if (tab === "follow") return state.favorites.has(id);
  return true;
};

const materializeLocalFilteredView = (state: State, tab: MatchTab): View => {
  syncGroupModeStores(state);
  const view = ensureView(state, tab, true);
  const sourceView = state.indexView;
  const ids = localFilterSourceIds(state).filter((id) =>
    matchPassesTab(state, id, tab),
  );

  view.ids = ids;
  // An empty state is only meaningful after the shared source has settled.
  // Until then keep the transition skeleton visible instead of replacing it
  // with an empty list while an index request is still in flight.
  view.page = ids.length > 0 ? Math.max(1, sourceView?.page || 0) : 0;
  view.total = sourceView?.done ? ids.length : Infinity;
  view.loading = false;
  view.done = Boolean(sourceView?.done);
  view.filtered = true;
  view.paint = true;
  state.views[tab] = view;
  return view;
};

const shouldUseLocalFilteredView = (state: State): boolean =>
  hasLocalEntityFilter(state) || state.applied.status === "odds";

const canUseLocalStatusTab = (state: State, tab: MatchTab): boolean =>
  !isFixturesBoard(state) &&
  Boolean(state.indexView?.done) &&
  state.applied.competitionIds.length === 0 &&
  state.applied.countryIds.length === 0 &&
  (tab === "total" || tab === "live" || tab === "finished");

const syncLocalFilteredActiveView = (board: HTMLElement, state: State) => {
  if (!shouldUseLocalFilteredView(state)) return;
  const view = materializeLocalFilteredView(state, state.active);
  syncViewBadge(board, state, view);
  updateFilterTabCounts(board, state);
  if (canPaint(state, view)) mountView(board, state, view);
};

const syncBackgroundStateToActiveView = (board: HTMLElement, state: State) => {
  if (shouldUseLocalFilteredView(state)) {
    syncLocalFilteredActiveView(board, state);
    return;
  }

  const view = ensureView(state, state.active, true);
  syncViewBadge(board, state, view);
  updateFilterTabCounts(board, state);
  if (canPaint(state, view)) patchVisibleViewRows(board, state, view);
};

const reloadIndexView = (
  board: HTMLElement,
  state: State,
): Promise<void> | undefined => {
  syncGroupModeStores(state);
  if (state.indexView?.loading) return;

  const freshIndex: View = {
    tab: "total",
    groupMode: dataGroupMode(state),
    query: buildIndexQuery(state),
    ids: [],
    page: 0,
    total: Infinity,
    loading: false,
    done: false,
    filtered: false,
    paint: false,
  };

  state.indexView = freshIndex;
  state.indexViewsByGroupMode[freshIndex.groupMode] = freshIndex;
  return loadFull(
    board,
    state,
    freshIndex,
    false,
    true,
    false,
    false,
  );
};

const recentFinishedQuery = (state: State): Record<string, any> => {
  const now = new Date();
  return {
    ...buildQuery(state, "finished", false),
    page: 1,
    limit: 50,
    fromDate: toGmt7Iso(new Date(now.getTime() - (4 * 60 * 60 * 1000))),
    toDate: toGmt7Iso(now),
    statuses: ["FINISHED"],
    sortMatchTime: "desc",
    returnList: ["info"],
    isIncludedRelation: "false",
  };
};

const refreshRecentFinishedForLive = async (
  board: HTMLElement,
  state: State,
) => {
  if (state.active !== "live") return;
  const query = recentFinishedQuery(state);
  const finishedView: View = {
    tab: "finished",
    groupMode: dataGroupMode(state),
    query,
    ids: [],
    page: 0,
    total: Infinity,
    loading: false,
    done: false,
    filtered: false,
    paint: false,
  };

  while (!finishedView.done) {
    const page = finishedView.page + 1;
    const payload = await api(state.contract.endpoint, { ...query, page });
    mergePayload(state, finishedView, payload);
    finishedView.page = page;
    state.recentFinishedIds = new Set(finishedView.ids);
  }

  if (state.active !== "live") return;
  const liveView = ensureView(state, "live", true);
  renderDesktopView(board, state, liveView, false, true);
  renderMobileView(board, state, liveView);
  syncEmptyState(board, state, liveView);
};

const filterSourceMatches = (state: State): any[] => {
  const ids = state.indexView?.ids?.length
    ? state.indexView.ids
    : Object.keys(state.entities.matches);
  return ids
    .filter((id) => isRenderableMatch(state, id))
    .map((id) => state.entities.matches[id])
    .filter(Boolean);
};

const filterMatchesForDraftStatus = (state: State): any[] => {
  return filterSourceMatches(state).filter((match) => {
    if (state.draft.status === "all") return true;
    if (state.draft.status === "odds") return hasOdds(match);
    return statusOf(match) === state.draft.status;
  });
};

const filterOptionCount = (state: State, mode: FilterMode): number => {
  const ids = new Set<string>();

  filterMatchesForDraftStatus(state).forEach((match) => {
    const info = match?.info || {};
    const competition =
      state.entities.competitions[String(info.competitionId || "")] || {};

    if (mode === "leagues") {
      const id = String(info.competitionId || "");
      if (id) ids.add(id);
      return;
    }

    ids.add(getMatchCountry(state, match, competition).id);
  });

  return ids.size;
};

const updateFilterTabCounts = (board: HTMLElement, state: State) => {
  if (!isIndexCountReady(state)) {
    if (!state.countBadgesReady.filters) {
      setBadgeLoading(board, "leagues", true);
      setBadgeLoading(board, "countries", true);
    }
    if (!state.countBadgesReady.odds) setBadgeLoading(board, "odds", true);
    return;
  }

  setAnyBadge(board, "leagues", filterOptionCount(state, "leagues"));
  setAnyBadge(board, "countries", filterOptionCount(state, "countries"));
  state.countBadgesReady.filters = true;
  syncOddsBadge(board, state);
};

const filterOptions = (state: State, mode: FilterMode): FilterOption[] => {
  const map = new Map<string, FilterOption>();
  const fixtureAllowedIds = fixtureCompetitionFilterIds(state);

  filterMatchesForDraftStatus(state).forEach((match) => {
    const info = match?.info || {};
    const competition =
      state.entities.competitions[String(info.competitionId || "")] || {};
    const status = statusOf(match);
    const matchHasOdds = hasOdds(match);
    let id = "";
    let label = "";

    if (mode === "leagues") {
      id = String(info.competitionId || "");
      if (fixtureAllowedIds.size && !fixtureAllowedIds.has(id)) return;
      label = competition.shortName || competition.name || "";
    } else {
      const country = getMatchCountry(state, match, competition);
      id = country.id;
      label = country.label;
    }

    if (!id) return;

    const option = map.get(id) || {
      id,
      label: label || "Khác",
      count: 0,
      statusCounts: emptyCounts(),
      hasOdds: false,
    };

    option.count += 1;
    option.statusCounts.all += 1;
    option.statusCounts[status] += 1;
    if (matchHasOdds) {
      option.hasOdds = true;
      option.statusCounts.odds += 1;
    }

    map.set(id, option);
  });

  const options = [...map.values()];

  if (state.draft.alphabetical) {
    options.sort((a, b) => a.label.localeCompare(b.label, "vi"));
  }

  return options;
};

const selectedSet = (state: State): Set<string> =>
  state.draft.mode === "countries"
    ? state.draft.countryIds
    : state.draft.competitionIds;

const setDraftSelection = (state: State, mode: FilterMode, ids: string[]) => {
  if (mode === "countries") {
    state.draft.countryIds = new Set(ids);
    state.draft.competitionIds = new Set();
    return;
  }

  state.draft.competitionIds = new Set(ids);
  state.draft.countryIds = new Set();
};

const selectAllDraftOptions = (state: State, mode: FilterMode) => {
  setDraftSelection(
    state,
    mode,
    filterOptions(state, mode).map((option) => option.id),
  );
};

const syncDraftFromApplied = (state: State, mode: FilterMode) => {
  state.draft.mode = mode;
  state.draft.status = state.applied.status || "all";
  state.draft.touched = false;

  const appliedIds =
    mode === "countries"
      ? state.applied.countryIds
      : state.applied.competitionIds;
  if (appliedIds.length) {
    setDraftSelection(state, mode, appliedIds);
    state.draft.touched = true;
    return;
  }

  selectAllDraftOptions(state, mode);
};

const renderFilterPopover = (board: HTMLElement, state: State) => {
  const mode = state.draft.mode;
  const popover = board.querySelector<HTMLElement>("[data-gwd-filter-popover]");
  if (!popover) return;

  if (!mode) {
    popover.hidden = true;
    updateTabUi(board, state);
    return;
  }

  const title = board.querySelector<HTMLElement>("[data-gwd-filter-title]");
  const items = board.querySelector<HTMLElement>("[data-gwd-filter-items]");
  const itemTemplate = board.querySelector<HTMLTemplateElement>(
    "[data-gwd-filter-item-template]",
  );
  const groupTemplate = board.querySelector<HTMLTemplateElement>(
    "[data-gwd-filter-group-template]",
  );
  const clearButton = board.querySelector<HTMLButtonElement>(
    "[data-gwd-filter-clear]",
  );

  if (!items || !itemTemplate) return;

  const options = filterOptions(state, mode);
  if (title)
    title.textContent = mode === "leagues" ? "CHỌN GIẢI ĐẤU" : "CHỌN QUỐC GIA";
  board
    .querySelectorAll<HTMLElement>("[data-gwd-filter-status]")
    .forEach((button) => {
      button.classList.toggle(
        "is-active",
        button.dataset.gwdFilterStatus === state.draft.status,
      );
    });

  if (options.length === 0 && state.indexView?.loading) {
    renderFilterSkeletons(board);
    popover.hidden = false;
    updateTabUi(board, state);
    return;
  }
  const hotIds = priorityCompetitionIds(state);
  const selected = selectedSet(state);
  const appliedIds =
    mode === "countries"
      ? state.applied.countryIds
      : state.applied.competitionIds;

  if (
    !state.draft.touched &&
    appliedIds.length === 0 &&
    selected.size !== options.length
  ) {
    setDraftSelection(
      state,
      mode,
      options.map((option) => option.id),
    );
  }

  const currentSelected = selectedSet(state);
  const visibleSelected = options.filter((option) =>
    currentSelected.has(option.id),
  ).length;

  if (clearButton) {
    clearButton.textContent =
      visibleSelected === options.length && options.length
        ? `Bỏ tất cả (${options.length})`
        : visibleSelected > 0
          ? `Bỏ (${visibleSelected})`
          : "Chọn tất cả";
  }

  const fragment = document.createDocumentFragment();
  let currentLetter = "";
  options.forEach((option) => {
    if (state.draft.alphabetical && groupTemplate) {
      const letter = filterGroupLetter(option.label);
      if (letter !== currentLetter) {
        currentLetter = letter;
        const group = groupTemplate.content.firstElementChild?.cloneNode(
          true,
        ) as HTMLElement | undefined;
        if (group) {
          setText(group, "[data-gwd-filter-letter]", letter);
          fragment.append(group);
        }
      }
    }

    const label = itemTemplate.content.firstElementChild?.cloneNode(true) as
      | HTMLElement
      | undefined;
    if (!label) return;
    label.classList.toggle(
      "is-hot",
      mode === "leagues" && hotIds.has(option.id),
    );

    const input = label.querySelector<HTMLInputElement>(
      "[data-gwd-filter-checkbox]",
    );
    if (input) {
      input.value = option.id;
      input.checked = currentSelected.has(option.id);
    }

    setText(label, "[data-gwd-filter-name]", option.label);
    setText(label, "[data-gwd-filter-count]", option.count);
    fragment.append(label);
  });

  items.replaceChildren(fragment);
  popover.hidden = false;
  updateTabUi(board, state);
};

const openFilter = (board: HTMLElement, state: State, mode: FilterMode) => {
  if (state.draft.mode === mode) {
    closeBoardOverlays(board, state);
    return;
  }

  closeBoardOverlays(board, state, "filter");
  syncDraftFromApplied(state, mode);
  renderFilterPopover(board, state);

  reloadIndexView(board, state);
};

const confirmFilter = (board: HTMLElement, state: State) => {
  const mode = state.draft.mode;
  const options = mode ? filterOptions(state, mode) : [];
  const selected = mode ? selectedSet(state) : new Set<string>();
  const selectedIds = options
    .filter((option) => selected.has(option.id))
    .map((option) => option.id)
    .filter((id) => id !== "__other__");
  const allSelected =
    options.length > 0 && options.every((option) => selected.has(option.id));

  state.applied.competitionIds = [];
  state.applied.countryIds = [];
  state.applied.mode = mode || null;
  state.applied.status = state.draft.status;

  if (!allSelected && selectedIds.length && mode === "leagues") {
    state.applied.competitionIds = selectedIds;
  }

  if (!allSelected && selectedIds.length && mode === "countries") {
    state.applied.countryIds = selectedIds;
  }

  state.draft.mode = null;
  state.active =
    state.applied.status === "live"
      ? "live"
      : state.applied.status === "finished"
        ? "finished"
        : state.applied.status === "odds"
          ? "odds"
          : "total";
  state.views[state.active] = undefined;

  prepareMatchListTransition(board);
  updateTabUi(board, state);
  renderFilterPopover(board, state);
  const current = shouldUseLocalFilteredView(state)
    ? materializeLocalFilteredView(state, state.active)
    : ensureView(state, state.active, true);
  syncViewBadge(board, state, current);
  if (current.ids.length > 0 || current.page > 0 || current.done) {
    mountView(board, state, current);
  }
  if (shouldUseLocalFilteredView(state)) {
    reloadIndexView(board, state);
    return;
  }
  void loadFull(board, state, current, true, false, true);
};

const handleFilterClick = (
  board: HTMLElement,
  state: State,
  target: HTMLElement,
): boolean => {
  const statusButton = target.closest<HTMLElement>("[data-gwd-filter-status]");
  if (statusButton) {
    state.draft.status =
      (statusButton.dataset.gwdFilterStatus as FilterStatus) || "all";
    if (state.draft.mode) {
      selectAllDraftOptions(state, state.draft.mode);
      state.draft.touched = false;
    }
    renderFilterPopover(board, state);
    return true;
  }

  if (target.closest("[data-gwd-filter-sort]")) {
    state.draft.alphabetical = !state.draft.alphabetical;
    renderFilterPopover(board, state);
    return true;
  }

  if (target.closest("[data-gwd-filter-clear]")) {
    const options = filterOptions(state, state.draft.mode || "leagues");
    const selected = selectedSet(state);
    const allVisibleSelected =
      options.length > 0 && options.every((option) => selected.has(option.id));

    options.forEach((option) => {
      if (allVisibleSelected) selected.delete(option.id);
      else selected.add(option.id);
    });

    state.draft.touched = true;
    renderFilterPopover(board, state);
    return true;
  }

  if (target.closest("[data-gwd-filter-confirm]")) {
    confirmFilter(board, state);
    return true;
  }

  return false;
};

const switchTab = (board: HTMLElement, state: State, tab: MatchTab) => {
  state.active = tab;
  state.draft.mode = null;
  state.draft.competitionIds = new Set();
  state.draft.countryIds = new Set();
  state.draft.touched = false;
  if (!isFixturesBoard(state)) {
    state.applied.competitionIds = [];
    state.applied.countryIds = [];
    state.applied.mode = null;
  }
  state.applied.status = tab === "odds" ? "odds" : "all";
  prepareMatchListTransition(board);
  updateTabUi(board, state);
  renderFilterPopover(board, state);

  deferUntilAfterTabPaint(() => {
    if (state.active !== tab) return;

    const useLocalStatusState = canUseLocalStatusTab(state, tab);
    const current =
      tab === "odds" || useLocalStatusState
        ? materializeLocalFilteredView(state, tab)
        : ensureView(state, tab, true);

    if (state.active !== tab) return;

    syncViewBadge(board, state, current);
    if (current.ids.length > 0 || current.page > 0 || current.done) {
      mountView(board, state, current);
    }
    if (tab === "odds") {
      reloadIndexView(board, state);
      return;
    }
    if (useLocalStatusState) return;
    void loadFull(board, state, current, true, false, true);
  });
};

const resetBoardViews = (state: State) => {
  state.viewsByGroupMode = { time: {}, league: {} };
  state.indexViewsByGroupMode = {};
  state.totalsByGroupMode = { time: {}, league: {} };
  state.countBadgesReady.filters = false;
  state.countBadgesReady.odds = false;
  syncGroupModeStores(state);
};

const syncFixtureDateUi = (board: HTMLElement, state: State) => {
  if (!isFixturesBoard(state)) return;
  const inputWrap = board.querySelector<HTMLElement>("[data-gwd-fixtures-date-wrap]");
  const selectWrap = board.querySelector<HTMLElement>("[data-gwd-fixtures-date-select-wrap]");
  const input = board.querySelector<HTMLInputElement>("[data-gwd-fixtures-date]");
  const select = board.querySelector<HTMLSelectElement>("[data-gwd-fixtures-date-select]");
  const isLeagueMode = isFixtureLeagueFiltered(state);

  if (inputWrap) inputWrap.hidden = isLeagueMode;
  if (selectWrap) selectWrap.hidden = !isLeagueMode;

  if (!isLeagueMode) {
    const date = normalizeFixtureDate(state.contract.fixtureDate);
    if (input) input.value = date;
  }

  if (isLeagueMode && select) {
    const dates = fixtureAvailableDates(state);
    const activeDate = state.applied.fixtureDate;
    const signature = dates.join("|");
    if (select.dataset.gwdFixtureDateSignature !== signature) {
      const fragment = document.createDocumentFragment();
      const all = document.createElement("option");
      all.value = "";
      all.textContent = dates.length ? "Tất cả ngày" : "Đang tải ngày...";
      all.disabled = dates.length === 0;
      fragment.append(all);
      dates.forEach((dateKey) => {
        const option = document.createElement("option");
        option.value = dateKey;
        option.textContent = fixtureDateLabel(dateKey);
        fragment.append(option);
      });
      select.replaceChildren(fragment);
      select.dataset.gwdFixtureDateSignature = signature;
    }
    select.value = dates.includes(activeDate) ? activeDate : "";
    select.disabled = dates.length === 0;
  }

  const date = normalizeFixtureDate(state.contract.fixtureDate);
  const today = normalizeFixtureDate();
  const hasSelectedDate = Boolean(String(state.contract.fixtureDate || "").trim());
  const isDefaultWindowMode =
    state.applied.competitionIds.length === 0 && !hasSelectedDate;
  board
    .querySelectorAll<HTMLElement>("[data-gwd-fixtures-today]")
    .forEach((button) =>
      button.classList.toggle("is-active", isDefaultWindowMode && date === today),
    );
};

const syncFixtureSeasonUi = (board: HTMLElement, state: State) => {
  if (!isFixturesBoard(state)) return;
  const wrap = board.querySelector<HTMLElement>("[data-gwd-fixtures-season-wrap]");
  const select = board.querySelector<HTMLSelectElement>("[data-gwd-fixtures-season]");
  if (!wrap || !select) return;

  if (isResultsBoard(state)) {
    wrap.hidden = true;
    select.replaceChildren();
    delete select.dataset.gwdFixtureSeasonSignature;
    select.value = "";
    return;
  }

  const competitionId = fixtureSelectedCompetitionId(state);
  const seasons = fixtureCompetitionSeasons(state, competitionId);
  if (!competitionId) {
    wrap.hidden = true;
    select.replaceChildren();
    delete select.dataset.gwdFixtureSeasonSignature;
    select.value = "";
    return;
  }

  if (seasons.length === 0) {
    const seasonLookupSettled = Boolean(state.views.total?.done);
    const emptySignature = seasonLookupSettled
      ? `${competitionId}:__empty__`
      : `${competitionId}:__loading__`;
    if (select.dataset.gwdFixtureSeasonSignature !== emptySignature) {
      const option = document.createElement("option");
      option.value = "";
      option.textContent = seasonLookupSettled
        ? "Không có season"
        : "Đang tải season...";
      option.disabled = true;
      select.replaceChildren(option);
      select.dataset.gwdFixtureSeasonSignature = emptySignature;
    }
    select.disabled = true;
    wrap.hidden = false;
    return;
  }

  const activeSeasonId = fixtureActiveSeasonId(state);
  const availableSeasonIds = seasons.map((season: any) =>
    String(season?.id || ""),
  );
  const resolvedSeasonId = availableSeasonIds.includes(activeSeasonId)
    ? activeSeasonId
    : availableSeasonIds[0] || "";
  if (resolvedSeasonId && state.applied.seasonId !== resolvedSeasonId) {
    state.applied.seasonId = resolvedSeasonId;
    state.fixtureSeasonSelectionByCompetitionId[competitionId] = resolvedSeasonId;
  }
  const signature = `${competitionId}:` + seasons
    .map((season: any) => `${season.id}:${fixtureSeasonLabel(season)}`)
    .join("|");

  if (select.dataset.gwdFixtureSeasonSignature !== signature) {
    const fragment = document.createDocumentFragment();
    seasons.forEach((season: any) => {
      const option = document.createElement("option");
      option.value = String(season.id || "");
      option.textContent = fixtureSeasonLabel(season);
      fragment.append(option);
    });
    select.replaceChildren(fragment);
    select.dataset.gwdFixtureSeasonSignature = signature;
  }

  select.value = resolvedSeasonId;
  select.disabled = seasons.length <= 1;
  wrap.hidden = false;
};

const syncFixtureLeagueHeading = (board: HTMLElement, state: State) => {
  if (!isFixturesBoard(state)) return;
  const heading = board.querySelector<HTMLElement>(
    "[data-gwd-fixtures-league-heading]",
  );
  if (!heading) return;

  const competitionId = fixtureSelectedCompetitionId(state);
  if (!competitionId) {
    heading.hidden = true;
    return;
  }

  const competition = state.entities.competitions[competitionId] || {};
  const resolvedLeagueName = String(
    competition.name ||
      competition.shortName ||
      competition.code ||
      competition.shortCode ||
      "",
  ).trim();
  const leagueName = resolvedLeagueName || "Đang tải giải đấu...";
  const seasonLabel = fixtureActiveSeasonLabel(state);
  const title = resolvedLeagueName
    ? [leagueName, seasonLabel].filter(Boolean).join(" ")
    : leagueName;
  setText(heading, "[data-gwd-fixtures-league-heading-title]", title);
  setImage(
    heading,
    "[data-gwd-fixtures-league-heading-logo]",
    competitionLogo(competition),
    leagueName,
  );
  heading.hidden = false;
};

const syncFixtureLeagueChips = (board: HTMLElement, state: State) => {
  if (!isFixturesBoard(state)) return;
  // PHP owns the fixture league IDs and labels. The client only marks selection.
  syncFixtureLeagueSelects(board, state);
  markFixtureLeagueChips(board, state);
};

const syncFixtureLeagueSelects = (board: HTMLElement, state: State) => {
  const options = Array.from(
    board.querySelectorAll<HTMLElement>(
      "[data-gwd-fixtures-league-chips] [data-gwd-fixtures-league-id]",
    ),
  ).reduce<Array<{ id: string; label: string }>>((items, button) => {
    const id = String(button.dataset.gwdFixturesLeagueId || "");
    if (!id || items.some((item) => item.id === id)) return items;
    items.push({ id, label: button.textContent?.trim() || id });
    return items;
  }, []);
  const signature = options.map((option) => `${option.id}:${option.label}`).join("|");
  const activeId = state.applied.competitionIds[0] || "";
  board
    .querySelectorAll<HTMLSelectElement>("[data-gwd-fixtures-league-select]")
    .forEach((select) => {
      if (select.dataset.gwdFixtureLeagueSignature !== signature) {
        const fragment = document.createDocumentFragment();
        const allOption = document.createElement("option");
        allOption.value = "";
        allOption.textContent = "Tất cả giải";
        fragment.append(allOption);
        options.forEach((item) => {
          const option = document.createElement("option");
          option.value = item.id;
          option.textContent = item.label;
          fragment.append(option);
        });
        select.replaceChildren(fragment);
        select.dataset.gwdFixtureLeagueSignature = signature;
      }
      select.value = activeId;
    });
};

const markFixtureLeagueChips = (board: HTMLElement, state: State) => {
  if (!isFixturesBoard(state)) return;
  const activeId = state.applied.competitionIds[0] || "";
  board
    .querySelectorAll<HTMLSelectElement>("[data-gwd-fixtures-league-select]")
    .forEach((select) => {
      select.value = activeId;
    });
  board
    .querySelectorAll<HTMLElement>("[data-gwd-fixtures-league-id]")
    .forEach((button) => {
      button.classList.toggle(
        "is-active",
        Boolean(activeId) && button.dataset.gwdFixturesLeagueId === activeId,
      );
    });
};

const changeFixtureDate = (
  board: HTMLElement,
  state: State,
  dateValue?: string,
  useDefaultWindow = false,
) => {
  if (!isFixturesBoard(state)) return;
  if (useDefaultWindow || !isFixtureLeagueFiltered(state)) {
    cancelFixtureRequests(state);
  }
  if (isFixtureLeagueFiltered(state) && !useDefaultWindow) {
    state.active = "total";
    state.applied.status = "all";
    state.applied.fixtureDate = dateValue ? normalizeFixtureDate(dateValue) : "";
    prepareMatchListTransition(board);
    syncFixtureDateUi(board, state);
    updateTabUi(board, state);
    deferUntilAfterTabPaint(() => {
      const view = materializeLocalFilteredView(state, "total");
      syncViewBadge(board, state, view);
      mountView(board, state, view);
      syncEmptyState(board, state, view);
    });
    return;
  }
  state.contract.fixtureDate =
    !useDefaultWindow && dateValue ? normalizeFixtureDate(dateValue) : "";
  state.active = "total";
  state.applied.mode = null;
  state.applied.status = "all";
  state.applied.competitionIds = [];
  state.applied.countryIds = [];
  state.applied.seasonId = "";
  state.applied.fixtureDate = "";
  state.views.total = undefined;
  state.indexView = undefined;
  state.indexViewsByGroupMode[dataGroupMode(state)] = undefined;
  prepareMatchListTransition(board);
  syncFixtureDateUi(board, state);
  syncFixtureSeasonUi(board, state);
  syncFixtureLeagueHeading(board, state);
  markFixtureLeagueChips(board, state);
  updateTabUi(board, state);
  switchTab(board, state, "total");
  reloadIndexView(board, state);
};

const filterFixtureLeague = (
  board: HTMLElement,
  state: State,
  competitionId: string,
) => {
  if (!isFixturesBoard(state) || !competitionId) return;
  cancelFixtureRequests(state);
  const previousScopeKey = fixtureViewScopeKey(state);
  const previousView = state.views.total;
  if (previousView?.done) {
    state.fixtureViewsByScopeKey[previousScopeKey] = previousView;
  }
  const isSame = state.applied.competitionIds[0] === competitionId;
  state.active = "total";
  state.applied.mode = isSame ? null : "leagues";
  state.applied.status = "all";
  state.applied.countryIds = [];
  state.applied.competitionIds = isSame ? [] : [competitionId];
  state.applied.seasonId = isResultsBoard(state)
    ? ""
    : isSame
    ? ""
    : state.fixtureSeasonSelectionByCompetitionId[competitionId] ||
      fixtureCurrentSeasonId(state, competitionId);
  state.applied.fixtureDate = "";
  if (!isSame) {
    state.groupMode = "time";
    saveGroupMode(state.groupMode);
    syncGroupModeStores(state);
  }
  state.views.total = undefined;
  state.indexView = undefined;
  state.indexViewsByGroupMode[dataGroupMode(state)] = undefined;
  prepareMatchListTransition(board);
  updateTabUi(board, state);
  updateGroupUi(board, state);
  syncFixtureDateUi(board, state);
  markFixtureLeagueChips(board, state);
  syncFixtureSeasonUi(board, state);
  syncFixtureLeagueHeading(board, state);
  const requestedCompetitionId = state.applied.competitionIds[0] || "";
  const requestedSeasonId = fixtureActiveSeasonId(state);
  const requestedScopeKey = fixtureViewScopeKey(state);
  const cachedScopeView = state.fixtureViewsByScopeKey[requestedScopeKey];

  if (cachedScopeView?.done) {
    cachedScopeView.paint = true;
    state.views.total = cachedScopeView;
    state.indexView = cachedScopeView;
    state.indexViewsByGroupMode[cachedScopeView.groupMode] = cachedScopeView;
    syncViewBadge(board, state, cachedScopeView);
    mountView(board, state, cachedScopeView);
    syncEmptyState(board, state, cachedScopeView);
    updateFilterTabCounts(board, state);
    state.lastActiveSyncAt = Date.now();
    return;
  }

  const hasCompleteSeasonState = Boolean(
    !isResultsBoard(state) &&
    requestedCompetitionId &&
      requestedSeasonId &&
      state.loadedFixtureSeasonIds.has(
        fixtureSeasonStateKey(state, requestedSeasonId, requestedCompetitionId),
      ),
  );

  if (hasCompleteSeasonState) {
    const current = materializeLocalFilteredView(state, "total");
    state.indexView = current;
    state.indexViewsByGroupMode[current.groupMode] = current;
    syncViewBadge(board, state, current);
    mountView(board, state, current);
    syncEmptyState(board, state, current);
    updateFilterTabCounts(board, state);
    state.lastActiveSyncAt = Date.now();
    return;
  }

  const current = ensureView(state, "total", true);
  current.paint = true;
  void loadFull(board, state, current, true, false, true).then(() => {
    if (
      (state.applied.competitionIds[0] || "") === requestedCompetitionId &&
      state.views.total === current
    ) {
      state.indexView = current;
      state.indexViewsByGroupMode[current.groupMode] = current;
      const loadedSeasonId = fixtureActiveSeasonId(state);
      const loadedSeasonKey = fixtureSeasonStateKey(state, loadedSeasonId);
      if (loadedSeasonKey) state.loadedFixtureSeasonIds.add(loadedSeasonKey);
      state.fixtureViewsByScopeKey[fixtureViewScopeKey(state)] = current;
      updateFilterTabCounts(board, state);
    }
  });
};

const changeFixtureSeason = (
  board: HTMLElement,
  state: State,
  seasonId: string,
) => {
  if (!isFixturesBoard(state) || !isFixtureLeagueFiltered(state)) return;
  cancelFixtureRequests(state);
  const requestedSeasonId = String(seasonId || "");
  const competitionId = fixtureSelectedCompetitionId(state);
  if (competitionId) {
    state.fixtureSeasonSelectionByCompetitionId[competitionId] = requestedSeasonId;
  }
  const requestedSeasonKey = fixtureSeasonStateKey(
    state,
    requestedSeasonId,
    competitionId,
  );
  const seasonAlreadyLoaded = state.loadedFixtureSeasonIds.has(requestedSeasonKey);
  const isCurrentSeason = requestedSeasonId === fixtureCurrentSeasonId(state);
  state.active = "total";
  state.applied.status = "all";
  state.applied.seasonId = requestedSeasonId;
  state.applied.fixtureDate = "";
  state.views.total = undefined;
  state.indexView = undefined;
  state.indexViewsByGroupMode[dataGroupMode(state)] = undefined;
  if (seasonAlreadyLoaded) setEmptyState(board, false);
  else prepareMatchListTransition(board);
  syncFixtureSeasonUi(board, state);
  syncFixtureLeagueHeading(board, state);
  updateTabUi(board, state);
  if (seasonAlreadyLoaded) {
    const current = materializeLocalFilteredView(state, "total");
    mountView(board, state, current);
    syncEmptyState(board, state, current);
    finalizeEmptyFixturesView(board, state, current);
    if (isCurrentSeason) void refreshFixtureLeagueLive(board, state);
    return;
  }

  const current = ensureView(state, "total", true);
  current.paint = true;
  void loadFull(board, state, current, true, false, false).then(() => {
    if (
      state.applied.seasonId === requestedSeasonId &&
      state.views.total === current &&
      current.done
    ) {
      if (requestedSeasonKey)
        state.loadedFixtureSeasonIds.add(requestedSeasonKey);
      state.indexView = current;
      state.indexViewsByGroupMode[current.groupMode] = current;
    }
  });
};

const toggleGroupMode = (board: HTMLElement, state: State) => {
  if (isFixturesBoard(state) && state.applied.competitionIds.length) {
    state.groupMode = "time";
    updateGroupUi(board, state);
    return;
  }

  state.groupMode = state.groupMode === "league" ? "time" : "league";
  const targetGroupMode = state.groupMode;
  saveGroupMode(state.groupMode);
  syncGroupModeStores(state);
  updateGroupUi(board, state);
  prepareMatchListTransition(board);

  deferUntilAfterTabPaint(() => {
    if (state.groupMode !== targetGroupMode) return;
    const current = ensureView(state, state.active, true);
    if (current.groupMode !== targetGroupMode) return;
    current.paint = true;
    syncViewBadge(board, state, current);
    if (current.ids.length > 0 || current.page > 0 || current.done) {
      mountView(board, state, current);
    }
    void loadFull(board, state, current, true, false, current.ids.length === 0);
    void refreshCounts(board, state);
  });
};

const rawMatchFromDetailPayload = (payload: any): any => {
  const data = payloadData(payload);
  if (data.match) return data.match;
  if (Array.isArray(data.matches)) return data.matches[0] || {};
  return data;
};

const normalizeMinute = (incident: any): string => {
  const value =
    incident?.minute ??
    incident?.time ??
    incident?.matchTime ??
    incident?.incidentTime ??
    incident?.elapsed;
  if (value === undefined || value === null || value === "") return "";
  const text = String(value).replace(/['′]/g, "");
  return /^\d+$/.test(text) ? `${text}'` : String(value);
};

const incidentTypeMeta = (incident: any): { label: string; icon: string } => {
  const raw = String(
    incident?.type ||
      incident?.typeId ||
      incident?.kind ||
      incident?.eventType ||
      incident?.incidentType ||
      incident?.incidentTypeId ||
      incident?.typeName ||
      "",
  ).trim();
  const key = raw.toLowerCase().replace(/[\s-]+/g, "_");
  if (MATCH_INSIGHT_INCIDENT_TYPE_LABELS[raw]) {
    return {
      label: MATCH_INSIGHT_INCIDENT_TYPE_LABELS[raw],
      icon: MATCH_INSIGHT_INCIDENT_TYPE_ICON_KEYS[raw] || "",
    };
  }
  if (MATCH_INSIGHT_INCIDENT_TYPE_LABELS[key]) {
    return {
      label: MATCH_INSIGHT_INCIDENT_TYPE_LABELS[key],
      icon: MATCH_INSIGHT_INCIDENT_TYPE_ICON_KEYS[key] || "",
    };
  }
  if (MATCH_INCIDENT_TYPE_LABELS[key]) return MATCH_INCIDENT_TYPE_LABELS[key];

  if (/goal|score|bàn/i.test(raw)) return MATCH_INCIDENT_TYPE_LABELS.goal;
  if (/yellow|vàng/i.test(raw)) return MATCH_INCIDENT_TYPE_LABELS.yellow_card;
  if (/red|đỏ/i.test(raw)) return MATCH_INCIDENT_TYPE_LABELS.red_card;
  if (/sub|change|thay/i.test(raw))
    return MATCH_INCIDENT_TYPE_LABELS.substitution;

  return { label: raw || "Diễn biến", icon: "" };
};

const normalizeIncidentSide = (
  incident: any,
  info: any,
): "home" | "away" | "neutral" => {
  const teamId = String(
    incident?.teamId || incident?.team_id || incident?.team?.id || "",
  );
  if (teamId && teamId === String(info?.homeTeamId || "")) return "home";
  if (teamId && teamId === String(info?.awayTeamId || "")) return "away";

  const side = String(
    incident?.side || incident?.position || incident?.teamType || "",
  ).toLowerCase();
  if (side.includes("home") || side === "1") return "home";
  if (side.includes("away") || side === "2") return "away";
  return "neutral";
};

const normalizeIncidentLabel = (incident: any): string => {
  const meta = incidentTypeMeta(incident);
  const player =
    incident?.playerName ||
    incident?.player?.name ||
    incident?.player ||
    incident?.name ||
    "";
  const assist = incident?.assistName || incident?.assist?.name || "";
  const description =
    incident?.description || incident?.reason || incident?.remark || "";
  const pieces = [player || description || meta.label].filter(Boolean);
  if (assist) pieces.push(`(${assist})`);
  return pieces.join(" ");
};

const incidentText = (value: any): string => {
  if (value === undefined || value === null) return "";
  return String(value).trim();
};

const incidentPlayerName = (incident: any): string =>
  incidentText(
    incident?.playerName ||
      incident?.player?.name ||
      incident?.player ||
      incident?.name,
  );

const incidentAssistName = (incident: any): string =>
  incidentText(
    incident?.assistName ||
      incident?.assist1Name ||
      incident?.assist?.name ||
      incident?.assist,
  );

const incidentInPlayerName = (incident: any): string =>
  incidentText(
    incident?.inPlayerName ||
      incident?.inPlayer?.name ||
      incident?.playerInName ||
      incident?.playerIn?.name ||
      incident?.inPlayer,
  );

const incidentOutPlayerName = (incident: any): string =>
  incidentText(
    incident?.outPlayerName ||
      incident?.outPlayer?.name ||
      incident?.playerOutName ||
      incident?.playerOut?.name ||
      incident?.outPlayer,
  );

const incidentIconKey = (icon: string): string => {
  const normalized = String(icon || "")
    .trim()
    .toLowerCase();
  if (!normalized) return "";
  if (normalized.includes("yellow") || normalized.includes("vàng") || normalized.includes("🟨"))
    return "yellow-card";
  if (normalized.includes("red") || normalized.includes("đỏ") || normalized.includes("🟥"))
    return "red-card";
  if (
    normalized.includes("sub") ||
    normalized.includes("change") ||
    normalized.includes("⇄")
  )
    return "substitution";
  if (normalized.includes("var")) return "var";
  if (normalized.includes("penalty")) return "goal";
  if (normalized.includes("goal") || normalized.includes("âš½")) return "goal";
  return normalized;
};

const patchIncidentIcon = (root: HTMLElement, icon: string) => {
  const iconElement = root.querySelector<HTMLElement>(
    "[data-gwd-insight-event-icon]",
  );
  if (!iconElement) return;
  const key = incidentIconKey(icon);
  const svg = key ? MATCH_INSIGHT_INCIDENT_ICONS[key] : "";
  iconElement.className = `gwd-livescore-board__insight-event-icon${key ? ` is-${key}` : ""}`;
  if (svg) {
    iconElement.innerHTML = svg;
    iconElement.hidden = false;
  } else {
    iconElement.replaceChildren();
    iconElement.hidden = true;
  }
};

const incidentKindLabel = (kind: string, fallback = "Diễn biến"): string => {
  const labels: Record<string, string> = {
    goal: "Bàn thắng",
    "own-goal": "Phản lưới",
    "yellow-card": "Thẻ vàng",
    "red-card": "Thẻ đỏ",
    substitution: "Thay người",
    var: "VAR",
    event: fallback,
  };
  return labels[kind] || fallback;
};

const patchInsightIncidentContent = (
  board: HTMLElement,
  target: HTMLElement,
  row: MatchInsightIncident,
) => {
  if (!hasInsightIncidentContent(row)) return;
  const content = cloneTemplateElement<HTMLElement>(
    board,
    "[data-gwd-insight-event-content-template]",
  );
  if (!content) return;

  content.classList.add(`is-${row.kind || "event"}`);
  patchIncidentIcon(content, row.icon);

  let primary = "";
  let secondary = "";
  let primaryTooltip = "";
  let secondaryTooltip = "";
  if (row.kind === "substitution") {
    const inName = row.inPlayerName || row.playerName;
    const outName = row.outPlayerName || row.assistName;
    primary = inName ? `V\u00e0o: ${inName}` : row.label || "Thay người";
    secondary = outName ? `Ra: ${outName}` : "";
    primaryTooltip = row.inPlayerFullName
      ? `V\u00e0o: ${row.inPlayerFullName}`
      : row.playerFullName
        ? `V\u00e0o: ${row.playerFullName}`
        : primary;
    secondaryTooltip = row.outPlayerFullName
      ? `Ra: ${row.outPlayerFullName}`
      : row.assistFullName
        ? `Ra: ${row.assistFullName}`
        : secondary;
    content.classList.add("is-substitution");
  } else {
    const eventLabel = incidentKindLabel(row.kind, row.label || "Diễn biến");
    primary = row.playerName
      ? `${eventLabel}: ${row.playerName}`
      : row.detail
        ? `${eventLabel}: ${row.detail}`
        : eventLabel;
    secondary = row.assistName
      ? `Ki\u1ebfn t\u1ea1o: ${row.assistName}`
      : row.detail && row.detail !== primary
        ? row.detail
        : "";
    primaryTooltip = row.playerFullName
      ? `${eventLabel}: ${row.playerFullName}`
      : primary;
    secondaryTooltip = row.assistFullName
      ? `Ki\u1ebfn t\u1ea1o: ${row.assistFullName}`
      : secondary;
  }

  setVisibleText(content, "[data-gwd-insight-primary]", primary, Boolean(primary));
  setTooltip(content, "[data-gwd-insight-primary]", primaryTooltip || primary);
  setVisibleText(
    content,
    "[data-gwd-insight-secondary]",
    secondary,
    Boolean(secondary),
  );
  setTooltip(
    content,
    "[data-gwd-insight-secondary]",
    secondaryTooltip || secondary,
  );
  target.append(content);
};

const renderInsightIncidentContent = (_row: MatchInsightIncident): string => "";

const hasInsightIncidentContent = (row: MatchInsightIncident): boolean => {
  if (row.middleLabel) return true;
  if (row.kind === "substitution") {
    return Boolean(
      row.inPlayerName ||
      row.outPlayerName ||
      row.playerName ||
      row.assistName ||
      row.detail,
    );
  }
  return Boolean(row.playerName || row.assistName || row.detail);
};

const normalizeIncidentIcon = (incident: any): string => {
  const raw = String(
    incident?.type ||
      incident?.typeId ||
      incident?.kind ||
      incident?.eventType ||
      incident?.incidentType ||
      incident?.incidentTypeId ||
      incident?.typeName ||
      "",
  ).trim();
  const key = raw.toLowerCase().replace(/[\s-]+/g, "_");
  if (MATCH_INSIGHT_INCIDENT_TYPE_ICON_KEYS[raw])
    return MATCH_INSIGHT_INCIDENT_TYPE_ICON_KEYS[raw];
  if (MATCH_INSIGHT_INCIDENT_TYPE_ICON_KEYS[key])
    return MATCH_INSIGHT_INCIDENT_TYPE_ICON_KEYS[key];
  if (key === "yellow_card" || key.includes("yellow")) return "yellow-card";
  if (key === "red_card" || key.includes("red")) return "red-card";
  if (
    key.includes("substitution") ||
    key.includes("sub") ||
    key.includes("change")
  )
    return "substitution";
  if (key.includes("var")) return "var";
  if (key.includes("own_goal")) return "own-goal";
  if (key.includes("goal") || key.includes("score") || key.includes("penalty"))
    return "goal";
  return incidentIconKey(incidentTypeMeta(incident).icon);
};

const normalizeIncidentKind = (incident: any): string =>
  normalizeIncidentIcon(incident) || "event";

const incidentRawType = (incident: any): string =>
  String(
    incident?.type ||
      incident?.typeId ||
      incident?.kind ||
      incident?.eventType ||
      incident?.incidentType ||
      incident?.incidentTypeId ||
      incident?.typeName ||
      "",
  ).trim();

const normalizeIncidentMiddleLabel = (incident: any): string => {
  const raw = incidentRawType(incident);
  if (raw === "11" || raw === "13") return "HT";
  if (raw === "12") return "K\u1ebft th\u00fac";
  return "";
};

const normalizeInsightIncidents = (
  payload: any,
  fallbackInfo: any,
): MatchInsightIncident[] => {
  const match = rawMatchFromDetailPayload(payload);
  const data = payloadData(payload);
  const info = match.info || data.info || fallbackInfo || {};
  const source = match.incidents || data.incidents || data.events || [];
  const rows = Array.isArray(source) ? source : Object.values(source || {});
  const players = [
    ...(Array.isArray(data.relation?.players) ? data.relation.players : []),
    ...(Array.isArray(data.players) ? data.players : []),
  ].reduce<Record<string, any>>((indexed, player) => {
    const id = String(player?.id || "");
    if (id) indexed[id] = player;
    return indexed;
  }, {});
  const playerNameById = (id: any): string => {
    const player = players[String(id || "")] || {};
    return incidentText(player.name || player.shortName || player.realName);
  };

  return rows
    .map((incident: any) => {
      const kind = normalizeIncidentKind(incident);
      const middleLabel = normalizeIncidentMiddleLabel(incident);
      const playerName =
        incidentPlayerName(incident) || playerNameById(incident?.playerId);
      const assistName =
        incidentAssistName(incident) ||
        playerNameById(incident?.assist1Id || incident?.assistId);
      const inPlayerName =
        incidentInPlayerName(incident) ||
        playerNameById(incident?.inPlayerId || incident?.playerInId);
      const outPlayerName =
        incidentOutPlayerName(incident) ||
        playerNameById(incident?.outPlayerId || incident?.playerOutId);
      return {
        minute: normalizeMinute(incident),
        side: middleLabel ? "neutral" : normalizeIncidentSide(incident, info),
        label: normalizeIncidentLabel(incident),
        icon: kind,
        kind,
        middleLabel,
        playerName: compactPlayerName(playerName),
        assistName: compactPlayerName(assistName),
        inPlayerName: compactPlayerName(inPlayerName),
        outPlayerName: compactPlayerName(outPlayerName),
        playerFullName: playerName,
        assistFullName: assistName,
        inPlayerFullName: inPlayerName,
        outPlayerFullName: outPlayerName,
        detail: incidentText(
          incident?.description ||
            incident?.reason ||
            incident?.remark ||
            incident?.detail,
        ),
      };
    })
    .filter(
      (row) =>
        (row.minute || row.middleLabel) &&
        hasInsightIncidentContent(row),
    )
    .sort(
      (a, b) =>
        Number(String(b.minute).replace(/\D/g, "")) -
        Number(String(a.minute).replace(/\D/g, "")),
    );
};

const valueFromObject = (source: any, aliases: string[]): any => {
  if (!source || typeof source !== "object") return undefined;
  for (const alias of aliases) {
    if (source[alias] !== undefined) return source[alias];
  }
  const lowerMap = new Map(
    Object.keys(source).map((key) => [key.toLowerCase(), key]),
  );
  for (const alias of aliases) {
    const key = lowerMap.get(alias.toLowerCase());
    if (key) return source[key];
  }
  return undefined;
};

const normalizeStatValue = (value: any, suffix = ""): string => {
  if (value === undefined || value === null || value === "") return "-";
  const text = String(value);
  if (suffix && !text.includes(suffix)) return `${text}${suffix}`;
  return text;
};

const asRecord = (value: any): Record<string, any> =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

const isZeroStatValue = (value: string): boolean => {
  const normalized = value.replace(/[%+]/g, "").trim();
  return normalized === "0" || normalized === "0.0" || normalized === "0.00";
};

const isMissingStatValue = (value: string): boolean =>
  value === "" || value === "-";

const hasVisibleStatPair = (home: string, away: string): boolean => {
  if (isMissingStatValue(home) && isMissingStatValue(away)) return false;
  if (
    (isMissingStatValue(home) || isZeroStatValue(home)) &&
    (isMissingStatValue(away) || isZeroStatValue(away))
  )
    return false;
  return true;
};

const formatDetailStatValue = (key: string, value: any): string => {
  if (value === undefined || value === null || value === "") return "";
  const text = String(value);
  if (key === "25" && /^-?\d+(?:\.\d+)?$/.test(text)) return `${text}%`;
  if (key === "19" && /^-?\d+(?:\.\d+)?$/.test(text)) return `+${text}`;
  return text;
};

const normalizeRowsFromDetailBucket = (
  bucket: Record<string, any>,
): MatchInsightStat[] => {
  const orderIndex = new Map(
    MATCH_DETAIL_STAT_ORDER.map((key, index) => [key, index]),
  );

  return Object.entries(bucket)
    .map(([key, value]) => {
      if (!MATCH_INSIGHT_ALLOWED_DETAIL_STATS.has(key)) return null;
      const label =
        MATCH_INSIGHT_DETAIL_STAT_LABEL_OVERRIDES[key] ||
        MATCH_DETAIL_STAT_LABELS[key];
      const pair = Array.isArray(value) ? value : [];
      const home = formatDetailStatValue(key, pair[0]);
      const away = formatDetailStatValue(key, pair[1]);

      if (!label || !hasVisibleStatPair(home, away)) return null;

      return {
        key,
        label,
        home,
        away,
      };
    })
    .filter((row): row is MatchInsightStat => row !== null)
    .sort((left, right) => {
      const leftOrder = orderIndex.get(left.key);
      const rightOrder = orderIndex.get(right.key);
      if (leftOrder !== undefined || rightOrder !== undefined) {
        return (
          (leftOrder ?? Number.MAX_SAFE_INTEGER) -
          (rightOrder ?? Number.MAX_SAFE_INTEGER)
        );
      }
      return Number(left.key) - Number(right.key);
    });
};

const statPairFromSource = (stats: any, aliases: string[]): [any, any] => {
  if (!stats) return [undefined, undefined];

  if (Array.isArray(stats)) {
    for (const row of stats) {
      const key = String(
        row?.key || row?.type || row?.name || row?.label || "",
      ).toLowerCase();
      if (
        !aliases.some(
          (alias) =>
            key === alias.toLowerCase() || key.includes(alias.toLowerCase()),
        )
      )
        continue;
      if (Array.isArray(row?.value)) return [row.value[0], row.value[1]];
      return [
        row?.home ?? row?.homeValue ?? row?.homeScore,
        row?.away ?? row?.awayValue ?? row?.awayScore,
      ];
    }
    return [undefined, undefined];
  }

  const direct = valueFromObject(stats, aliases);
  if (Array.isArray(direct)) return [direct[0], direct[1]];
  if (direct && typeof direct === "object")
    return [direct.home ?? direct[0], direct.away ?? direct[1]];

  const home = valueFromObject(
    stats.home || stats.homeStats || stats.local || {},
    aliases,
  );
  const away = valueFromObject(
    stats.away || stats.awayStats || stats.visitor || {},
    aliases,
  );
  return [home, away];
};

const normalizeInsightStats = (
  payload: any,
  fallbackInfo: any,
): MatchInsightStat[] => {
  const match = rawMatchFromDetailPayload(payload);
  const data = payloadData(payload);
  const stats = asRecord(
    match.stats || data.stats || data.statistics || data.stat || {},
  );
  const info = match.info || data.info || fallbackInfo || {};
  const bucketRows = normalizeRowsFromDetailBucket(asRecord(stats.ft));
  if (bucketRows.length) return bucketRows;

  const rows = MATCH_INSIGHT_STAT_DEFS.filter((def) =>
    ["shots", "shotsOnTarget", "possession"].includes(def.key),
  ).map((def) => {
    let [home, away] = statPairFromSource(stats, def.aliases);

    if (home === undefined && away === undefined) {
      const homeMeta = scoreMeta(info.homeScores);
      const awayMeta = scoreMeta(info.awayScores);
      if (def.key === "corners")
        [home, away] = [
          homeMeta.corner >= 0 ? homeMeta.corner : undefined,
          awayMeta.corner >= 0 ? awayMeta.corner : undefined,
        ];
      if (def.key === "yellowCards")
        [home, away] = [homeMeta.yellow, awayMeta.yellow];
      if (def.key === "redCards") [home, away] = [homeMeta.red, awayMeta.red];
    }

    return {
      key: def.key,
      label: MATCH_INSIGHT_FALLBACK_STAT_LABEL_OVERRIDES[def.key] || def.label,
      home: normalizeStatValue(home, def.suffix),
      away: normalizeStatValue(away, def.suffix),
    };
  });

  return rows.filter((row) => hasVisibleStatPair(row.home, row.away));
};

const insightTitle = (
  state: State,
  match: any,
): { home: string; away: string } => {
  const info = match?.info || {};
  const home = state.entities.teams[String(info.homeTeamId || "")] || {};
  const away = state.entities.teams[String(info.awayTeamId || "")] || {};
  return {
    home: String(home.shortName || home.name || "Chủ"),
    away: String(away.shortName || away.name || "Khách"),
  };
};

const renderInsightHtml = (
  state: State,
  matchId: string,
  entry?: MatchInsightEntry,
): string => {
  const match = state.entities.matches[matchId] || {};
  const title = insightTitle(state, match);
  if (!entry || entry.status === "loading") {
    return `<div class="gwd-livescore-board__insight-loading"><span></span><span></span><span></span><span></span></div>`;
  }
  if (entry.status === "error") {
    return `<div class="gwd-livescore-board__insight-empty">Không tải được dữ liệu trận đấu.</div>`;
  }

  const incidentRows = entry.incidents.length
    ? entry.incidents
        .map(
          (row) => `
      <div class="gwd-livescore-board__insight-event is-${escapeHtml(row.side)}">
        <span>${row.side === "home" ? renderInsightIncidentContent(row) : ""}</span>
        <b><time>${escapeHtml(row.middleLabel || row.minute)}</time></b>
        <span>${row.side === "away" ? renderInsightIncidentContent(row) : ""}</span>
      </div>
    `,
        )
        .join("")
    : `<div class="gwd-livescore-board__insight-empty">Chưa có diễn biến.</div>`;

  const statRows = entry.stats.length
    ? entry.stats
        .map(
          (row) => `
      <div class="gwd-livescore-board__insight-stat" data-gwd-insight-stat="${escapeHtml(row.key)}">
        <strong>${escapeHtml(row.home)}</strong>
        <span>${escapeHtml(row.label)}</span>
        <strong>${escapeHtml(row.away)}</strong>
      </div>
    `,
        )
        .join("")
    : `<div class="gwd-livescore-board__insight-empty">Chưa có thông số.</div>`;

  return `
    <div class="gwd-livescore-board__insight-teams">
      <strong>${escapeHtml(title.home)}</strong>
      <span>Phút</span>
      <strong>${escapeHtml(title.away)}</strong>
    </div>
    <div class="gwd-livescore-board__insight-events">${incidentRows}</div>
    <h4>THỐNG KÊ TRẬN ĐẤU</h4>
    <div class="gwd-livescore-board__insight-stats">${statRows}</div>
  `;
};

const patchInsightMessage = (
  board: HTMLElement,
  body: HTMLElement,
  selector: string,
  text?: string,
) => {
  const element = cloneTemplateElement<HTMLElement>(board, selector);
  if (!element) return;
  if (text) setText(element, "[data-gwd-insight-message]", text);
  body.replaceChildren(element);
};

const patchInsightContent = (
  board: HTMLElement,
  body: HTMLElement,
  state: State,
  matchId: string,
  entry?: MatchInsightEntry,
) => {
  const match = state.entities.matches[matchId] || {};
  const title = insightTitle(state, match);

  if (!entry || entry.status === "loading") {
    patchInsightMessage(board, body, "[data-gwd-insight-loading-template]");
    return;
  }

  if (entry.status === "error") {
    patchInsightMessage(
      board,
      body,
      "[data-gwd-insight-empty-template]",
      "Không tải được dữ liệu trận đấu.",
    );
    return;
  }

  const shell = cloneTemplateElement<HTMLElement>(
    board,
    "[data-gwd-insight-shell-template]",
  );
  if (!shell) return;

  setText(shell, "[data-gwd-insight-home-title]", title.home);
  setText(shell, "[data-gwd-insight-away-title]", title.away);

  const events = shell.querySelector<HTMLElement>("[data-gwd-insight-events]");
  if (events) {
    if (entry.incidents.length) {
      const fragment = document.createDocumentFragment();
      entry.incidents.forEach((row) => {
        const event = cloneTemplateElement<HTMLElement>(
          board,
          "[data-gwd-insight-event-template]",
        );
        if (!event) return;
        event.classList.add(`is-${row.side}`);
        setText(event, "[data-gwd-insight-minute]", row.middleLabel || row.minute);
        const homeCell = event.querySelector<HTMLElement>(
          "[data-gwd-insight-home-cell]",
        );
        const awayCell = event.querySelector<HTMLElement>(
          "[data-gwd-insight-away-cell]",
        );
        if (row.side === "home" && homeCell)
          patchInsightIncidentContent(board, homeCell, row);
        if (row.side === "away" && awayCell)
          patchInsightIncidentContent(board, awayCell, row);
        fragment.append(event);
      });
      events.replaceChildren(fragment);
    } else {
      patchInsightMessage(
        board,
        events,
        "[data-gwd-insight-empty-template]",
        "Chưa có diễn biến.",
      );
    }
  }

  const stats = shell.querySelector<HTMLElement>("[data-gwd-insight-stats]");
  if (stats) {
    if (entry.stats.length) {
      const fragment = document.createDocumentFragment();
      entry.stats.forEach((row) => {
        const stat = cloneTemplateElement<HTMLElement>(
          board,
          "[data-gwd-insight-stat-template]",
        );
        if (!stat) return;
        stat.dataset.gwdInsightStat = row.key;
        setText(stat, "[data-gwd-insight-stat-home]", row.home);
        setText(stat, "[data-gwd-insight-stat-label]", row.label);
        setText(stat, "[data-gwd-insight-stat-away]", row.away);
        fragment.append(stat);
      });
      stats.replaceChildren(fragment);
    } else {
      patchInsightMessage(
        board,
        stats,
        "[data-gwd-insight-empty-template]",
        "Chưa có thông số.",
      );
    }
  }

  body.replaceChildren(shell);
};

const canShowInsight = (state: State, match: any): boolean => {
  if (!state.settings.showPopup) return false;
  const status = statusOf(match);
  return status === "live" || status === "finished";
};

const positionInsightPanel = (
  board: HTMLElement,
  panel: HTMLElement,
  anchor?: HTMLElement | null,
) => {
  if (!anchor) return;
  const boardRect = board.getBoundingClientRect();
  const anchorRect = anchor.getBoundingClientRect();
  const maxPanelWidth = Math.max(320, board.clientWidth - 16);
  const panelWidth = Math.min(500, maxPanelWidth);
  const gap = 8;
  const minLeft = 8;
  const maxLeft = Math.max(minLeft, board.clientWidth - panelWidth - 8);
  const anchorCenter =
    anchorRect.left - boardRect.left + board.scrollLeft + anchorRect.width / 2;
  const left = Math.min(
    maxLeft,
    Math.max(minLeft, anchorCenter - panelWidth / 2),
  );
  const top = anchorRect.bottom - boardRect.top + board.scrollTop + gap;

  panel.style.width = `${panelWidth}px`;
  panel.style.left = `${left}px`;
  panel.style.top = `${top}px`;
};

const setInsightContent = (
  board: HTMLElement,
  state: State,
  matchId: string,
  anchor?: HTMLElement | null,
) => {
  const panel = board.querySelector<HTMLElement>("[data-gwd-match-insight]");
  const body = board.querySelector<HTMLElement>(
    "[data-gwd-match-insight-body]",
  );
  if (!panel || !body) return;
  closeBoardOverlays(board, state, "insight");
  patchInsightContent(board, body, state, matchId, state.insights[matchId]);
  panel.hidden = false;
  panel.dataset.gwdMatchId = matchId;
  positionInsightPanel(board, panel, anchor);
};

const loadMatchInsight = async (
  board: HTMLElement,
  state: State,
  matchId: string,
  force = false,
  anchor?: HTMLElement | null,
) => {
  const cached = state.insights[matchId];
  if (
    !force &&
    cached?.status === "ready" &&
    Date.now() - cached.fetchedAt < MATCH_INSIGHT_CACHE_MS
  ) {
    setInsightContent(board, state, matchId, anchor);
    return;
  }
  if (state.insightRequests.has(matchId)) return;

  state.insights[matchId] = {
    matchId,
    status: "loading",
    fetchedAt: cached?.fetchedAt || 0,
    incidents: cached?.incidents || [],
    stats: cached?.stats || [],
  };
  setInsightContent(board, state, matchId, anchor);
  state.insightRequests.add(matchId);

  try {
    const payload = await api(
      matchDetailEndpoint(state, matchId),
      buildMatchInsightQuery(state),
      fixtureRequestSignal(state),
    );
    const data = payloadData(payload);
    mergeRelations(state, {
      ...(data.relation || {}),
      teams: data.relation?.teams || data.teams || [],
      competitions: data.relation?.competitions || data.competitions || [],
      venues: data.relation?.venues || data.venues || [],
      countries: data.relation?.countries || data.countries || [],
    });
    const match = rawMatchFromDetailPayload(payload);
    if (match?.id) {
      const id = String(match.id);
      if (mergeMatchEntity(state, id, match))
        livescoreRuntime.emit("matches:update", {
          changedIds: [id],
          source: "matchInsight",
        });
    }
    state.insights[matchId] = {
      matchId,
      status: "ready",
      fetchedAt: Date.now(),
      incidents: normalizeInsightIncidents(
        payload,
        state.entities.matches[matchId]?.info || {},
      ),
      stats: normalizeInsightStats(
        payload,
        state.entities.matches[matchId]?.info || {},
      ),
    };
  } catch (error) {
    if (isAbortError(error)) {
      delete state.insights[matchId];
      return;
    }
    console.warn("GWD Livescore match insight request failed", error);
    state.insights[matchId] = {
      matchId,
      status: "error",
      fetchedAt: Date.now(),
      incidents: cached?.incidents || [],
      stats: cached?.stats || [],
    };
  } finally {
    state.insightRequests.delete(matchId);
    const panel = board.querySelector<HTMLElement>("[data-gwd-match-insight]");
    if (panel?.dataset.gwdMatchId === matchId && !panel.hidden)
      setInsightContent(board, state, matchId, anchor);
  }
};

const initMatchInsightHover = (board: HTMLElement, state: State) => {
  const panel = board.querySelector<HTMLElement>("[data-gwd-match-insight]");
  let closeTimer = 0;

  const clearCloseTimer = () => {
    if (closeTimer) window.clearTimeout(closeTimer);
    closeTimer = 0;
  };

  const close = () => {
    if (panel) panel.hidden = true;
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimer = window.setTimeout(() => {
      if (panel?.matches(":hover")) return;
      close();
    }, MATCH_INSIGHT_CLOSE_DELAY_MS);
  };

  board.addEventListener("pointerover", (event) => {
    if (window.matchMedia(MOBILE_VIEW_QUERY).matches) return;
    const trigger =
      event.target instanceof Element
        ? event.target.closest<HTMLElement>(
            "[data-gwd-match-insight-trigger], .gwd-livescore-board__score",
          )
        : null;
    if (!trigger || !board.contains(trigger)) return;
    if (
      event.relatedTarget instanceof Node &&
      trigger.contains(event.relatedTarget)
    )
      return;

    const row = trigger.closest<HTMLElement>("[data-gwd-match-id]");
    const matchId = String(row?.dataset.gwdMatchId || "");
    const match = state.entities.matches[matchId];
    if (!matchId || !match || !canShowInsight(state, match)) return;

    clearCloseTimer();
    void loadMatchInsight(board, state, matchId, false, trigger);
  });

  board.addEventListener("pointerout", (event) => {
    if (window.matchMedia(MOBILE_VIEW_QUERY).matches) return;
    const trigger =
      event.target instanceof Element
        ? event.target.closest<HTMLElement>(
            "[data-gwd-match-insight-trigger], .gwd-livescore-board__score",
          )
        : null;
    if (!trigger) return;
    if (
      event.relatedTarget instanceof Node &&
      (trigger.contains(event.relatedTarget) ||
        panel?.contains(event.relatedTarget))
    )
      return;
    scheduleClose();
  });

  panel?.addEventListener("pointerover", clearCloseTimer);
  panel?.addEventListener("pointerout", (event) => {
    if (
      event.relatedTarget instanceof Node &&
      panel.contains(event.relatedTarget)
    )
      return;
    scheduleClose();
  });
  board
    .querySelector<HTMLElement>("[data-gwd-match-insight-close]")
    ?.addEventListener("click", close);
};

document
  .querySelectorAll<HTMLElement>("[data-gwd-livescore-board]")
  .forEach((board) => {
    let hydratedState: { request?: any; data?: any } = {};
    if (board.dataset.gwdState) {
      try { hydratedState = JSON.parse(board.dataset.gwdState); }
      catch (error) { console.warn("GWD livescore initial state is invalid", error); }
      delete board.dataset.gwdState;
    }
    const contract = hydratedState.request || JSON.parse(board.dataset.gwdContract || "{}");
    if (["fixtures", "results"].includes(String(contract.variant || contract.feature || ""))) {
      const selectedFixtureDate =
        contract.fixtureDate || contract.query?.fixtureDate || contract.query?.date;
      contract.fixtureDate = selectedFixtureDate
        ? normalizeFixtureDate(selectedFixtureDate)
        : "";
    }
    layoutRegistry.mount(board, contract);
    const active = matchTabs.includes(contract.activeTab)
      ? contract.activeTab
      : "total";
    const contractCompetitionIds = normalizeArray(
      contract.query?.competitionIds,
    );
    const contractHotIds = normalizeArray(contract.priorityCompetitionIds)
      .length
      ? normalizeArray(contract.priorityCompetitionIds)
      : normalizeArray(contract.query?.priorityCompetitionIds);
    const initialCompetitionIds =
      active === "hot" && sameKeys(contractCompetitionIds, contractHotIds)
        ? []
        : contractCompetitionIds;
    const initialCountryIds = initialCompetitionIds.length
      ? []
      : normalizeArray(contract.query?.countryIds);
    const initialGroupMode: GroupMode =
      contract.groupMode === "league" ? "league" : "time";
    const state: State = {
      contract,
      active,
      views: {},
      viewsByGroupMode: { time: {}, league: {} },
      indexView: undefined,
      indexViewsByGroupMode: {},
      totals: {},
      totalsByGroupMode: { time: {}, league: {} },
      polling: false,
      recentFinishedIds: new Set(),
      liveFallbackChecked: false,
      lastActiveSyncAt: 0,
      lastCountsSyncAt: 0,
      wakeSyncing: false,
      loadedFixtureSeasonIds: new Set(),
      fixtureSeasonsByCompetitionId: {},
      fixtureSeasonSelectionByCompetitionId: {},
      fixtureViewsByScopeKey: {},
      patchQueue: {
        matchIds: new Set(),
        oddsIds: new Set(),
        frame: 0,
      },
      countBadgesReady: {
        filters: false,
        odds: false,
      },
      oddsRequests: new Set(),
      insightRequests: new Set(),
      insights: {},
      companyId:
        normalizeArray(contract.companyIds)[0] ||
        normalizeArray(contract.query?.companyIds)[0] ||
        DEFAULT_COMPANY_ID,
      oddsFormat: normalizeOddsFormat(
        contract.oddsFormat || contract.query?.oddsFormat,
      ),
      groupMode: initialGroupMode,
      settings: loadSettings(),
      favorites: loadFavorites(),
      entities: livescoreRuntime.entities,
      applied: {
        mode: initialCompetitionIds.length
          ? "leagues"
          : initialCountryIds.length
            ? "countries"
            : null,
        competitionIds: initialCompetitionIds,
        countryIds: initialCountryIds,
        seasonId: String(contract.query?.seasonId || contract.seasonId || ""),
        fixtureDate: "",
        status: "all",
      },
      draft: {
        mode: null,
        status: "all",
        competitionIds: new Set(),
        countryIds: new Set(),
        alphabetical: false,
        touched: false,
      },
    };

    syncGroupModeStores(state);
    const activeView = ensureView(state, state.active, true);
    const initialData = hydratedState.data ?? contract.initialData ?? null;
    if (initialData && typeof initialData === "object") {
      logResultsApiData(state, "php-initial", initialData, activeView.query);
      const initialMerge = mergePayload(state, activeView, initialData);
      // A cold gwd-api-sport cache can return an empty first response while it is
      // being populated. Do not treat that response as a completed first page:
      // keep any relations it supplied, then let loadFull fetch and paint page 1.
      if (initialMerge.ids.length > 0) {
        activeView.page = 1;
      } else {
        activeView.page = 0;
        activeView.total = Infinity;
        activeView.done = false;
      }
      activeView.paint = true;
      if (isFixturesBoard(state)) {
        state.indexView = activeView;
        state.indexViewsByGroupMode[activeView.groupMode] = activeView;
        state.fixtureViewsByScopeKey[fixtureViewScopeKey(state)] = activeView;
      }
      delete contract.initialData;
    }
    states.set(board, state);
    subscribeSharedStore(board, state);
    updateBookmakerUi(board, state);
    updateGroupUi(board, state);
    syncFixtureDateUi(board, state);
    syncFixtureLeagueChips(board, state);
    syncFixtureSeasonUi(board, state);
    syncFixtureLeagueHeading(board, state);
    applySettings(board, state);
    syncFavoriteBadges(board, state);
    updateTabUi(board, state);

    activeView.paint = true;
    if (initialData && isTimeTotalSegmentedView(state, activeView)) {
      // Hydration may mark a one-page SSR payload as complete. Force the
      // prioritized refresh so LIVE always settles before UPCOMING/FINISHED.
      activeView.done = false;
    }
    const initialLoad = initialData
      ? loadFull(board, state, activeView, true)
      : refreshActiveTab(board, state);
    void initialLoad.then(async () => {
      if (
        !isFixturesBoard(state) &&
        activeView.tab === "total" &&
        state.applied.competitionIds.length === 0 &&
        state.applied.countryIds.length === 0
      ) {
        state.indexView = activeView;
        state.indexViewsByGroupMode[activeView.groupMode] = activeView;
      }
      await refreshCounts(board, state);
      if (!state.indexView) await reloadIndexView(board, state);
      startActiveTabPolling(board, state);
      startWakeSync(board, state);
    });
    startMatchClock(board, state);
    startResponsiveViewSync(board, state);
    initMatchInsightHover(board, state);

    board.addEventListener("pointerdown", (event) => {
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (!target) return;
      const tabButton = target.closest<HTMLElement>("[data-gwd-tab]");
      const tab = tabButton?.dataset.gwdTab;
      if (tab && matchTabs.includes(tab as MatchTab)) {
        activateTabButtonImmediately(board, tab);
      }
    });

    board.addEventListener("click", (event) => {
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (!target) return;
      const fixtureWatchButton = target.closest<HTMLElement>(
        "[data-gwd-fixture-watch] button",
      );
      if (fixtureWatchButton) {
        const wrap = fixtureWatchButton.closest<HTMLElement>(
          "[data-gwd-fixture-watch]",
        );
        const fallbackHref = wrap?.dataset.gwdFixtureWatchHref || "";
        if (wrap?.dataset.gwdFixtureWatchMode === "fallback" && fallbackHref) {
          closeBoardOverlays(board, state);
          window.open(fallbackHref, "_blank", "noopener,noreferrer");
          return;
        }
        const menu = wrap?.querySelector<HTMLElement>(
          "[data-gwd-fixture-watch-menu]",
        );
        if (!menu) return;

        const shouldOpen = menu.hidden;
        closeBoardOverlays(board, state, "fixtureWatch");
        menu.hidden = !shouldOpen;
        return;
      }

      if (target.closest("[data-gwd-settings-toggle]")) {
        toggleSettingsPanel(board, state);
        return;
      }
      if (target.closest("[data-gwd-group-toggle]")) {
        toggleGroupMode(board, state);
        return;
      }
    if (target.closest("[data-gwd-settings-close]")) {
      toggleSettingsPanel(board, state, false);
      return;
    }
      if (target.closest("[data-gwd-fixtures-today]")) {
        changeFixtureDate(board, state, undefined, true);
        return;
      }
      const fixtureDateClick = target.closest<HTMLInputElement>(
        "[data-gwd-fixtures-date]",
      );
      const fixtureDatePicker = fixtureDateClick as
        | (HTMLInputElement & { showPicker?: () => void })
        | null;
      if (fixtureDatePicker && typeof fixtureDatePicker.showPicker === "function") {
        try {
          fixtureDatePicker.showPicker();
        } catch {
          // Some browsers block showPicker outside trusted/native paths.
        }
      }
    const fixtureLeagueButton = target.closest<HTMLElement>(
      "[data-gwd-fixtures-league-id]",
    );
    if (fixtureLeagueButton?.dataset.gwdFixturesLeagueId) {
      filterFixtureLeague(
        board,
        state,
        fixtureLeagueButton.dataset.gwdFixturesLeagueId,
      );
      return;
    }
    const bookmakerToggle = target.closest<HTMLElement>(
      "[data-gwd-bookmaker-toggle]",
    );
      if (bookmakerToggle) {
        closeBoardOverlays(board, state, "bookmaker");
        const menu = board.querySelector<HTMLElement>(
          "[data-gwd-bookmaker-menu]",
        );
        if (menu) menu.hidden = !menu.hidden;
        return;
      }
      const bookmakerOption = target.closest<HTMLElement>(
        "[data-gwd-bookmaker-id]",
      );
      if (bookmakerOption?.dataset.gwdBookmakerId) {
        changeBookmaker(board, state, bookmakerOption.dataset.gwdBookmakerId);
        return;
      }
      const favorite = target.closest<HTMLElement>("[data-gwd-favorite]");
      if (favorite) {
        const row = favorite.closest<HTMLElement>("[data-gwd-match-id]");
        toggleFavorite(board, state, String(row?.dataset.gwdMatchId || ""));
        return;
      }
      if (handleFilterClick(board, state, target)) return;

      const tabButton = target.closest<HTMLElement>("[data-gwd-tab]");
      const tab = tabButton?.dataset.gwdTab;
      if (!tab) return;

      if (tab === "leagues" || tab === "countries") {
        openFilter(board, state, tab);
        return;
      }

      if (matchTabs.includes(tab as MatchTab)) {
        activateTabButtonImmediately(board, tab);
        switchTab(board, state, tab as MatchTab);
      }
    });

    board.addEventListener("change", (event) => {
      const settingCheckbox = (
        event.target as HTMLElement
      ).closest<HTMLInputElement>("[data-gwd-setting]");
      if (settingCheckbox) {
        updateSetting(
          board,
          state,
          settingCheckbox.dataset.gwdSetting as keyof BoardSettings,
          settingCheckbox.checked,
        );
        return;
      }
      const settingSelect = (
        event.target as HTMLElement
      ).closest<HTMLSelectElement>("[data-gwd-setting-select]");
      if (settingSelect) {
        updateSetting(
          board,
          state,
          settingSelect.dataset.gwdSettingSelect as keyof BoardSettings,
          settingSelect.value,
        );
        return;
      }

      const fixtureDateInput = (
        event.target as HTMLElement
      ).closest<HTMLInputElement>("[data-gwd-fixtures-date]");
      if (fixtureDateInput) {
        changeFixtureDate(board, state, fixtureDateInput.value);
        return;
      }

      const fixtureDateSelect = (
        event.target as HTMLElement
      ).closest<HTMLSelectElement>("[data-gwd-fixtures-date-select]");
      if (fixtureDateSelect) {
        changeFixtureDate(board, state, fixtureDateSelect.value);
        return;
      }

      const fixtureSeasonSelect = (
        event.target as HTMLElement
      ).closest<HTMLSelectElement>("[data-gwd-fixtures-season]");
      if (fixtureSeasonSelect) {
        changeFixtureSeason(board, state, fixtureSeasonSelect.value);
        return;
      }

      const fixtureLeagueSelect = (
        event.target as HTMLElement
      ).closest<HTMLSelectElement>("[data-gwd-fixtures-league-select]");
      if (fixtureLeagueSelect) {
        if (!fixtureLeagueSelect.value) {
          changeFixtureDate(board, state, undefined, true);
          return;
        }
        filterFixtureLeague(board, state, fixtureLeagueSelect.value);
        return;
      }

      const checkbox = (event.target as HTMLElement).closest<HTMLInputElement>(
        "[data-gwd-filter-checkbox]",
      );
      if (!checkbox) return;

      const selected = selectedSet(state);
      if (checkbox.checked) selected.add(checkbox.value);
      else selected.delete(checkbox.value);
      state.draft.touched = true;
      renderFilterPopover(board, state);
    });

    document.addEventListener("click", (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      if (
        target.closest(
          [
            "[data-gwd-settings-panel]",
            "[data-gwd-settings-toggle]",
            "[data-gwd-filter-popover]",
            '[data-gwd-tab="leagues"]',
            '[data-gwd-tab="countries"]',
            "[data-gwd-bookmaker]",
            "[data-gwd-match-insight]",
            "[data-gwd-match-insight-trigger]",
            "[data-gwd-fixture-watch]",
            ".gwd-livescore-board__score",
          ].join(","),
        )
      ) {
        return;
      }

      closeBoardOverlays(board, state);
    });
  });
